(() => {
  // src/store.js
  var state = {
    status: "empty",
    // empty | uploading | uploaded
    mode: "single",
    // single | multi
    // rows carry NO price — unit/total live in state.pricing.rows (pricing is
    // the sole writer there; sizing renders its cells from it, matched by
    // fileId + row index). File dims: widthIn/heightIn canonical; the ported
    // pipeline's inW/inH are stamped as aliases at record creation.
    files: [],
    // [{ id, name, type, s3Url, imgixUrl, widthIn, heightIn, rows: [{ w, h, qty, variantId, mode }], designNote }]
    activeFileId: null,
    // which file the page preview/tools show; null => files[0]
    // LIVE TAB SEMANTICS (Owen 2026-07-07, ported from bySize_popular.js):
    // custom and popular rows are SEPARATE persistent lists (row.mode tags
    // them; missing mode reads as 'custom'). sizeTab picks which list renders,
    // prices, previews and CHECKS OUT — active-tab-only at add-to-cart
    // (deliberate delta: live's edited-both-tabs cross-add quirk NOT ported).
    sizeTab: "custom",
    // custom | popular — written by sizing on tab switch
    precut: false,
    designNotes: "",
    cartQty: 0,
    // cumulative DTF/UV qty already in cart (tier input); seeded from cfg.cartQty at boot (main.js)
    family: "dtf",
    // dtf | uv | sticker | precut | puff | cling — set once at boot from cfg
    options: {},
    // family option selections, e.g. { shape:'circle', finish:'gloss' } (empty for dtf)
    pricing: { model: "cumulative", rows: [], total: 0, tier: null }
    // written ONLY by pricing; rows: [{fileId,w,h,qty,unit,total}]
  };
  var listeners = /* @__PURE__ */ new Set();
  function getState() {
    return state;
  }
  function update(patch) {
    Object.assign(state, patch);
    emit(patch);
  }
  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }
  function emit(patch) {
    for (const fn of listeners) fn(state, patch);
  }
  function reset() {
    const printMethod = state.options && state.options.printMethod;
    update({
      status: "empty",
      mode: "single",
      files: [],
      activeFileId: null,
      precut: false,
      designNotes: "",
      options: printMethod ? { printMethod } : {},
      pricing: { model: state.pricing.model, rows: [], total: 0, tier: null }
    });
    announce("reset", {});
  }
  function announce(name, detail) {
    document.dispatchEvent(new CustomEvent(`bsv4:${name}`, { detail, bubbles: false }));
  }

  // src/config.js
  function parseImgixMatrix(dsl) {
    const out = {};
    if (!dsl || typeof dsl !== "string") return out;
    for (const line of dsl.split("\n")) {
      const ci = line.indexOf(":");
      if (ci < 1) continue;
      const ext = line.slice(0, ci).trim().toLowerCase();
      if (!ext) continue;
      const entry = {};
      for (const seg of line.slice(ci + 1).split(";")) {
        const ei = seg.indexOf("=");
        if (ei < 1) continue;
        const k = seg.slice(0, ei).trim();
        let v = seg.slice(ei + 1).trim();
        if (v === "true") v = true;
        else if (v === "false") v = false;
        entry[k] = v;
      }
      if (Object.keys(entry).length) out[ext] = entry;
    }
    return out;
  }
  function parsePopularCaps(dsl) {
    const out = [];
    if (!dsl || typeof dsl !== "string") return out;
    for (const line of dsl.split("\n")) {
      const ci = line.indexOf(":");
      if (ci < 1) continue;
      const size = line.slice(0, ci).trim().toLowerCase();
      const sm = size.match(/^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)$/);
      if (!sm) continue;
      const caps = { w: parseFloat(sm[1]), h: parseFloat(sm[2]) };
      for (const seg of line.slice(ci + 1).split(",")) {
        const ei = seg.indexOf("=");
        if (ei < 1) continue;
        const k = seg.slice(0, ei).trim();
        const v = parseFloat(seg.slice(ei + 1));
        if (k === "max_w" && !isNaN(v)) caps.maxW = v;
        if (k === "max_h" && !isNaN(v)) caps.maxH = v;
      }
      out.push(caps);
    }
    return out;
  }
  function readConfig() {
    const el = document.getElementById("bsv4-config");
    const cfg7 = el ? JSON.parse(el.textContent) : {};
    if (cfg7.preview) {
      cfg7.preview.imgix = parseImgixMatrix(cfg7.preview.imgixMatrix);
      const _j = cfg7.preview.imgix.jpg;
      if (_j) {
        const _rOrder = String(_j.order || "").replace("[imgIx]", "[imgIx]fm=png&");
        for (const _e of ["webp", "heic", "avif", "psd", "gif", "bmp"]) {
          if (!cfg7.preview.imgix[_e]) cfg7.preview.imgix[_e] = { ..._j, order: _rOrder };
        }
      }
    }
    if (Array.isArray(cfg7.sizing)) {
      for (const s of cfg7.sizing) s.popular = parsePopularCaps(s.popularCaps);
    }
    if (!cfg7.imgixParams || !Object.keys(cfg7.imgixParams).length) {
      cfg7.imgixParams = cfg7.preview ? cfg7.preview.imgix : {};
    }
    return cfg7;
  }

  // src/ported/pipeline.js
  var API_URL = "https://k7ylxp3inc.execute-api.eu-west-2.amazonaws.com/production";
  var IMGIX_HOST = "ninjauploads-production-uk.imgix.net";
  var S3_HOST = "ninja-services-production-ninjauploadss3bucket-ozlwonhajxot.s3.eu-west-2.amazonaws.com";
  var IN2CM = 2.54;
  function cmFromIn(inches) {
    const n = parseFloat(inches);
    return Number.isFinite(n) ? Math.round(n * IN2CM * 10) / 10 : n;
  }
  function inFromCm(cm) {
    const n = parseFloat(cm);
    return Number.isFinite(n) ? n / IN2CM : n;
  }
  function buildUploadName(fileName) {
    const nameOnly = fileName.split(".")[0].toLowerCase().replace(/[^a-z0-9]/gi, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
    const extension = /\.[a-z0-9]{1,5}$/i.test(fileName) ? fileName.split(".").pop() : "png";
    return `${nameOnly}____image_${Date.now()}_${Math.floor(Math.random() * 1e3)}.${extension}`;
  }
  async function getPresignedUploadUrl(fileName, fileType) {
    const res = await fetch(
      `${API_URL}/uploads/getPresignedUploadUrl?file_name=${encodeURIComponent(fileName)}&file_type=${encodeURIComponent(fileType)}`
    );
    if (!res.ok) throw new Error(`presign failed (${res.status})`);
    return res.json();
  }
  function putToS3(presignedUrl, file, onProgress, onXhr) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presignedUrl);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error(`S3 PUT ${xhr.status}`));
      xhr.onerror = () => reject(new Error("S3 PUT network error"));
      xhr.onabort = () => reject(Object.assign(new Error("upload cancelled"), { name: "AbortError" }));
      if (onXhr) onXhr(xhr);
      xhr.send(file);
    });
  }
  var crcTable;
  function crc32Buf(buf) {
    if (!crcTable) {
      crcTable = new Int32Array(256);
      for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 3988292384 ^ c >>> 1 : c >>> 1;
        crcTable[n] = c;
      }
    }
    let crc = -1;
    for (let i = 0; i < buf.length; i++) crc = crc >>> 8 ^ crcTable[(crc ^ buf[i]) & 255];
    return crc ^ -1;
  }
  async function crc32Hex(file) {
    const buf = new Uint8Array(await file.arrayBuffer());
    return (crc32Buf(buf) >>> 0).toString(16);
  }
  function saveFileToUser({ customerId, productId, fileRecord }) {
    if (!customerId) return;
    const { inW, inH, imgixUrl, uploadName, crc } = fileRecord;
    if (!crc || !uploadName || !inW || !inH || !imgixUrl) return;
    fetch(`${API_URL}/uploads/saveData`, {
      method: "POST",
      body: JSON.stringify([
        {
          variant_title: `${inW}x${inH}`,
          file: imgixUrl,
          customer_id: customerId,
          file_name: uploadName,
          file_crc: crc,
          product_id: productId,
          gangsheet: false
        }
      ])
    }).catch(() => {
    });
  }
  var PRINT_DPI = 300;
  var ntmLoading = null;
  function ensureNTMeasure() {
    if (typeof window === "undefined") return Promise.resolve(null);
    if (window.NTMeasure) return Promise.resolve(window.NTMeasure);
    if (!ntmLoading) {
      ntmLoading = new Promise((resolve) => {
        const sib = document.querySelector('script[src*="bysize-v4"]');
        if (!sib || !sib.src) {
          resolve(null);
          return;
        }
        const s = document.createElement("script");
        s.src = sib.src.split("?")[0].replace(/[^/]+$/, "nt-measure.js");
        s.onload = () => resolve(window.NTMeasure || null);
        s.onerror = () => resolve(null);
        document.head.appendChild(s);
      });
    }
    return ntmLoading;
  }
  async function resolveDimensions(fileRecord, previewParams) {
    const NTM = await ensureNTMeasure();
    const m = NTM ? await NTM.measure({
      imgixUrl: fileRecord.imgixUrl,
      ext: fileRecord.ext,
      s3Key: fileRecord.uploadName
    }) : null;
    if (!m) {
      if (!NTM) {
        console.error("bysize-v4: NTMeasure missing and self-heal load failed");
      }
      return {
        pxW: PRINT_DPI,
        pxH: PRINT_DPI,
        inW: "1.00",
        inH: "1.00",
        measureFailed: true
      };
    }
    return {
      pxW: m.pxW,
      pxH: m.pxH,
      inW: m.inW.toFixed(2),
      inH: m.inH.toFixed(2),
      measureFailed: false
    };
  }

  // src/ported/bgr.js
  var BGR_MODELS = [
    // imgixServed: imgix produces this removal from URL PARAMS, so the
    // full-resolution ORDER url can reproduce it — no Lambda-hosted file needed,
    // and crucially NO `w=2000` cap on what production prints (see
    // applyBgrProps). As of 2026-07-29 that is ALL THREE models ("mirror imgix",
    // Owen): imgix's bg-remove-model=3 IS fal-Ideogram — the same model the
    // Lambda preview calls — so gram's production file is the untouched order
    // url + the param, exactly like ix/pc, at full print resolution.
    // ⚠ gram's PREVIEW stays on the Lambda (small input, ~10s flat — see
    // bg-removal-multi.js callBgRemoval). Its modelParam here feeds the
    // PRODUCTION stamp and the engine-absent candidateUrl fallback only; that
    // fallback renders model 3 cold on the full source (measured 45.8s at 25MP),
    // acceptable for a rare fallback, never for the main picker path.
    // ⚠ Same-model contract: Lambda 'gram' and bg-remove-model=3 are both
    // fal-Ideogram; if either side ever changes vendor, change both.
    // ⚠ ORDER IS THE PICKER'S SLOT ORDER and must match the engine's
    // SLOT_ORDER in bg-removal-multi.js (['ix','pc','gram']): paintSlots uses
    // THIS array while candidates are pending and the engine's res.all once the
    // run lands. When the two disagreed (this array once read ix,gram,pc) the
    // tiles visibly RESORTED mid-load and "Option 2" changed vendor identity
    // (Owen 2026-07-30: tiles only collapse on a fail, never resort).
    // ⚠ gram reverted OFF imgixServed 2026-07-29: imgix does not compose trim
    // with bg-remove-model=3 (full-frame output = wrong print size; measured
    // 1902/2406/4000px across the three models on one source). gram production
    // = the Lambda-hosted PNG + trim=colorUnlessAlpha (trim composes correctly
    // on a plain alpha asset — Owen's pipeline). Re-add modelParam: 3 +
    // imgixServed ONLY after imgix's composition fix is verified trimmed.
    { key: "ix", label: "IX", modelParam: null, imgixServed: true },
    { key: "pc", label: "PC", modelParam: 4, imgixServed: true },
    // UK port: gram = imgix-native bg-remove-model=3 (fal-Ideogram), mirroring the
    // UK v1 fork's 2026-07-23 recipe. The NT Lambda path is unusable here (its CORS
    // allowlist excludes ninjatransfers.co.uk) and trim+model=3 composition was
    // verified on THIS source (uploaded a 400px canvas w/ 100px art; every model
    // returned 100x100 trimmed, 2026-08-04). NT keeps gram off imgixServed for its
    // own source — do not blindly re-sync this line from NT.
    // UK 2026-08-18: Option 3 now calls OUR Ideogram API via the bg-removal proxy
    // (window.__bsv4BgrRun in nt-ideogram-edit.js) instead of imgix
    // bg-remove-model=3, which reached Ideogram through fal. Off imgixServed so
    // applyBgrProps stamps the Lambda-hosted PNG (which already carries cs=strip)
    // + trim, rather than re-rendering the source with the model param. Mirrors
    // ninjatransfers.com. modelParam stays 3: candidateUrl's cs=strip rule and the
    // UK picker's engine-absent fallback (src/bgr.js startCandidates loads every
    // BGR_MODELS row via candidateUrl(runUrl, m)) key off it.
    // (UK re-port 2026-09-14 onto the B-tools build — keep this row as is.)
    { key: "gram", label: "Gram", modelParam: 3, imgixServed: false }
  ];
  function candidateUrl(bareImgixUrl, model2) {
    const base = `${bareImgixUrl.split("?")[0]}?trim=colorUnlessAlpha&fm=png&w=2000&bg-remove=true`;
    if (!model2.modelParam) return base;
    const withModel = `${base}&bg-remove-model=${model2.modelParam}`;
    return model2.modelParam === 3 ? `${withModel}&cs=strip` : withModel;
  }
  var BGR_VERSION_PROP_LABELS = {
    ix: "_IX Version",
    gram: "_Gram Version",
    pc: "_PC Version"
  };
  function applyBgrProps(properties, file) {
    if (!file.bgRemove || !file.bgrVersions || !file.bgrModel) return properties;
    const chosen = file.bgrVersions[file.bgrModel];
    if (!chosen) return properties;
    const isVectorSource = /\.(?:svg|eps|pdf|ai)(?:[?#]|$)/i.test(
      file.uploadName || file.s3Url || ""
    );
    const model2 = BGR_MODELS.find((m) => m.key === file.bgrModel);
    if (model2 && model2.imgixServed) {
      if (model2.modelParam) {
        for (const k of ["Upload (Vector Files Preferred)", "_cartImg"]) {
          const v = properties[k];
          if (typeof v === "string" && /[?&]bg-remove=true/.test(v) && !/[?&]bg-remove-model=/.test(v)) {
            properties[k] = `${v}&bg-remove-model=${model2.modelParam}${model2.modelParam === 3 ? "&cs=strip" : ""}`;
          }
        }
        if (model2.key === "gram") {
          const up = properties["Upload (Vector Files Preferred)"];
          if (typeof up === "string" && /[?&]bg-remove-model=3/.test(up)) {
            try {
              new Image().src = `${up}${up.includes("?") ? "&" : "?"}w=64`;
            } catch (_) {
            }
          }
        }
      }
    } else if (!isVectorSource) {
      const sep = chosen.includes("?") ? "&" : "?";
      const sr = file.superRes ? "&upscale=true" : "";
      properties["Upload (Vector Files Preferred)"] = `${chosen}${sep}trim=colorUnlessAlpha&trim-tol=10${sr}`;
      properties["_cartImg"] = `${chosen}${sep}trim=colorUnlessAlpha&trim-tol=10&auto=compress&q=50&h=150`;
    }
    properties["_BG Removal Service"] = BGR_MODELS.find((m) => m.key === file.bgrModel)?.label || file.bgrModel;
    for (const m of BGR_MODELS) {
      if (m.key === file.bgrModel) continue;
      const url = file.bgrVersions[m.key];
      if (url) properties[BGR_VERSION_PROP_LABELS[m.key]] = url;
    }
    return properties;
  }

  // src/ported/treat-props.js
  var VECTOR_RE = /\.(svg|eps|pdf|ai)(?:[?#]|$)/i;
  var UPLOAD_KEY = "Upload (Vector Files Preferred)";
  var ORIG_KEY = "_Original Image";
  var PAINTED_RE = /[?&](duotone|monochrome)=/;
  function applyPaintProps(properties, file) {
    const paint = file.ntPaint;
    if (!paint) return;
    properties["_Ink Paint"] = `${paint.color} (${paint.mode})`;
    if (VECTOR_RE.test(String(file.uploadName || file.s3Url || ""))) return;
    if (paint.hostedUrl) {
      if (!properties[ORIG_KEY]) properties[ORIG_KEY] = file.originalUploadReference || file.s3Url;
      properties[UPLOAD_KEY] = `${paint.hostedUrl}?trim=colorUnlessAlpha`;
      properties["_cartImg"] = `${paint.hostedUrl}?trim=colorUnlessAlpha&fm=png&auto=compress&q=50&h=150`;
      return;
    }
    if (!paint.params) return;
    const appendTo = (key) => {
      const u = String(properties[key] || "");
      if (!u || u.indexOf("imgix") === -1 || PAINTED_RE.test(u)) return;
      properties[key] = u + (u.indexOf("?") > -1 ? "&" : "?") + paint.params;
    };
    appendTo(UPLOAD_KEY);
    appendTo("_cartImg");
  }

  // src/ported/sizing-math.js
  var printMethodSource = null;
  function setPrintMethodSource(fn) {
    printMethodSource = typeof fn === "function" ? fn : null;
  }
  function selectedPrintMethod() {
    if (!printMethodSource) return null;
    const value = printMethodSource();
    return value ? String(value) : null;
  }
  function pmTokenKey(s) {
    const t = String(s).toLowerCase().match(/[a-z0-9]+/g);
    return t ? [...new Set(t)].sort().join(" ") : "";
  }
  function variantMatchesPrintMethod(variant, printMethod) {
    const selected = printMethod === void 0 ? selectedPrintMethod() : printMethod;
    if (!selected || !variant.option2 || variant.option2 === selected) return true;
    return pmTokenKey(variant.option2) === pmTokenKey(selected);
  }
  function matchCustomVariant(variants, widthIn, heightIn, printMethod) {
    let area = parseFloat(widthIn) * parseFloat(heightIn) * 6.4516;
    if (!(area >= 1)) area = 1;
    area = Math.ceil(area);
    const customs = variants.filter((v) => v.isCustom && variantMatchesPrintMethod(v, printMethod));
    if (!customs.length) {
      if (variants.some((v) => v.isCustom)) {
        console.error(
          "[bsv4] print-method filter matched 0 custom variants - template print_method values do not match the product option2 strings"
        );
      }
      return null;
    }
    const highestMax = Math.max(...customs.map((v) => v.maxIn));
    if (area > highestMax) {
      return customs.reduce((max, cur) => cur.maxIn > max.maxIn ? cur : max);
    }
    return customs.slice().reverse().find((v) => area >= v.minIn && area <= v.maxIn) || null;
  }
  function parsePopularDims(option1) {
    const raw = (option1 || "").trim();
    const cm = /^([\d.]+)\s*cm\s*[x×]\s*([\d.]+)\s*cm/i.exec(raw);
    if (cm) return { w: parseFloat(cm[1]) / 2.54, h: parseFloat(cm[2]) / 2.54 };
    const m = /^([\d.]+)[\^"]?\s*x\s*([\d.]+)[\^"]?/i.exec(raw);
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }
  function containFit(designW, designH, boxW, boxH) {
    const maxW = parseFloat(boxW);
    const maxH = parseFloat(boxH);
    if (!maxW || !maxH || !designW || !designH) return { w: maxW, h: maxH };
    const aspect = designW / designH;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    return { w: parseFloat(w.toFixed(2)), h: parseFloat(h.toFixed(2)) };
  }
  function clampToMax(w, h, maxW, maxH) {
    w = parseFloat(w);
    h = parseFloat(h);
    if (!Number.isFinite(w) || !Number.isFinite(h) || !(w > 0) || !(h > 0)) return null;
    if (w <= maxW && h <= maxH) return { w, h, clamped: false };
    const scale = Math.min(maxW / w, maxH / h);
    return {
      w: parseFloat((w * scale).toFixed(2)),
      h: parseFloat((h * scale).toFixed(2)),
      clamped: true
    };
  }
  function lockedCounterpart(editedValue, editedAxis, designW, designH) {
    const ratio = editedAxis === "w" ? designH / designW : designW / designH;
    return parseFloat((editedValue * ratio).toFixed(2));
  }
  function parseTiers(tiers2) {
    return tiers2.map((t, i) => {
      const label = String(t.qty).replace(/\s|pcs?/gi, "");
      let min;
      let max;
      if (label.includes("+")) {
        min = parseInt(label, 10);
        max = 1e6;
      } else if (label.includes("-")) {
        const [a, b] = label.split("-");
        min = parseInt(a, 10);
        max = parseInt(b, 10);
      } else {
        min = max = parseInt(label, 10);
      }
      const next = tiers2[i + 1];
      return { index: i + 1, min, max, off: t.pct || 0, nextOff: next ? next.pct || 0 : null };
    });
  }
  function tierFor(parsedTiers2, totalQty) {
    return parsedTiers2.find((t) => totalQty >= t.min && totalQty <= t.max) || null;
  }
  function unitsToNextTier(tier, pageQty2, cartQty) {
    const state2 = cartQty + pageQty2;
    if (pageQty2 === 1) return tier.max + 2 - state2;
    if (tier.max - state2 === 0) return 1;
    return tier.max - state2 + 1;
  }
  var DPI_WARN_MIN = 100;
  function effectiveDpi(pxW, pxH, inW, inH) {
    const area = inW * inH;
    if (!(area > 0) || !(pxW > 0) || !(pxH > 0)) return Infinity;
    return Math.sqrt(pxW * pxH / area);
  }
  function discounted(unitPrice, offPct) {
    const cents = Math.round((parseFloat(unitPrice) || 0) * 100);
    const off = (window.__bsv4TierExactCents ? Math.round : Math.floor)(cents * (offPct || 0) / 100);
    return (cents - off) / 100;
  }
  var round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));
  function resolveUnit(row, variants, printMethod) {
    const direct = parseFloat(row.price);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const list = Array.isArray(variants) ? variants : [];
    if (row.variantId != null) {
      const v = list.find((x) => x.id === row.variantId);
      if (v) return parseFloat(v.price) || 0;
    }
    const hasBands = list.some((v) => v.isCustom);
    if (!hasBands) {
      if (row.popularKey) {
        const byKey = list.find(
          (v) => v.isPopular && variantMatchesPrintMethod(v, printMethod) && v.option1 === row.popularKey
        );
        if (byKey) return parseFloat(byKey.price) || 0;
      }
      for (const v of list) {
        if (!v.isPopular || !variantMatchesPrintMethod(v, printMethod)) continue;
        const dims = parsePopularDims(v.option1);
        if (dims && dims.w === row.w && dims.h === row.h) return parseFloat(v.price) || 0;
      }
    }
    const custom = matchCustomVariant(list, row.w, row.h, printMethod);
    return custom ? parseFloat(custom.price) || 0 : 0;
  }

  // src/ported/cart-payload.js
  function isValidOrderRef(v) {
    if (v === null || v === void 0) return false;
    let s = String(v).trim();
    if (!s) return false;
    s = s.replace(/^previously\s+on\s+order\s*#?\s*/i, "").trim();
    if (!s) return false;
    return /^[A-Z]{2,4}-?\d{4,}$/i.test(s);
  }
  function parseOrderString(imgIxURL, s3URL, order, selectedOptions = "") {
    const result = { imgIx: false, imgIx_params: "", s3: false, s3_params: "" };
    selectedOptions = (selectedOptions || "").replace(/^[?&]+/, "");
    const normalizePart = (part) => part.replace(/^(\[(?:imgIx|s3|both_imgix|both_s3)\])([^?].+)/i, "$1?$2");
    const buildUrl = (baseURL, rawParams, appendSelected) => {
      let url = baseURL;
      if (rawParams) url += rawParams;
      if (appendSelected && selectedOptions) {
        url += (rawParams.includes("?") ? "&" : "?") + selectedOptions;
      }
      return url;
    };
    (order || "").split("&&").forEach((rawPart) => {
      const part = normalizePart(rawPart.trim());
      const match = part.match(/\[(imgIx|s3|both_imgix|both_s3)\](\?[^\s]*)?/i);
      if (!match) return;
      const key = match[1].toLowerCase();
      const rawParams = (match[2] || "").trim();
      if (key === "imgix") {
        result.imgIx = true;
        result.imgIx_params = buildUrl(imgIxURL, rawParams, true);
      } else if (key === "s3") {
        result.s3 = true;
        result.s3_params = buildUrl(s3URL, rawParams, false);
      } else if (key === "both_imgix") {
        result.imgIx = true;
        result.s3 = true;
        result.imgIx_params = buildUrl(imgIxURL, rawParams, true);
        result.s3_params = buildUrl(imgIxURL, rawParams, true);
      } else if (key === "both_s3") {
        result.imgIx = true;
        result.s3 = true;
        result.imgIx_params = buildUrl(s3URL, rawParams, false);
        result.s3_params = buildUrl(s3URL, rawParams, false);
      }
    });
    return result;
  }
  function toggleParams(file) {
    if (!file.bgRemove && !file.superRes) return null;
    let p = "";
    if (file.bgRemove) p += "&bg-remove=true";
    if (file.superRes) p += "&upscale=true";
    return p;
  }
  var attribution = { designType: false, orderName: "", photoId: "" };
  function setAttribution({ designType, orderName, photoId }) {
    if (designType !== void 0) attribution.designType = designType;
    if (orderName !== void 0) attribution.orderName = orderName;
    if (photoId !== void 0) attribution.photoId = photoId;
  }
  function buildItem(file, row, { designNote, precut, popupSource }) {
    const properties = {};
    const options = toggleParams(file) || file.currentOptions || "";
    const orderProperty = parseOrderString(file.imgixUrl, file.s3Url, file.params.order, options);
    if (orderProperty.imgIx) {
      properties["Upload (Vector Files Preferred)"] = orderProperty.imgIx_params || "";
      if (attribution.designType && isValidOrderRef(attribution.photoId)) {
        properties["design re-used"] = `${attribution.photoId}`;
        attribution.designType = false;
        attribution.orderName = "";
      }
      if (attribution.designType === 1 && isValidOrderRef(attribution.orderName)) {
        properties["design re-used"] = `previously on order # ${attribution.orderName}`;
        attribution.designType = false;
        attribution.orderName = "";
      }
    }
    if (orderProperty.s3) {
      if (file.isReadyToPress === "Yes") {
        properties["Upload (Vector Files Preferred)"] = orderProperty.s3_params || "";
      } else if (file.isAIEdit && file.originalUploadReference) {
        properties["_Original Image"] = file.originalUploadReference;
      } else {
        properties["_Original Image"] = orderProperty.s3_params || "";
      }
    }
    properties["_cartImg"] = `${file.imgixUrl}?${file.params.cart}${toggleParams(file) || file.currentOptions || ""}`;
    properties["Remove Background"] = file.bgRemove ? "Yes" : "No";
    properties["Super Resolution"] = file.superRes ? "Yes" : "No";
    if (selectedPrintMethod()) properties["Print Method"] = selectedPrintMethod();
    Object.assign(properties, window.__bsv4ItemProps && window.__bsv4ItemProps(file, row) || {});
    if (designNote) properties["Design Notes"] = designNote;
    if (row.dpiWarn) properties["DPI Warning"] = "Yes";
    if (row.mode === "popular") properties["_pop size"] = "Yes";
    if (precut) properties["Precut"] = "Yes";
    properties["_width"] = (parseFloat(row.w) || 0).toFixed(2);
    properties["_height"] = (parseFloat(row.h) || 0).toFixed(2);
    {
      const _wIn = parseFloat(row.w) || 0;
      const _hIn = parseFloat(row.h) || 0;
      properties["Width (cm)"] = (_wIn * 2.54).toFixed(2);
      properties["Height (cm)"] = (_hIn * 2.54).toFixed(2);
    }
    if (file.isReadyToPress === "Yes") properties["_Ready to Press"] = "Yes";
    if (file.isRtpAiEdit === "Yes") properties["_Ready to Press with AI Edit"] = "Yes";
    if (file.isDesignStudio === "Yes") properties["_Design Studio"] = "Yes";
    if (file.addText && file.addText.layers && file.addText.layers.length) properties["_Text Added"] = "Yes";
    if (file.isAIImage) properties["AI Created"] = "Yes";
    if (file.isAIEdit) {
      properties["AI Edit"] = "Yes";
      if (file.aiEditCount) properties["_AI Edit Count"] = String(file.aiEditCount);
    }
    if (file.ntOrderProps) Object.assign(properties, file.ntOrderProps);
    if (popupSource) properties["_popup_source"] = "dtf_popup";
    applyBgrProps(properties, file);
    applyPaintProps(properties, file);
    return { id: row.variantId, quantity: row.qty, properties };
  }

  // src/ported/ai-edit.js
  var PROXY_URL = "https://ruhg140mmf.execute-api.us-east-1.amazonaws.com/";
  var STORE = "uk";
  function withCsStrip(url) {
    if (!url || typeof url !== "string") return url;
    if (/[?&]cs=/.test(url)) return url;
    if (!/imgix\.net/i.test(url)) return url;
    return url + (url.indexOf("?") >= 0 ? "&" : "?") + "cs=strip";
  }
  async function nbEdit(imageUrl, promptText, falKey, signal) {
    const resp = await fetch(PROXY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "gram-edit-45g",
        // codenamed request token (vendor-scrub Phase 4)
        imageUrl,
        prompt: promptText,
        transparency: "AUTO",
        store: STORE
      }),
      signal
    });
    const txt = await resp.text().catch(() => "");
    let data = null;
    try {
      data = JSON.parse(txt);
    } catch (e) {
      data = null;
    }
    if (!resp.ok) {
      const detail = data && (data.details || data.error) || txt.slice(0, 200);
      throw new Error(`AI edit request failed (${resp.status}): ${detail}`);
    }
    if (!data || !data.url) throw new Error("AI edit response missing url");
    return withCsStrip(data.url);
  }

  // src/uploader.js
  var EXT_MIME = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    ai: "application/postscript",
    eps: "application/postscript",
    pdf: "application/pdf",
    tif: "image/tiff",
    tiff: "image/tiff",
    svg: "image/svg+xml",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
    avif: "image/avif",
    psd: "image/vnd.adobe.photoshop",
    gif: "image/gif",
    bmp: "image/bmp",
    jfif: "image/jpeg"
    // JFIF is JPEG — normalized to jpg (rides the jpg path)
  };
  var MAX_BYTES = 5368709120;
  var IMGIX_RENDER_EXTS = /^(webp|heic|heif|avif|psd|gif|bmp)$/;
  var IMGIX_MAX_BYTES = 104857600;
  var DEFAULT_MAX_FILES = 25;
  var cfg;
  var els;
  var seq = 0;
  var announcingStore = false;
  var tabEventsWired = false;
  var aiShimsInstalled = false;
  var addAnotherGate = null;
  function setAddAnotherGate(fn) {
    addAnotherGate = fn;
  }
  function installAiHandoffShims() {
    if (aiShimsInstalled) return;
    aiShimsInstalled = true;
    const w = window;
    const toEntry = (d) => {
      const url = String(d && (d.fileURL || d.url) || "").split("?")[0];
      if (!url) return null;
      return {
        url,
        name: d && d.name || url.split("/").pop(),
        isAIImage: d && d.isAIImage || void 0,
        isAIEdit: d && d.isAIEdit || void 0,
        isDesignStudio: d && d.isDesignStudio || void 0,
        isReadyToPress: d && d.isReadyToPress || void 0,
        isRtpAiEdit: d && d.isRtpAiEdit || void 0
      };
    };
    if (typeof w.getPresignedUploadUrl !== "function") {
      w.getPresignedUploadUrl = getPresignedUploadUrl;
    }
    if (typeof w.nbEditRequest !== "function" && cfg && cfg.preview && cfg.preview.aiEdit) {
      w.nbEditRequest = (imageUrl, promptText) => nbEdit(imageUrl, promptText, cfg.falKey);
    }
    if (!w.__dtfutFetchPatched && typeof w.fetch === "function") {
      w.__dtfutFetchPatched = true;
      const origFetch = w.fetch;
      w.fetch = function(url, options) {
        try {
          const urlStr = typeof url === "string" ? url : url && url.url || "";
          if (urlStr.indexOf("getGenerativeAiUploads.php") !== -1) {
            return origFetch.apply(this, arguments).then(
              (resp) => resp.clone().json().then((data) => {
                const capped = Array.isArray(data) ? data.slice(0, 3) : [];
                if (capped.length > 0) w.__dtfutSkipNewAIGeneration = true;
                return new Response(JSON.stringify(capped), {
                  status: resp.status,
                  headers: { "Content-Type": "application/json" }
                });
              }).catch(() => resp)
            );
          }
          if (w.__dtfutSkipNewAIGeneration && urlStr.indexOf("postBatchGenerativeAiUpload.php") !== -1) {
            w.__dtfutSkipNewAIGeneration = false;
            return Promise.resolve(
              new Response("[]", { status: 200, headers: { "Content-Type": "application/json" } })
            );
          }
        } catch (e) {
        }
        return origFetch.apply(this, arguments);
      };
    }
    if (typeof w.createFileObjectFromUrl !== "function") {
      w.createFileObjectFromUrl = (url) => ({
        name: String(url || "").split("?")[0].split("/").pop() || "ai-image.png",
        fileURL: url
      });
    }
    if (typeof w.singleFileManageByURL !== "function") {
      w.singleFileManageByURL = (detail) => {
        const e = toEntry(detail);
        if (!e) return;
        if (addAnotherGate && addAnotherGate({ entries: [e] })) return;
        intakeFromUrls([e]);
      };
    }
    if (typeof w.multiFilesManageByURL !== "function") {
      w.multiFilesManageByURL = (files) => {
        const entries = (files || []).map(toEntry).filter(Boolean);
        if (!entries.length) return;
        if (addAnotherGate && addAnotherGate({ entries })) return;
        intakeFromUrls(entries);
      };
    }
    if (typeof w.uploadFile !== "function") {
      w.uploadFile = (file) => {
        if (!file) return;
        queueMicrotask(() => {
          if (w.isAIImage) {
            file.isAIImage = true;
            w.isAIImage = false;
          }
          if (addAnotherGate && addAnotherGate({ files: [file] })) return;
          intake([file]);
        });
      };
    }
    if (typeof w.manageFiles !== "function") {
      w.manageFiles = (files) => {
        if (!files || !files.length) return;
        if (addAnotherGate && addAnotherGate({ files })) return;
        intake(files);
      };
    }
  }
  function setSource(src) {
    if (!els || !els.box) return;
    for (const pill of els.box.querySelectorAll("[data-bsv4-source]")) {
      pill.classList.toggle("is-active", pill.dataset.bsv4Source === src);
    }
    const titleEl = els.box.querySelector("[data-bsv4-title]");
    const activePill = els.box.querySelector(`[data-bsv4-source="${src}"]`);
    if (titleEl && activePill && activePill.dataset.bsv4TitleText) {
      titleEl.textContent = activePill.dataset.bsv4TitleText;
    }
    const panelSrc = src === "studio" ? "upload" : src;
    for (const panel of els.box.querySelectorAll("[data-bsv4-panel]")) {
      panel.hidden = panel.dataset.bsv4Panel !== panelSrc;
    }
    announce("source-changed", { source: src });
    if (src === "ai") {
      document.dispatchEvent(
        new CustomEvent("dtfut:tab-change", { detail: { name: "ai" }, bubbles: true })
      );
    }
  }
  function initUploader(root2, config) {
    cfg = config;
    const box = root2.querySelector('[data-bsv4-block="upload_box"]');
    if (!box || !cfg.uploadBox) return;
    if (box.dataset.bsv4UploaderBound === "1") return;
    box.dataset.bsv4UploaderBound = "1";
    els = {
      root: root2,
      box,
      zone: box.querySelector("[data-bsv4-dropzone]"),
      input: box.querySelector("[data-bsv4-file-input]")
    };
    if (!els.zone || !els.input) return;
    els.ehead = box.querySelector("[data-bsv4-ehead]");
    els.esub = box.querySelector("[data-bsv4-esub]");
    els.dhead = els.zone.querySelector(".bsv4-uploader__headline:not(.bsv4-uploader__ehead)");
    els.dhint = els.zone.querySelector(".bsv4-uploader__hint:not(.bsv4-uploader__esub)");
    const errClose = box.querySelector("[data-bsv4-errclose]");
    if (errClose) errClose.addEventListener("click", (e) => {
      e.stopPropagation();
      clearZoneError();
    });
    installCancelHook();
    const entries = cfg.uploadBox.entries || {};
    for (const [source, on] of [
      ["previous", entries.previousDesigns],
      ["ai", entries.aiCreate],
      ["studio", entries.designStudio]
    ]) {
      const pill = box.querySelector(`[data-bsv4-source="${source}"]`);
      if (pill) pill.hidden = on === false;
    }
    els.input.accept = allowedExts().map((e) => `.${e}`).join(",");
    els.input.multiple = resolveIntakeCap(cfg) > 1;
    els.zone.addEventListener("click", () => els.input.click());
    box.addEventListener("click", (e) => {
      const pill = e.target.closest("[data-bsv4-source]");
      if (!pill) return;
      e.preventDefault();
      const src = pill.dataset.bsv4Source;
      setSource(src);
      if (e.isTrusted && src === "upload" && root2.dataset.state === "empty" && els.zone.offsetParent !== null) {
        els.input.click();
      }
    });
    els.zone.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        els.input.click();
      }
    });
    els.zone.addEventListener("dragover", (e) => {
      e.preventDefault();
      clearZoneError();
      els.zone.classList.add("is-dragover");
    });
    els.zone.addEventListener("dragleave", () => els.zone.classList.remove("is-dragover"));
    els.zone.addEventListener("drop", (e) => {
      e.preventDefault();
      els.zone.classList.remove("is-dragover");
      if (e.dataTransfer && e.dataTransfer.files.length) intake(e.dataTransfer.files);
    });
    els.input.addEventListener("change", () => {
      if (els.input.files.length) intake(els.input.files);
    });
    initPageDrop();
    box.addEventListener("paste", (e) => {
      const pasted = e.clipboardData && e.clipboardData.files;
      if (pasted && pasted.length) {
        e.preventDefault();
        intake(pasted);
      }
    });
    const addZone = root2.querySelector("[data-bsv4-add-zone]");
    if (addZone && addZone.dataset.bsv4UploaderBound !== "1") {
      addZone.dataset.bsv4UploaderBound = "1";
      addZone.addEventListener("click", () => els.input.click());
      addZone.addEventListener("dragover", (e) => {
        e.preventDefault();
        addZone.classList.add("is-dragover");
      });
      addZone.addEventListener("dragleave", () => addZone.classList.remove("is-dragover"));
      addZone.addEventListener("drop", (e) => {
        e.preventDefault();
        addZone.classList.remove("is-dragover");
        if (e.dataTransfer && e.dataTransfer.files.length) intake(e.dataTransfer.files);
      });
    }
    installAiHandoffShims();
    if (!tabEventsWired) {
      tabEventsWired = true;
      document.addEventListener("bsv4:files-received", () => setSource("upload"));
    }
    if (!announcingStore) {
      announcingStore = true;
      let known = getState().files.map((f) => f.id);
      subscribe((state2, patch) => {
        if (patch.status === "empty" && els) {
          els.zone.classList.remove("is-dragover");
          clearZoneError();
        }
        if (!("files" in patch)) return;
        const ids = state2.files.map((f) => f.id);
        for (const f of state2.files) {
          if (!known.includes(f.id)) announce("file-added", { id: f.id, name: f.name });
        }
        for (const id of known) {
          if (!ids.includes(id)) announce("file-removed", { id });
        }
        known = ids;
      });
    }
  }
  var pageDropWired = false;
  var pageDropActive = false;
  function isFileDrag(e) {
    const types = e.dataTransfer && e.dataTransfer.types;
    if (!types) return false;
    for (const t of types) if (t === "Files") return true;
    return false;
  }
  function showDropScreen() {
    if (document.querySelector(".bsv4-dropscreen")) return;
    const el = document.createElement("div");
    el.className = "bsv4-dropscreen";
    const plural = els && els.input && els.input.multiple;
    el.innerHTML = `<div class="bsv4-dropscreen__inner"><div class="bsv4-dropscreen__glyph" aria-hidden="true"></div><div class="bsv4-dropscreen__title">Drop your file${plural ? "(s)" : ""}</div><div class="bsv4-dropscreen__sub">anywhere to upload</div></div>`;
    document.body.appendChild(el);
  }
  function clearDropScreen() {
    const el = document.querySelector(".bsv4-dropscreen");
    if (el) el.remove();
    pageDropActive = false;
  }
  function initPageDrop() {
    if (pageDropWired) return;
    pageDropWired = true;
    document.addEventListener(
      "dragover",
      (e) => {
        if (getState().status !== "empty") return;
        if (!isFileDrag(e)) return;
        e.preventDefault();
        if (!pageDropActive) {
          pageDropActive = true;
          showDropScreen();
        }
      },
      true
    );
    document.addEventListener(
      "dragleave",
      (e) => {
        if (!e.relatedTarget) clearDropScreen();
      },
      true
    );
    document.addEventListener("dragend", clearDropScreen, true);
    document.addEventListener(
      "drop",
      (e) => {
        if (pageDropActive) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const files = e.dataTransfer && e.dataTransfer.files;
          if (files && files.length) intake(files);
        }
        clearDropScreen();
      },
      true
    );
  }
  function resolveIntakeCap(config) {
    const c = config || {};
    const entryOn = c.uploadBox && c.uploadBox.entries ? c.uploadBox.entries.multiupload !== false : true;
    if (!c.modal || !entryOn) return 1;
    const n = Number(c.modal.maxFiles);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_FILES;
  }
  function fileIntakeError(file, exts) {
    const ext = String((file && file.name || "").split(".").pop() || "").toLowerCase();
    const okExt = exts.includes(ext);
    const okMime = exts.some((e) => {
      const mime = EXT_MIME[e];
      return mime && file.type && file.type.indexOf(mime) !== -1;
    });
    if (!okExt && !okMime) {
      return exts.length ? `File type not supported. Allowed: ${exts.map((e) => e.toUpperCase()).join(", ")}` : "File type not supported.";
    }
    if (file.size > MAX_BYTES) return "File must be 5 GB or less";
    if (IMGIX_RENDER_EXTS.test(ext) && file.size > IMGIX_MAX_BYTES)
      return "That file is too large to process \u2014 please upload a version under 100 MB (export as PNG or TIFF).";
    return null;
  }
  function allowedExts() {
    const list = cfg && cfg.uploadBox && Array.isArray(cfg.uploadBox.fileTypes) ? cfg.uploadBox.fileTypes : [];
    return list.map((e) => String(e).toLowerCase().replace(/^\./, "")).filter(Boolean);
  }
  async function intake(fileList) {
    if (!els) return;
    if (getState().status === "uploading" && getState().mode !== "multi") return;
    let files = Array.from(fileList);
    els.input.value = "";
    const cap = resolveIntakeCap(cfg);
    const existing = getState().files.length;
    if (cap === 1 && existing + files.length > 1) {
      surfaceUploadError(activeUploadZone(), "single", {
        msg: existing ? "This product takes one design at a time \u2014 remove the current design first." : "This product takes one design at a time \u2014 only the first file was kept."
      });
      files = files.slice(0, Math.max(0, 1 - existing));
      if (!files.length) return;
    } else if (existing + files.length > cap) {
      const skipped = existing + files.length - cap;
      surfaceUploadError(activeUploadZone(), "limit", { count: existing + files.length, cap, skipped });
      files = files.slice(0, cap - existing);
      if (!files.length) return;
    }
    const exts = allowedExts();
    const accepted = [];
    let typeRejected = false;
    let typeExt = "";
    let otherReject = null;
    for (const file of files) {
      const err = fileIntakeError(file, exts);
      if (err) {
        if (/not supported/i.test(err)) {
          typeRejected = true;
          if (!typeExt) typeExt = "." + String((file && file.name || "").split(".").pop() || "").toLowerCase();
        } else otherReject = err;
        announce("file-rejected", { name: file.name, error: err });
        continue;
      }
      accepted.push(file);
    }
    if (typeRejected) surfaceUploadError(activeUploadZone(), "type", { ext: typeExt });
    else if (otherReject) surfaceUploadError(activeUploadZone(), "other", { msg: otherReject });
    if (!accepted.length) return;
    clearZoneError();
    update({
      status: "uploading",
      mode: getState().mode === "multi" || accepted.length > 1 || getState().files.length ? "multi" : "single"
    });
    announce("files-received", { count: accepted.length });
    ldlReset();
    activeBatches++;
    try {
      await Promise.all(accepted.map((file) => uploadOne(file)));
    } finally {
      activeBatches--;
    }
    settleBatch();
  }
  function settleBatch() {
    if (activeBatches > 0) return;
    const state2 = getState();
    if (state2.files.length) {
      update({ status: "uploaded" });
      announce("upload-complete", { count: state2.files.length });
      if (state2.mode === "multi") ldlDone();
      scrollToTop();
    } else {
      ldlDone();
      update({ status: "empty", mode: "single" });
    }
  }
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  async function uploadBlob(file, onProgress, onXhr) {
    if (!file.type) {
      const ext = String((file.name || "").split(".").pop() || "").toLowerCase();
      file = new File([file], file.name, { type: EXT_MIME[ext] || "application/octet-stream" });
    }
    const uploadName = buildUploadName(file.name);
    const presign = await getPresignedUploadUrl(uploadName, file.type);
    if (!presign || !presign.url) throw new Error("presign unavailable");
    await putToS3(presign.url, file, onProgress, onXhr);
    const crc = await crc32Hex(file);
    return {
      uploadName,
      crc,
      s3Url: presign.sourceUrl,
      imgixUrl: `https://${IMGIX_HOST}/${uploadName}`
    };
  }
  var ldlFracs = /* @__PURE__ */ new Map();
  var ldlCancelled = false;
  var activeBatches = 0;
  var ldlT0 = 0;
  var ldlEta = 0;
  var ldlTarget = 0;
  var ldlShown = 0;
  var ldlDriver = 0;
  var ldlBgr = false;
  var ldlXfers = 0;
  var activeXhrs = /* @__PURE__ */ new Set();
  function ldlReset() {
    ldlFracs.clear();
    ldlCancelled = false;
    ldlBgr = false;
    ldlXfers = 0;
    ldlT0 = Date.now();
    ldlEta = ldlTarget = ldlShown = 0;
    clearInterval(ldlDriver);
    ldlDriver = setInterval(() => {
      const tail = ldlFracs.size && ldlXfers === 0 && ldlTarget >= 90;
      const cap = tail || ldlTarget >= 95 ? 99 : ldlTarget;
      const step = tail && ldlShown >= 88 ? Math.max(0.02, (99 - ldlShown) * 0.012) : Math.max(0.3, (cap - ldlShown) * 0.12);
      ldlShown = Math.min(ldlShown + step, cap);
      ldlWrite(Math.round(ldlShown));
    }, 120);
    ldlWrite(0);
  }
  function ldlDone() {
    clearInterval(ldlDriver);
    ldlShown = ldlTarget = 100;
    ldlWrite(100);
  }
  document.addEventListener("bsv4:preview-ready", ldlDone);
  function ldlReport(id, frac) {
    ldlFracs.set(id, Math.max(ldlFracs.get(id) || 0, frac));
    let sum = 0;
    for (const v of ldlFracs.values()) sum += v;
    ldlTarget = Math.round(sum / ldlFracs.size * 100);
  }
  function ldlWrite(pct) {
    if (!els || !els.root) return;
    let eta = "";
    const tp = ldlTarget;
    if (ldlFracs.size && ldlXfers === 0 && tp >= 50 && pct < 100) {
      eta = ldlBgr ? "Removing background\u2026" : "Preparing preview\u2026";
    } else if (ldlT0 && tp >= 3 && tp <= 90) {
      const remain = (Date.now() - ldlT0) / tp * (100 - tp) / 1e3;
      ldlEta = ldlEta ? ldlEta * 0.6 + remain * 0.4 : remain;
      if (ldlEta > 8) {
        eta = ldlEta > 90 ? `about ${Math.round(ldlEta / 60)}m left` : `about ${Math.ceil(ldlEta / 5) * 5}s left`;
      }
    }
    for (const node of els.root.querySelectorAll("[data-bsv4-upprog]")) {
      node.textContent = `${pct}%`;
    }
    for (const node of els.root.querySelectorAll("[data-bsv4-upeta]")) {
      node.textContent = eta;
    }
    for (const bar of els.root.querySelectorAll(".bsv4-preview__ringbar")) {
      bar.style.strokeDashoffset = 427.26 * (1 - Math.max(pct, 3) / 100);
    }
  }
  function installCancelHook() {
    window.resetLoading = () => {
      ldlCancelled = true;
      for (const x of activeXhrs) {
        try {
          x.abort();
        } catch (e) {
        }
      }
      activeXhrs.clear();
    };
  }
  function blobUrlFor(file) {
    try {
      return /\.(png|jpe?g|svg)$/i.test(file.name || "") ? URL.createObjectURL(file) : null;
    } catch (e) {
      return null;
    }
  }
  var ALPHA_CORNER_EXTS = /^(png|webp|tif)$/;
  var VECTOR_ALPHA_EXTS = /^(ai|eps|pdf|svg)$/;
  function cornersTransparent(source) {
    try {
      const c = document.createElement("canvas");
      c.width = 64;
      c.height = 64;
      const x = c.getContext("2d");
      x.drawImage(source, 0, 0, 64, 64);
      const d = x.getImageData(0, 0, 64, 64).data;
      const a = (px, py) => d[(py * 64 + px) * 4 + 3];
      return a(0, 0) < 16 && a(63, 0) < 16 && a(0, 63) < 16 && a(63, 63) < 16;
    } catch (e) {
      return false;
    }
  }
  async function hasTransparentCorners(file) {
    try {
      const bmp = await createImageBitmap(file);
      const r = cornersTransparent(bmp);
      if (bmp.close) bmp.close();
      return r;
    } catch (e) {
      return false;
    }
  }
  function hasTransparentCornersRemote(imgixUrl) {
    return new Promise((resolve) => {
      try {
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = () => resolve(cornersTransparent(im));
        im.onerror = () => resolve(false);
        im.src = `${String(imgixUrl).split("?")[0]}?fm=png&w=64`;
      } catch (e) {
        resolve(false);
      }
    });
  }
  async function uploadOne(file) {
    const id = `f-${++seq}`;
    const localUrl = blobUrlFor(file);
    announce("upload-started", { id, name: file.name, localUrl });
    ldlReport(id, 0);
    const alphaProbe = ALPHA_CORNER_EXTS.test((file.name.split(".").pop() || "").toLowerCase()) ? hasTransparentCorners(file) : Promise.resolve(false);
    try {
      ldlXfers++;
      const up = await uploadBlob(
        file,
        (frac) => {
          ldlReport(id, frac * 0.9);
        },
        (xhr) => activeXhrs.add(xhr)
      );
      ldlXfers = Math.max(0, ldlXfers - 1);
      if (ldlCancelled) return;
      const { uploadName, crc } = up;
      const rawExt = (file.name.split(".").pop() || "").toLowerCase();
      let ext = rawExt === "jpeg" || rawExt === "jfif" ? "jpg" : rawExt === "tiff" ? "tif" : rawExt === "heif" ? "heic" : rawExt;
      if (!(cfg.imgixParams || {})[ext]) ext = "png";
      const params = (cfg.imgixParams || {})[ext] || {};
      const hasAlpha = await alphaProbe;
      const autoBgr = !!params.bgRemoverDefault && cfg.preview?.bgRemove !== false && !cfg.autoBgRemoveOff;
      const probing = autoBgr && !hasAlpha && VECTOR_ALPHA_EXTS.test(ext);
      const remoteProbe = probing ? hasTransparentCornersRemote(up.imgixUrl) : Promise.resolve(false);
      if (params.preview) {
        remoteProbe.then((remoteAlpha2) => {
          const bgR = autoBgr && !hasAlpha && !remoteAlpha2;
          new Image().src = bgR ? candidateUrl(up.imgixUrl, BGR_MODELS[0]) : `${up.imgixUrl}?${params.preview}`;
        });
      }
      const record = {
        id,
        name: file.name,
        uploadName,
        ext,
        s3Url: up.s3Url,
        imgixUrl: up.imgixUrl,
        crc,
        params,
        hasAlpha,
        // The matrix's per-type default only applies when the preview block's
        // Background Remove toggle is on — a merchant turning the feature OFF
        // must not get silently bg-removed art with the control hidden.
        // Already-transparent art (hasAlpha) defaults OFF too — see the
        // alpha-corner probe above.
        bgRemove: autoBgr && !hasAlpha,
        superRes: false,
        // (record.bgRemove feeds the loader's honest tail copy)
        // Attribution riders (cart-payload.js:126-129 stamps from these): the
        // AI shims tag the File object (manageFiles direct, uploadFile via the
        // one-shot window-flag consume above).
        isAIImage: file.isAIImage || void 0,
        isAIEdit: file.isAIEdit || void 0,
        rows: []
      };
      if (record.bgRemove && !probing) ldlBgr = true;
      const [dims, remoteAlpha] = await Promise.all([
        resolveDimensions(record, params.preview),
        remoteProbe
      ]);
      if (remoteAlpha) {
        record.hasAlpha = true;
        record.bgRemove = false;
      }
      if (record.bgRemove && probing) ldlBgr = true;
      Object.assign(record, dims);
      if (record.inW != null) record.widthIn = Number(record.inW);
      if (record.inH != null) record.heightIn = Number(record.inH);
      ldlReport(id, 0.95);
      if (ldlCancelled) return;
      update({ files: [...getState().files, record] });
      announce("file-uploaded", { id, name: file.name, inW: record.inW, inH: record.inH });
      saveFileToUser({ customerId: cfg.customerId, productId: cfg.productId, fileRecord: record });
    } catch (err) {
      if (err && err.name === "AbortError") return;
      showError(`${file.name}: ${err.message || "upload failed"} \u2014 please try again.`);
      announce("file-failed", { id, name: file.name, error: String(err && err.message) });
    } finally {
      if (localUrl) setTimeout(() => URL.revokeObjectURL(localUrl), 6e4);
    }
  }
  async function intakeFromUrls(entries) {
    if (!els) return;
    if (!entries.length) return;
    if (getState().status === "uploading" && getState().mode !== "multi") return;
    const cap = resolveIntakeCap(cfg);
    const room = cap - getState().files.length;
    if (entries.length > room) {
      showError(
        cap === 1 ? "This product takes one design at a time \u2014 only the first selection was kept." : `Maximum ${cap} designs per order \u2014 ${entries.length - Math.max(0, room)} selection(s) skipped.`
      );
      entries = entries.slice(0, Math.max(0, room));
      if (!entries.length) return;
    }
    if (entries.length === 1 && entries[0].orderName) {
      setAttribution({ designType: 1, orderName: entries[0].orderName });
    }
    update({
      status: "uploading",
      mode: getState().mode === "multi" || entries.length > 1 || getState().files.length ? "multi" : "single"
    });
    announce("files-received", { count: entries.length, source: "previous-designs" });
    ldlReset();
    activeBatches++;
    try {
      await Promise.all(
        entries.map(async (entry) => {
          const id = `f-${++seq}`;
          const urlBg = /bg-remove=true/i.test(String(entry.url || ""));
          const urlSr = /upscale=true/i.test(String(entry.url || ""));
          const clean = entry.url.split("?")[0];
          const uploadName = clean.split("/").pop();
          const rawExt = (uploadName.split(".").pop() || "").toLowerCase();
          let ext = rawExt === "jpeg" || rawExt === "jfif" ? "jpg" : rawExt === "tiff" ? "tif" : rawExt === "heif" ? "heic" : rawExt;
          if (!(cfg.imgixParams || {})[ext]) ext = "png";
          announce("upload-started", { id, name: entry.name || uploadName });
          ldlReport(id, 0.5);
          try {
            let params = (cfg.imgixParams || {})[ext] || {};
            if (entry.isReadyToPress === "Yes") {
              params = { ...params, order: "[S3]" };
            }
            const reuseImgix = clean.split(S3_HOST).join(IMGIX_HOST);
            const wouldDefaultOn = entry.bgRemove !== true && !urlBg && !!params.bgRemoverDefault && !cfg.autoBgRemoveOff;
            const hasAlpha = wouldDefaultOn && (ALPHA_CORNER_EXTS.test(ext) || VECTOR_ALPHA_EXTS.test(ext)) && entry.isReadyToPress !== "Yes" ? await hasTransparentCornersRemote(reuseImgix) : false;
            const record = {
              id,
              name: entry.name || uploadName,
              uploadName,
              ext,
              s3Url: clean.split(IMGIX_HOST).join(S3_HOST),
              imgixUrl: reuseImgix,
              crc: null,
              params,
              hasAlpha,
              isReadyToPress: entry.isReadyToPress,
              isRtpAiEdit: entry.isRtpAiEdit,
              isDesignStudio: entry.isDesignStudio,
              isAIImage: entry.isAIImage,
              isAIEdit: entry.isAIEdit,
              // Same merchant-toggle guard as fresh uploads; RTP files stay
              // untransformed (raw S3 order contract — never re-treat them).
              bgRemove: (
                // the "disable default bg remove" account tag suppresses only the
                // AUTO default; an explicit carried removal (entry.bgRemove / URL
                // param) still wins.
                (entry.bgRemove === true || urlBg || !!params.bgRemoverDefault && !cfg.autoBgRemoveOff && !hasAlpha) && entry.isReadyToPress !== "Yes" && cfg.preview?.bgRemove !== false
              ),
              superRes: (entry.superRes === true || urlSr) && entry.isReadyToPress !== "Yes",
              rows: []
            };
            if (record.params.preview && entry.isReadyToPress !== "Yes") {
              new Image().src = record.bgRemove ? candidateUrl(record.imgixUrl, BGR_MODELS[0]) : `${record.imgixUrl}?${record.params.preview}`;
            }
            Object.assign(record, await resolveDimensions(record, record.params.preview));
            if (record.inW != null) record.widthIn = Number(record.inW);
            if (record.inH != null) record.heightIn = Number(record.inH);
            ldlReport(id, 0.95);
            if (ldlCancelled) return;
            update({ files: [...getState().files, record] });
            announce("file-uploaded", { id, name: record.name, inW: record.inW, inH: record.inH });
          } catch (err) {
            showError(`${entry.name || uploadName}: ${err.message || "could not load design"}`);
            announce("file-failed", { id, name: entry.name || uploadName, error: String(err && err.message) });
          }
        })
      );
    } finally {
      activeBatches--;
    }
    settleBatch();
  }
  function activeUploadZone() {
    if (!els) return null;
    const modalAdd = els.root && els.root.querySelector("[data-bsv4-modal-addfile]");
    if (getState().mode === "multi" && modalAdd && modalAdd.offsetParent !== null) return modalAdd;
    const add = els.root && els.root.querySelector("[data-bsv4-add-another]");
    if (getState().status !== "empty" && add && add.offsetParent !== null) return add;
    return els.zone;
  }
  function fileTypesSentence() {
    const list = allowedExts();
    if (!list.length) return "That file type is not supported.";
    const labels = [];
    const used = /* @__PURE__ */ new Set();
    for (const e of list) {
      if (used.has(e)) continue;
      if (e === "jpg" || e === "jpeg" || e === "jfif") {
        labels.push("JPG / JPEG");
        used.add("jpg");
        used.add("jpeg");
        used.add("jfif");
      } else if (e === "tif" || e === "tiff") {
        labels.push("TIF / TIFF");
        used.add("tif");
        used.add("tiff");
      } else if (e === "heic" || e === "heif") {
        labels.push("HEIC / HEIF");
        used.add("heic");
        used.add("heif");
      } else {
        labels.push(e.toUpperCase());
        used.add(e);
      }
    }
    const last = labels.pop();
    const joined = labels.length ? `${labels.join(", ")} and ${last}` : last;
    return `We accept the following file types: ${joined}`;
  }
  var errCardTimer = null;
  function showUploadErrorCard(zone, message) {
    if (!zone) return;
    clearTimeout(errCardTimer);
    const prev = zone.querySelector(":scope > .bsv4-uperr");
    if (prev) prev.remove();
    zone.classList.add("bsv4-uperr-host");
    const card = document.createElement("div");
    card.className = "bsv4-uperr";
    card.setAttribute("role", "alert");
    card.innerHTML = '<span class="bsv4-uperr__circle" aria-hidden="true"><svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="#e5484d" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5"/><path d="M12 16.4v.1"/></svg></span><p class="bsv4-uperr__head">Couldn\u2019t upload your files</p><p class="bsv4-uperr__msg"></p><button type="button" class="bsv4-uperr__dismiss">Dismiss</button>';
    card.querySelector(".bsv4-uperr__msg").textContent = message;
    const close = () => {
      clearTimeout(errCardTimer);
      card.remove();
      zone.classList.remove("bsv4-uperr-host");
    };
    card.addEventListener("click", (e) => e.stopPropagation());
    card.querySelector(".bsv4-uperr__dismiss").addEventListener("click", (e) => {
      e.stopPropagation();
      close();
    });
    zone.appendChild(card);
    errCardTimer = setTimeout(close, 4e3);
  }
  function showError(msg) {
    surfaceUploadError(activeUploadZone(), "other", { msg });
  }
  function surfaceUploadError(zone, kind, data) {
    data = data || {};
    const onMain = els && zone === els.zone;
    let title;
    let parts;
    let msg;
    if (kind === "type") {
      title = "File not supported";
      msg = fileTypesSentence();
      parts = data.ext ? [{ b: true, t: data.ext + " " }, { t: "isn\u2019t a supported file type. Please use one of the formats below." }] : [{ t: "That file type is not supported. Please use one of the formats below." }];
    } else if (kind === "limit") {
      title = "Too many files";
      msg = `Maximum ${data.cap} designs per order \u2014 ${data.skipped} file(s) skipped.`;
      parts = [{ b: true, t: `${data.count} files` }, { t: ` selected \u2014 the limit is ${data.cap}. Remove a few and try again.` }];
    } else if (kind === "single") {
      title = "One file at a time";
      msg = data.msg;
      parts = [{ t: data.msg }];
    } else {
      title = "Couldn\u2019t upload";
      msg = data.msg;
      parts = [{ t: data.msg }];
    }
    if (onMain) showZoneError(title, parts);
    else showUploadErrorCard(zone, msg);
  }
  function showZoneError(title, parts) {
    if (!els || !els.ehead || !els.esub) return;
    els.ehead.textContent = title;
    els.esub.textContent = "";
    for (const p of parts) {
      const t = document.createTextNode(p.t);
      if (p.b) {
        const b = document.createElement("b");
        b.appendChild(t);
        els.esub.appendChild(b);
      } else {
        els.esub.appendChild(t);
      }
    }
    if (els.dhead) els.dhead.hidden = true;
    if (els.dhint) els.dhint.hidden = true;
    els.ehead.hidden = false;
    els.esub.hidden = false;
    els.zone.classList.add("is-error");
  }
  function clearZoneError() {
    if (!els || !els.zone.classList.contains("is-error")) return;
    els.zone.classList.remove("is-error");
    if (els.ehead) els.ehead.hidden = true;
    if (els.esub) els.esub.hidden = true;
    if (els.dhead) els.dhead.hidden = false;
    if (els.dhint) els.dhint.hidden = false;
  }

  // src/preview.js
  var NINJA_GIF = "https://cdn.shopify.com/s/files/1/0558/0265/8899/files/Ninja-Running-Loop-200x-200-cleaned.gif?v=1758717834";
  var STATUS = [
    "Sharpening the katana\u2026",
    "Tightening the headband\u2026",
    "Tossing a smoke bomb\u2026",
    "Nunchucking your enemies\u2026",
    "Loading at ninja speed\u2026",
    "Consulting the scrolls\u2026",
    "Polishing the shuriken\u2026",
    "Dusting off the ancient scrolls\u2026",
    "Unsheathing the pixels\u2026"
  ];
  var cfg2;
  var stagePending = null;
  var stageCanvas = null;
  var stageDocBound = false;
  function initPreview(root2, config) {
    if (config) cfg2 = config;
    const block = root2.querySelector('[data-bsv4-block="upload_preview"]');
    if (!block || !cfg2 || !cfg2.preview) return;
    if (block.dataset.bsv4PreviewInit === "1") return;
    block.dataset.bsv4PreviewInit = "1";
    const box = block.querySelector(".bsv4-preview");
    const canvas = block.querySelector("[data-bsv4-canvas]");
    const tools = block.querySelector("[data-bsv4-bgr-host]");
    if (!box || !canvas) return;
    stageCanvas = canvas;
    if (!stageDocBound) {
      stageDocBound = true;
      document.addEventListener("bsv4:upload-started", (e) => {
        const d = e.detail || {};
        if (!d.localUrl || getState().mode !== "single" || !stageCanvas) return;
        const loader = stageCanvas.querySelector(".bsv4-preview__loading");
        if (!loader || loader.querySelector(".bsv4-preview__loading-art")) return;
        const img = document.createElement("img");
        img.className = "bsv4-preview__loading-art";
        img.alt = "";
        img.style.cssText = "position:absolute;inset:0;margin:auto;width:auto;height:auto;max-width:98cqw;max-height:98cqh;object-fit:contain";
        img.src = d.localUrl;
        loader.prepend(img);
      });
    }
    box.classList.toggle("bsv4-preview--no-bgr", !cfg2.preview.bgRemove);
    box.classList.toggle("bsv4-preview--no-upscale", !cfg2.preview.upscale);
    box.classList.toggle("bsv4-preview--no-aiedit", !cfg2.preview.aiEdit);
    const zoom = document.createElement("dialog");
    zoom.className = "bsv4-zoom";
    zoom.setAttribute("data-lenis-prevent", "");
    zoom.setAttribute("data-bsv4-zoom", "");
    zoom.setAttribute("aria-label", "Zoomed design preview");
    zoom.innerHTML = `<button class="bsv4-zoom__close">Close</button><img alt="">`;
    block.appendChild(zoom);
    zoom.addEventListener("click", () => zoom.close());
    canvas.addEventListener("click", (e) => {
      const file = activeFile(getState());
      if (!file) return;
      if (e.target.closest("[data-bsv4-file-delete]")) {
        if ((file.rows || []).length > 1) confirmRemove(() => deleteFile(file));
        else deleteFile(file);
      } else if (e.target.closest("[data-bsv4-zoom-open]") || e.target.matches(".bsv4-preview__art img")) {
        const img = zoom.querySelector("img");
        img.alt = file.name || "";
        swapImage(img, previewUrl(file), zoom);
        zoom.showModal();
      }
    });
    subscribe((state2) => {
      const file = activeFile(state2);
      if (tools && tools.dataset.bsv4BgrFor && (!file || tools.dataset.bsv4BgrFor !== file.id)) {
        tools.innerHTML = "";
        delete tools.dataset.bsv4BgrFor;
      }
      if (!file || !file.imgixUrl) {
        stagePending = null;
        if (state2.status === "uploading") {
          if (!canvas.querySelector(".bsv4-preview__loading")) {
            canvas.innerHTML = "";
            canvas.appendChild(buildStageLoader());
          }
        } else {
          canvas.innerHTML = "";
        }
        return;
      }
      const url = previewUrl(file);
      if (!canvas.querySelector(".bsv4-preview__art")) {
        if (stagePending !== url) {
          stagePending = url;
          let loader = canvas.querySelector(".bsv4-preview__loading");
          if (!loader) {
            canvas.innerHTML = "";
            loader = buildStageLoader();
            canvas.appendChild(loader);
          }
          loader.classList.remove("is-done");
          const pre = new Image();
          pre.onload = pre.onerror = () => {
            if (stagePending !== url) return;
            announce("preview-ready");
            loader.classList.add("is-done");
            setTimeout(() => {
              if (stagePending !== url) return;
              stagePending = null;
              buildArt(canvas);
              const ph = canvas.querySelector(".bsv4-preview__frame img");
              if (pre.naturalWidth) {
                pre.alt = file.name || "";
                ph.replaceWith(pre);
                const fr = canvas.querySelector(".bsv4-preview__frame");
                if (fr && pre.naturalHeight) {
                  fr.style.setProperty("--bsv4-ar", (pre.naturalWidth / pre.naturalHeight).toFixed(4));
                }
              } else {
                ph.alt = file.name || "";
                swapImage(ph, url, canvas.querySelector(".bsv4-preview__frame"));
              }
              const d = activeDims(file);
              setDim(canvas.querySelector("[data-bsv4-dim-w]"), d.w);
              setDim(canvas.querySelector("[data-bsv4-dim-h]"), d.h);
            }, 220);
          };
          pre.src = url;
        }
        return;
      }
      const img = canvas.querySelector("img");
      img.alt = file.name || "";
      swapImage(img, url, canvas.querySelector(".bsv4-preview__frame"));
      const dims = activeDims(file);
      setDim(canvas.querySelector("[data-bsv4-dim-w]"), dims.w);
      setDim(canvas.querySelector("[data-bsv4-dim-h]"), dims.h);
    });
    initMobileTabs(root2, box);
  }
  function buildArt(canvas) {
    canvas.innerHTML = `
<figure class="bsv4-preview__art">
<span class="bsv4-preview__actions">
<button type="button" data-bsv4-file-delete aria-label="Delete design" title="Delete design">
<svg viewBox="0 0 24 24" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="2"><path fill="currentColor" fill-rule="nonzero" d="m4.015 5.494h-.253c-.413 0-.747-.335-.747-.747s.334-.747.747-.747h5.253v-1c0-.535.474-1 1-1h4c.526 0 1 .465 1 1v1h5.254c.412 0 .746.335.746.747s-.334.747-.746.747h-.254v15.435c0 .591-.448 1.071-1 1.071-2.873 0-11.127 0-14 0-.552 0-1-.48-1-1.071zm14.5 0h-13v15.006h13zm-4.25 2.506c-.414 0-.75.336-.75.75v8.5c0 .414.336.75.75.75s.75-.336.75-.75v-8.5c0-.414-.336-.75-.75-.75zm-4.5 0c-.414 0-.75.336-.75.75v8.5c0 .414.336.75.75.75s.75-.336.75-.75v-8.5c0-.414-.336-.75-.75-.75zm3.75-4v-.5h-3v.5z"/></svg>
</button>
<button type="button" data-bsv4-zoom-open aria-label="Zoom design" title="Zoom">
<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/></svg>
</button>
</span>
<span class="bsv4-preview__frame">
<span class="bsv4-preview__dim bsv4-preview__dim--w" data-bsv4-dim-w hidden><i></i></span>
<span class="bsv4-preview__dim bsv4-preview__dim--h" data-bsv4-dim-h hidden><i></i></span>
<img alt="">
</span>
</figure>`;
  }
  function initMobileTabs(root2, box) {
    const guide = root2.querySelector('[data-bsv4-block="size_guide"]');
    const body = box.querySelector(".bsv4-preview__body");
    const tabs = box.querySelector("[data-bsv4-mobtabs]");
    if (!guide || !body || !tabs) return;
    const home = { parent: guide.parentNode, next: guide.nextSibling };
    const mq = window.matchMedia("(max-width: 999px)");
    const setView = (view) => {
      box.dataset.bsv4Mobview = view === "guide" ? "guide" : "customize";
      tabs.querySelectorAll("[data-bsv4-mobtab]").forEach((b) => {
        b.classList.toggle("is-active", b.dataset.bsv4Mobtab === box.dataset.bsv4Mobview);
      });
    };
    const place = () => {
      if (mq.matches) {
        if (guide.parentNode !== box) box.insertBefore(guide, body.nextSibling);
        if (!box.dataset.bsv4Mobview) setView("customize");
      } else if (guide.parentNode === box) {
        home.parent.insertBefore(guide, home.next);
      }
    };
    tabs.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-bsv4-mobtab]");
      if (btn) setView(btn.dataset.bsv4Mobtab);
    });
    mq.addEventListener("change", place);
    place();
  }
  function buildStageLoader() {
    const card = document.createElement("div");
    card.className = "bsv4-preview__loading";
    card.setAttribute("role", "status");
    card.innerHTML = '<span class="bsv4-preview__ring" aria-hidden="true"><svg viewBox="0 0 150 150"><circle cx="75" cy="75" r="68" fill="none" stroke="#fff" stroke-width="7"/><circle class="bsv4-preview__ringbar" cx="75" cy="75" r="68" fill="none" stroke="#019aff" stroke-width="7" stroke-linecap="round" stroke-dasharray="427.26"/></svg><img class="bsv4-preview__loading-ninja" alt="" width="200" height="200"></span><span class="bsv4-preview__pill bsv4-preview__lpill"><small><span class="bsv4-preview__lfull">We\u2019re making sure your upload is perfect for printing</span><span class="bsv4-preview__lshort">Making sure your upload prints perfectly</span></small><span class="bsv4-preview__lrow"><span class="bsv4-preview__ltxt"><b><span>0%</span> Uploading</b><i> - </i><em class="bsv4-preview__lstatus"></em></span><button type="button">cancel</button></span></span>';
    card.querySelector(".bsv4-preview__ringbar").style.strokeDashoffset = 414.44;
    card.querySelector("img").src = NINJA_GIF;
    card.querySelector("b span").setAttribute("data-bsv4-upprog", "");
    const stat = card.querySelector(".bsv4-preview__lstatus");
    let sIdx = 0;
    stat.textContent = STATUS[0];
    const rotor = setInterval(() => {
      if (!card.isConnected) return clearInterval(rotor);
      sIdx = (sIdx + 1) % STATUS.length;
      stat.textContent = STATUS[sIdx];
    }, 2400);
    card.querySelector("button").addEventListener("click", () => {
      if (getState().status === "uploading") window.resetLoading?.();
      else reset();
    });
    return card;
  }
  function setDim(el, val) {
    if (!el) return;
    el.hidden = val == null;
    if (val != null && el.firstElementChild) el.firstElementChild.textContent = `${cmFromIn(val)}cm`;
  }
  function activeFile(state2) {
    return state2.activeFileId && state2.files.find((f) => f.id === state2.activeFileId) || state2.files[0] || null;
  }
  function activeDims(file) {
    const tab = getState().sizeTab === "popular" ? "popular" : "custom";
    const rows = (file.rows || []).filter((r) => (r.mode || "custom") === tab && r.w > 0);
    const row = rows[rows.length - 1];
    const w = row ? row.w : file.widthIn != null ? file.widthIn : file.inW;
    const h = row ? row.h : file.heightIn != null ? file.heightIn : file.inH;
    return { w: fmtIn(w), h: fmtIn(h) };
  }
  function fmtIn(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toFixed(2) : null;
  }
  function normalizeExt(file) {
    const raw = (file.ext || file.type || (file.name || "").split(".").pop() || "").toLowerCase();
    return raw === "jpeg" || raw === "jfif" ? "jpg" : raw === "tiff" ? "tif" : raw === "heif" ? "heic" : raw;
  }
  function paramsFor(file, previewCfg) {
    if (file.params && Object.keys(file.params).length) return file.params;
    return previewCfg && previewCfg.imgix && previewCfg.imgix[normalizeExt(file)] || {};
  }
  function buildPreviewUrl(file, params) {
    if (file.bgRemove && file.bgrModel) {
      const pers = file.bgrVersions && file.bgrVersions[file.bgrModel];
      const disp = file.bgrDisplay && file.bgrDisplay[file.bgrModel];
      if (file.superRes) {
        const b = disp && disp[0] === "h" ? disp : pers;
        if (b && !/[?&]bg-remove=/.test(b)) {
          const base = pers && pers[0] === "h" ? pers : b.split("?")[0];
          return `${base}${base.includes("?") ? "&" : "?"}trim=colorUnlessAlpha&trim-tol=10&upscale=true`;
        }
        if (b) return `${b}${b.includes("?") ? "&" : "?"}upscale=true`;
      }
      if (disp || pers) return disp || pers;
    }
    const url = file.imgixUrl;
    if (!url || /^(data|blob):/.test(url)) return url;
    if (file.bgRemove) {
      const c = candidateUrl(url, BGR_MODELS[0]);
      return file.superRes ? `${c}&upscale=true` : c;
    }
    const parts = [];
    if (params.preview) parts.push(params.preview);
    if (file.superRes) parts.push("upscale=true");
    return parts.length ? `${url}?${parts.join("&")}` : url;
  }
  function previewUrl(file) {
    const paint = file.ntPaint;
    if (paint && paint.displayUrl) return paint.displayUrl;
    return buildPreviewUrl(file, paramsFor(file, cfg2 && cfg2.preview));
  }
  function originalUrl(file) {
    const url = file.imgixUrl;
    if (!url || /^(data|blob):/.test(url)) return url || "";
    const p = paramsFor(file, cfg2 && cfg2.preview);
    return p.preview ? `${url}?${p.preview}` : url;
  }
  var swapState = /* @__PURE__ */ new WeakMap();
  function swapImage(img, url, host2) {
    if (!img || !url) return;
    let st = swapState.get(img);
    if (!st) swapState.set(img, st = { pending: null, wait: null });
    st.painted = img.getAttribute("src") || null;
    const settle = () => {
      if (st.wait) {
        st.wait.remove();
        st.wait = null;
      }
    };
    if (url === st.painted) {
      st.pending = null;
      settle();
      return;
    }
    if (url === st.pending) return;
    st.pending = url;
    if (!st.painted) img.style.visibility = "hidden";
    if (host2 && !st.wait) {
      st.wait = document.createElement("span");
      st.wait.className = "bsv4-preview__imgwait";
      st.wait.setAttribute("aria-hidden", "true");
      st.wait.innerHTML = '<span class="bsv4-preview__loading-spin"></span>';
      host2.appendChild(st.wait);
    }
    const pre = new Image();
    pre.onload = () => {
      if (st.pending !== url) return;
      img.src = url;
      img.style.visibility = "";
      if (host2 && pre.naturalHeight) {
        host2.style.setProperty("--bsv4-ar", (pre.naturalWidth / pre.naturalHeight).toFixed(4));
      }
      st.painted = url;
      st.pending = null;
      settle();
    };
    pre.onerror = () => {
      if (st.pending !== url) return;
      st.tries = st.tryUrl === url ? st.tries + 1 : (st.tryUrl = url, 1);
      if (st.tries <= 4) {
        setTimeout(() => {
          if (st.pending !== url) return;
          pre.src = "";
          pre.src = url;
        }, 1e3 * 2 ** st.tries);
        return;
      }
      st.pending = null;
      settle();
    };
    pre.src = url;
  }
  var confirmDlg = null;
  var confirmYes = null;
  function confirmRemove(onYes) {
    if (!confirmDlg) {
      confirmDlg = document.createElement("dialog");
      confirmDlg.className = "bsv4-confirm";
      confirmDlg.setAttribute("aria-label", "Remove file confirmation");
      confirmDlg.innerHTML = `<button class="bsv4-confirm__x" data-bsv4-confirm-no aria-label="Close">&times;</button><h3 class="bsv4-confirm__title">Remove File?</h3><p class="bsv4-confirm__body">Are you sure you would like to remove this file?</p><div class="bsv4-confirm__actions"><button class="bsv4-btn bsv4-btn--primary" data-bsv4-confirm-yes>Remove File</button><button class="bsv4-btn bsv4-confirm__cancel" data-bsv4-confirm-no>Cancel</button></div>`;
      document.querySelector("[data-bsv4-root]").appendChild(confirmDlg);
      confirmDlg.addEventListener("click", (e) => {
        if (e.target.closest("[data-bsv4-confirm-yes]")) {
          confirmDlg.close();
          if (confirmYes) confirmYes();
        } else if (e.target === confirmDlg || e.target.closest("[data-bsv4-confirm-no]")) {
          confirmDlg.close();
        }
      });
    }
    confirmYes = onYes;
    confirmDlg.showModal();
  }
  function deleteFile(file) {
    const files = getState().files.filter((f) => f.id !== file.id);
    if (!files.length) {
      reset();
    } else {
      update({ files });
    }
    announce("rows-changed", {});
    announce("file-removed", { id: file.id, name: file.name });
  }

  // src/sizing.js
  var cfg3;
  var sectionRoot;
  var instances = [];
  var docBound = false;
  var rowSeq = 0;
  var debounces = /* @__PURE__ */ new Map();
  var pendingInputs = /* @__PURE__ */ new Set();
  function resolveLimits(instanceCfg, globalLimits) {
    const g = globalLimits || {};
    const num = (v) => {
      const n = typeof v === "string" && v.trim() !== "" ? parseFloat(v) : v;
      return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
    };
    const minIn = num(g.minIn) ?? 0.25;
    const maxIn = num(g.maxIn) ?? 22;
    const i = instanceCfg || {};
    return {
      minW: num(i.minW) ?? minIn,
      maxW: num(i.maxW) ?? maxIn,
      minH: num(i.minH) ?? minIn,
      maxH: num(i.maxH) ?? maxIn,
      minQty: num(i.minQty) ?? num(g.minQty) ?? 1
    };
  }
  function normSizeKey(s) {
    return String(s || "").toLowerCase().replace(/["\s]/g, "");
  }
  function parsePopularList(text) {
    const out = [];
    for (const line of String(text || "").split("\n")) {
      const i = line.indexOf(":");
      const keyRaw = i >= 0 ? line.slice(0, i) : line;
      const desc = i >= 0 ? line.slice(i + 1).trim() : "";
      const m = normSizeKey(keyRaw).match(/^(\d+(?:\.\d+)?)(?:cm)?x(\d+(?:\.\d+)?)(?:cm)?$/);
      if (!m) continue;
      out.push({ value: `${m[1]}cm x ${m[2]}cm`, w: parseFloat(m[1]), h: parseFloat(m[2]), desc });
    }
    return out;
  }
  function popularCapFor(popular, w, h) {
    if (!Array.isArray(popular)) return null;
    return popular.find((c) => c.w === w && c.h === h) || null;
  }
  function effectiveMax(lim, cap) {
    return {
      maxW: Math.min(lim.maxW, cap && Number.isFinite(cap.maxW) ? cap.maxW : Infinity),
      maxH: Math.min(lim.maxH, cap && Number.isFinite(cap.maxH) ? cap.maxH : Infinity)
    };
  }
  function computeLockedDims(value, axis, aspectW, aspectH, lim) {
    let val = parseFloat(value);
    if (!Number.isFinite(val) || val <= 0) return null;
    let err = null;
    const minEdit = axis === "w" ? lim.minW : lim.minH;
    const maxEdit = axis === "w" ? lim.maxW : lim.maxH;
    const overMax = (v, cap) => cmFromIn(v) > cmFromIn(cap);
    const underMin = (v, cap) => cmFromIn(v) < cmFromIn(cap);
    if (minEdit && underMin(val, minEdit)) {
      val = minEdit;
      err = `${cmFromIn(minEdit)}cm minimum`;
    }
    if (overMax(val, maxEdit)) {
      val = maxEdit;
      err = `${cmFromIn(maxEdit)}cm max`;
    }
    const other = lockedCounterpart(val, axis, aspectW, aspectH);
    let w = axis === "w" ? val : other;
    let h = axis === "w" ? other : val;
    const minOther = axis === "w" ? lim.minH : lim.minW;
    const maxOther = axis === "w" ? lim.maxH : lim.maxW;
    const counter = axis === "w" ? h : w;
    if (overMax(counter, maxOther)) {
      const fit = clampToMax(w, h, lim.maxW, lim.maxH);
      w = fit.w;
      h = fit.h;
      err = `${cmFromIn(maxOther)}cm max`;
    } else if (minOther && underMin(counter, minOther)) {
      const scale = minOther / counter;
      w = parseFloat((w * scale).toFixed(2));
      h = parseFloat((h * scale).toFixed(2));
      err = `${cmFromIn(minOther)}cm minimum`;
      const fit = clampToMax(w, h, lim.maxW, lim.maxH);
      if (fit && fit.clamped) {
        w = fit.w;
        h = fit.h;
      }
    }
    return { w, h, err };
  }
  function fileDims(file) {
    return {
      w: parseFloat(file.inW ?? file.widthIn) || 0,
      h: parseFloat(file.inH ?? file.heightIn) || 0
    };
  }
  function fileAspect(file) {
    if (file.pxW && file.pxH) return { w: file.pxW, h: file.pxH };
    const d = fileDims(file);
    return { w: d.w || 1, h: d.h || 1 };
  }
  function fileExt(file) {
    return (file.ext || file.type || "").toLowerCase();
  }
  function initSizing(root2, config) {
    cfg3 = config;
    sectionRoot = root2;
    instances = [];
    const entries = Array.isArray(cfg3.sizing) ? cfg3.sizing : [];
    if (!entries.length) return;
    root2.querySelectorAll('[data-bsv4-block="size_inputs"]').forEach((el, i) => {
      const settings = entries[Math.min(i, entries.length - 1)];
      const inst = {
        el,
        settings,
        lim: resolveLimits(settings, cfg3.limits),
        rows: el.querySelector("[data-bsv4-rows]"),
        template: el.querySelector("[data-bsv4-row-template]"),
        tab: "custom"
      };
      if (!inst.rows || !inst.template) return;
      instances.push(inst);
    });
    if (!instances.length) return;
    if (instances[0].settings && instances[0].settings.popularOnly) {
      if (getState().sizeTab !== "popular") update({ sizeTab: "popular" });
    }
    if (root2.dataset.bsv4SizingBound !== "1") {
      root2.dataset.bsv4SizingBound = "1";
      root2.addEventListener("input", onInput);
      root2.addEventListener("change", onChange);
      root2.addEventListener("click", onClick);
      root2.addEventListener("focusout", onFieldSettle);
      root2.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && e.target.matches && e.target.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) {
          e.preventDefault();
          e.target.blur();
        }
      });
      const selectAll = (e) => {
        if (e.target.matches && e.target.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) {
          e.target.select();
        }
      };
      root2.addEventListener("focusin", selectAll);
      root2.addEventListener("click", selectAll);
      window.addEventListener(
        "scroll",
        (e) => {
          if (!sectionRoot || sectionRoot.dataset.bsv4CddOpen !== "1") return;
          if (e.target instanceof Element && e.target.closest("[data-bsv4-color-menu]")) return;
          const menu = sectionRoot.querySelector("[data-bsv4-color-menu]:not([hidden])");
          if (!menu) {
            sectionRoot.dataset.bsv4CddOpen = "";
            return;
          }
          const btn = menu.parentElement.querySelector("[data-bsv4-color-open]");
          const br = btn.getBoundingClientRect();
          if (br.bottom < 0 || br.top > window.innerHeight) {
            menu.hidden = true;
            sectionRoot.dataset.bsv4CddOpen = "";
            return;
          }
          placeColorMenu(menu, btn);
        },
        true
      );
      window.addEventListener("resize", () => closeColorMenus());
    }
    if (!docBound) {
      docBound = true;
      document.addEventListener("bsv4:file-uploaded", onFileUploaded);
      document.addEventListener("bsv4:price-changed", () => syncAll());
      subscribe(() => syncAll());
    }
    syncAll();
  }
  function onFileUploaded(e) {
    const file = getState().files.find((f) => f.id === e.detail.id);
    if (!file) return;
    const popularOnly = instances[0] && instances[0].settings && instances[0].settings.popularOnly;
    if (!popularOnly && getState().sizeTab !== "custom") update({ sizeTab: "custom" });
    if (popularOnly && getState().sizeTab !== "popular") update({ sizeTab: "popular" });
    const tileRows = sectionRoot.querySelector(
      `[data-bsv4-tile="${file.id}"] [data-bsv4-tile-rows]`
    );
    const d = fileDims(file);
    if (popularOnly) {
      const row = addRow(file, { w: d.w, h: d.h }, tileRows || void 0, void 0, "popular");
      const inst0 = instances[0];
      const cmsFirst = parsePopularList(inst0 && inst0.settings && inst0.settings.popularDescs)[0];
      const firstVariant = (cfg3.variants || []).find((v) => v.isPopular);
      const firstKey = cmsFirst ? cmsFirst.value : firstVariant && firstVariant.option1;
      if (row && firstKey) {
        const box = parsePopularDims(firstKey);
        row.popularKey = firstKey;
        if (box) {
          const a = fileAspect(file);
          const fit = containFit(a.w, a.h, box.w, box.h);
          row.w = fit.w;
          row.h = fit.h;
        }
        commit();
      }
      return;
    }
    addRow(file, { w: d.w, h: d.h }, tileRows || void 0);
  }
  function instanceFor(el) {
    return el && instances.find((inst) => inst.el.contains(el)) || null;
  }
  function addRow(file, dims, container, via, modeOverride) {
    const inst = via || instanceFor(container) || instances[0];
    if (!inst) return null;
    const mode = modeOverride === "popular" || modeOverride === "custom" ? modeOverride : getState().sizeTab === "popular" ? "popular" : "custom";
    let row;
    if (mode === "popular") {
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode,
        w: 0,
        h: 0,
        qty: inst.lim.minQty,
        variantId: null
      };
    } else {
      const max = effectiveMax(inst.lim, null);
      const clamped = clampToMax(dims.w, dims.h, max.maxW, max.maxH) || dims;
      const variant = matchCustomVariant(cfg3.variants || [], clamped.w, clamped.h);
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode,
        w: clamped.w,
        h: clamped.h,
        qty: inst.lim.minQty,
        variantId: variant ? variant.id : null
      };
    }
    file.rows.push(row);
    if (container) renderRow(file, row, container);
    commit();
    return row;
  }
  var POPULAR_DESC_UV = {
    '1" x 1"': "Small Logo / Lid Dot",
    '2" x 2"': "Cup / Bottle Logo",
    '3" x 3"': "Tumbler Logo",
    '4" x 4"': "Tumbler / Bottle Front",
    '5" x 5"': "Laptop / Cooler Decal",
    '6" x 6"': "Box / Case Branding",
    '7" x 7"': "Equipment Label",
    '8" x 8"': "Window / Wall Decal",
    '9" x 9"': "Large Equipment Decal",
    '10" x 10"': "Signage / Cornhole",
    '11" x 11"': "Large Signage Decal",
    '22" x 24"': "Max Sheet / Multi-Decal"
  };
  var POPULAR_DESC = {
    "6cm x 6cm": "Tag Print / Neck Label",
    "8cm x 8cm": "Small Tag/Sleeve Icon",
    "11cm x 6cm": "Name Label/Small Chest Text",
    "11cm x 11cm": "Left-Chest Logo",
    "13cm x 8cm": "Small Badge Logo",
    "13cm x 13cm": "Small Chest/Youth Print",
    "16cm x 16cm": "Std Left-Chest/Youth",
    "18cm x 18cm": "Small Front (Adult)",
    "21cm x 21cm": "Medium Front Print",
    "23cm x 23cm": "Large Chest/Med Front",
    "23cm x 28cm": "Vertical Front/Youth",
    "26cm x 26cm": "Standard Adult Front",
    "28cm x 13cm": "Chest Banner/Sleeve",
    "28cm x 28cm": "Adult Full Front",
    "28cm x 36cm": "Tall Front/Jersey",
    "31cm x 44cm": "Oversized Front/Hoodie"
  };
  function renderRow(file, row, container) {
    const inst = instanceFor(container) || instances[0];
    if (!inst) return null;
    const node = inst.template.content.firstElementChild.cloneNode(true);
    node.dataset.bsv4RowId = row.id;
    const select = node.querySelector("[data-bsv4-popular]");
    if (select) {
      select.innerHTML = "";
      const ph = document.createElement("option");
      ph.value = "";
      ph.textContent = "Select a popular size";
      ph.disabled = true;
      ph.selected = true;
      select.appendChild(ph);
      const fam = getState().family;
      const famMap = fam === "uv" ? POPULAR_DESC_UV : fam === "dtf" ? POPULAR_DESC : {};
      const cmsList = parsePopularList(inst.settings && inst.settings.popularDescs);
      if (cmsList.length) {
        for (const en of cmsList) {
          const capDims = parsePopularDims(en.value);
          if (capDims && inst.lim && (capDims.w > inst.lim.maxW + 0.02 || capDims.h > inst.lim.maxH + 0.02)) continue;
          const opt = document.createElement("option");
          opt.value = en.value;
          const desc = en.desc || famMap[en.value];
          opt.textContent = desc ? `${en.value} - ${desc}` : en.value;
          opt.dataset.orig = opt.textContent;
          select.appendChild(opt);
        }
      } else {
        const seenSizes = /* @__PURE__ */ new Set();
        for (const v of cfg3.variants || []) {
          if (!v.isPopular || !variantMatchesPrintMethod(v)) continue;
          if (seenSizes.has(v.option1)) continue;
          seenSizes.add(v.option1);
          const capDims = parsePopularDims(v.option1);
          if (capDims && inst.lim && (capDims.w > inst.lim.maxW + 0.02 || capDims.h > inst.lim.maxH + 0.02)) continue;
          const opt = document.createElement("option");
          opt.value = v.option1;
          const desc = famMap[v.option1];
          opt.textContent = desc ? `${v.option1} - ${desc}` : v.option1;
          opt.dataset.orig = opt.textContent;
          select.appendChild(opt);
        }
      }
    }
    applyTabToNode(node, row.mode || "custom");
    syncRowDom(node, file, row);
    container.appendChild(node);
    return node;
  }
  function syncAll() {
    if (!instances.length) return;
    const state2 = getState();
    const tab = state2.sizeTab === "popular" ? "popular" : "custom";
    for (const inst of instances) {
      inst.el.querySelectorAll("[data-bsv4-size-tab]").forEach((t) => {
        t.classList.toggle("is-active", t.dataset.bsv4SizeTab === tab);
      });
      const intro = inst.el.querySelector(".bsv4-sizing__intro");
      if (intro) applyTabToNode(intro, tab);
    }
    const valid = /* @__PURE__ */ new Set();
    for (const file of state2.files) {
      for (const row of file.rows) {
        if (!row.id) row.id = `r-${++rowSeq}`;
        valid.add(row.id);
      }
    }
    sectionRoot.querySelectorAll("[data-bsv4-row-id]").forEach((n) => {
      if (!valid.has(n.dataset.bsv4RowId)) n.remove();
    });
    const tiled = /* @__PURE__ */ new Set();
    if (state2.mode === "multi" && state2.files.length) {
      sectionRoot.querySelectorAll("[data-bsv4-tile] [data-bsv4-row-id]").forEach((n) => {
        tiled.add(n.dataset.bsv4RowId);
      });
    } else {
      sectionRoot.querySelectorAll("[data-bsv4-tile] [data-bsv4-row-id]").forEach((n) => n.remove());
    }
    for (const inst of instances) {
      const keep = /* @__PURE__ */ new Set();
      let cursor = null;
      for (const file of state2.files) {
        for (const row of file.rows) {
          if (tiled.has(row.id)) continue;
          let node = inst.rows.querySelector(`[data-bsv4-row-id="${row.id}"]`);
          if (node) syncRowDom(node, file, row);
          else node = renderRow(file, row, inst.rows);
          keep.add(node);
          const expected = cursor ? cursor.nextElementSibling : inst.rows.firstElementChild;
          if (node !== expected) inst.rows.insertBefore(node, expected);
          cursor = node;
        }
      }
      inst.rows.querySelectorAll("[data-bsv4-row-id]").forEach((n) => {
        if (!keep.has(n)) n.remove();
      });
    }
    if (state2.mode === "multi") {
      for (const file of state2.files) {
        const host2 = sectionRoot.querySelector(
          `[data-bsv4-tile="${file.id}"] [data-bsv4-tile-rows]`
        );
        if (!host2) continue;
        const firstRow = host2.querySelector("[data-bsv4-row-id]");
        if (!firstRow) continue;
        let cursor = firstRow.previousElementSibling;
        for (const row of file.rows) {
          const node = host2.querySelector(`[data-bsv4-row-id="${row.id}"]`);
          if (!node) continue;
          const expected = cursor ? cursor.nextElementSibling : host2.firstElementChild;
          if (node !== expected) host2.insertBefore(node, expected);
          cursor = node;
        }
      }
    }
    sectionRoot.querySelectorAll("[data-bsv4-row-id]").forEach((n) => {
      if (state2.mode === "multi" && n.closest("[data-bsv4-tile]")) return;
      const id = n.dataset.bsv4RowId;
      let mode = "custom";
      for (const file of state2.files) {
        const row = (file.rows || []).find((r) => r.id === id);
        if (row) {
          mode = row.mode || "custom";
          break;
        }
      }
      const off = mode !== tab;
      n.classList.toggle("is-disabled", off);
      n.querySelectorAll("input, select, button").forEach((el) => {
        el.disabled = off;
      });
    });
    sectionRoot.querySelectorAll("[data-bsv4-tile] [data-bsv4-row-id]").forEach((n) => {
      const id = n.dataset.bsv4RowId;
      for (const file of state2.files) {
        const row = (file.rows || []).find((r) => r.id === id);
        if (row) {
          syncRowDom(n, file, row);
          projectPrice(n, file, row);
          break;
        }
      }
    });
  }
  function syncRowDom(node, file, row) {
    const wEl = node.querySelector("[data-bsv4-w]");
    const hEl = node.querySelector("[data-bsv4-h]");
    const qEl = node.querySelector("[data-bsv4-qty]");
    const fmt2 = (n) => {
      const x = parseFloat(n);
      return Number.isFinite(x) && x > 0 ? String(cmFromIn(x)) : n;
    };
    if (wEl && !pendingInputs.has(wEl)) wEl.value = fmt2(row.w);
    if (hEl && !pendingInputs.has(hEl)) hEl.value = fmt2(row.h);
    if (qEl && !pendingInputs.has(qEl)) qEl.value = row.qty;
    const colorSel = node.querySelector("[data-bsv4-color]");
    if (colorSel) {
      colorSel.value = row.color || "";
      const lbl = node.querySelector("[data-bsv4-color-label]");
      if (lbl) lbl.textContent = row.color || lbl.dataset.ph || (lbl.dataset.ph = lbl.textContent);
      const dot = node.querySelector("[data-bsv4-colordot]");
      if (dot) {
        const key = row.color || "";
        if (dot.dataset.bsv4DotFor !== key) {
          dot.dataset.bsv4DotFor = key;
          while (dot.firstChild) dot.removeChild(dot.firstChild);
          if (key) dot.appendChild(swatchChip(key));
        }
      }
    }
    const select = node.querySelector("[data-bsv4-popular]");
    if (select) {
      select.value = row.mode === "popular" && row.popularKey ? row.popularKey : "";
      for (const opt of select.options) {
        if (!opt.dataset.orig) continue;
        let label = opt.dataset.orig;
        if (cfg3.smartSizing && opt.value === select.value && row.mode === "popular" && row.w > 0 && row.h > 0) {
          const parts = opt.dataset.orig.split("-");
          const desc = parts.length > 1 ? ` -${parts.slice(1).join("-")}` : "";
          label = `${cmFromIn(row.w)}cm x ${cmFromIn(row.h)}cm${desc}`;
        }
        if (opt.textContent !== label) opt.textContent = label;
      }
    }
    const smart = node.querySelector("[data-bsv4-smart]");
    if (smart) {
      smart.hidden = !(cfg3.smartSizing && row.mode === "popular" && row.popularKey);
    }
    const removeBtn = node.querySelector("[data-bsv4-row-remove]");
    if (removeBtn) removeBtn.classList.toggle("is-lone-hidden", (file.rows || []).length <= 1);
    const unsetPopular = row.mode === "popular" && !row.popularKey;
    const ext = fileExt(file);
    const raster = ext === "png" || ext === "jpg" || ext === "jpeg";
    const eff = unsetPopular || !raster ? Infinity : effectiveDpi(file.pxW, file.pxH, row.w, row.h);
    const warn = (file.superRes ? eff * 4 : eff) < DPI_WARN_MIN;
    row.dpiWarn = warn;
    const dpiEl = node.querySelector("[data-bsv4-dpi]");
    if (dpiEl) {
      const upClears = warn && !file.superRes && !!(file.params || {}).superRes && eff * 4 >= DPI_WARN_MIN;
      const dpiState = warn ? upClears ? "link" : "plain" : "";
      if (dpiEl._bsv4Dpi !== dpiState) {
        dpiEl._bsv4Dpi = dpiState;
        dpiEl.innerHTML = warn ? '<svg width="13" height="13" viewBox="0 0 16 16" style="vertical-align:-1px"><circle cx="8" cy="8" r="8" fill="#fa0"/><path d="M8 3.5v5M8 12v.1" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg> Artwork may lose clarity at this size. ' : "";
        if (upClears) {
          const up = document.createElement("button");
          up.type = "button";
          up.className = "bsv4-dpi-up";
          up.textContent = "Upscale for Free";
          dpiEl.append(up);
        } else if (warn) {
          dpiEl.append("Choose a smaller size.");
        }
      }
      dpiEl.hidden = !warn;
    }
    projectPrice(node, file, row);
  }
  function projectPrice(node, file, row) {
    const priceEl = node.querySelector("[data-bsv4-row-price]");
    const totalEl = node.querySelector("[data-bsv4-row-total]");
    const discEl = node.querySelector("[data-bsv4-row-disc]");
    if (row.mode === "popular" && !row.popularKey) {
      if (priceEl) priceEl.innerHTML = "&mdash;";
      if (totalEl) totalEl.innerHTML = "&mdash;";
      if (discEl) discEl.textContent = "";
      return;
    }
    const pricing = getState().pricing;
    const rows = pricing && pricing.rows || [];
    let entry = rows.find((r) => r.rowId === row.id);
    if (!entry) {
      const mine = rows.filter((r) => r.fileId === file.id);
      const byIndex = mine[file.rows.indexOf(row)];
      if (byIndex && byIndex.rowId == null) entry = byIndex;
    }
    if (!entry) return;
    const struck = entry.off > 0 && entry.orig > entry.unit;
    if (priceEl && Number.isFinite(entry.unit)) {
      priceEl.textContent = "";
      if (struck && Number.isFinite(entry.orig)) {
        const was = document.createElement("s");
        was.className = "bsv4-row__was";
        was.textContent = `\xA3${entry.orig.toFixed(2)}`;
        priceEl.appendChild(was);
      }
      priceEl.appendChild(document.createTextNode(`\xA3${entry.unit.toFixed(2)}`));
      const ea = document.createElement("small");
      ea.textContent = " ea";
      priceEl.appendChild(ea);
    }
    if (totalEl && Number.isFinite(entry.total)) {
      totalEl.textContent = "";
      if (struck && Number.isFinite(entry.origTotal)) {
        const was = document.createElement("s");
        was.className = "bsv4-row__was";
        was.textContent = `\xA3${entry.origTotal.toFixed(2)}`;
        totalEl.appendChild(was);
      }
      totalEl.appendChild(document.createTextNode(`\xA3${entry.total.toFixed(2)}`));
    }
    if (discEl) discEl.textContent = entry.off > 0 ? `${entry.off}% off` : "";
  }
  function applyTabToNode(node, tab) {
    node.querySelectorAll("[data-bsv4-mode-custom]").forEach((el) => {
      el.hidden = tab !== "custom";
    });
    node.querySelectorAll("[data-bsv4-mode-popular]").forEach((el) => {
      el.hidden = tab !== "popular";
    });
  }
  function ensureModeRow(file, tab) {
    if ((file.rows || []).some((r) => (r.mode || "custom") === tab)) return null;
    const inst = instances[0];
    if (!inst) return null;
    let row;
    if (tab === "popular") {
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode: "popular",
        w: 0,
        h: 0,
        qty: inst.lim.minQty,
        variantId: null
      };
    } else {
      const d = fileDims(file);
      const max = effectiveMax(inst.lim, null);
      const clamped = clampToMax(d.w, d.h, max.maxW, max.maxH) || d;
      const variant = matchCustomVariant(cfg3.variants || [], clamped.w, clamped.h);
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode: "custom",
        w: clamped.w,
        h: clamped.h,
        qty: inst.lim.minQty,
        variantId: variant ? variant.id : null
      };
    }
    file.rows.push(row);
    return row;
  }
  function setSizeTab(tab) {
    const primary = instances[0];
    if (tab === "popular" && !(primary && primary.settings && primary.settings.popularEnabled)) return;
    if (getState().sizeTab === tab) return;
    const state2 = getState();
    for (const file of state2.files) ensureModeRow(file, tab);
    update({ files: state2.files, sizeTab: tab });
    announce("rows-changed", {});
  }
  function findRow(target) {
    const node = target.closest("[data-bsv4-row-id]");
    if (!node) return {};
    const id = node.dataset.bsv4RowId;
    for (const file of getState().files) {
      const row = file.rows.find((r) => r.id === id);
      if (row) return { node, file, row };
    }
    return { node };
  }
  function onInput(e) {
    const { node, file, row } = findRow(e.target);
    if (!row) return;
    if (e.target.matches("[data-bsv4-w], [data-bsv4-h]")) {
      const axis = e.target.matches("[data-bsv4-w]") ? "w" : "h";
      pendingInputs.add(e.target);
      debounce(`${row.id}-${axis}`, 300, () => {
        if (document.activeElement !== e.target) pendingInputs.delete(e.target);
        applyDimensionEdit(node, file, row, e.target);
      });
    } else if (e.target.matches("[data-bsv4-qty]")) {
      pendingInputs.add(e.target);
      debounce(`${row.id}-q`, 300, () => {
        if (document.activeElement !== e.target) pendingInputs.delete(e.target);
        const inst = instanceFor(node) || instances[0];
        row.qty = Math.max(inst.lim.minQty, parseInt(e.target.value, 10) || inst.lim.minQty);
        if (!pendingInputs.has(e.target)) e.target.value = row.qty;
        commit();
      });
    }
  }
  function onFieldSettle(e) {
    const t = e.target;
    if (!t.matches || !t.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) return;
    if (!pendingInputs.has(t)) return;
    pendingInputs.delete(t);
    const { node, file, row } = findRow(t);
    if (!row) return;
    if (t.matches("[data-bsv4-qty]")) {
      cancelDebounce(`${row.id}-q`);
      const inst = instanceFor(node) || instances[0];
      row.qty = Math.max(inst.lim.minQty, parseInt(t.value, 10) || inst.lim.minQty);
      t.value = row.qty;
      commit();
    } else {
      const axis = t.matches("[data-bsv4-w]") ? "w" : "h";
      cancelDebounce(`${row.id}-${axis}`);
      applyDimensionEdit(node, file, row, t);
    }
  }
  function applyDimensionEdit(node, file, row, editedInput) {
    const inst = instanceFor(node) || instances[0];
    const axis = editedInput.matches("[data-bsv4-w]") ? "w" : "h";
    const errEl = node.querySelector("[data-bsv4-row-err]");
    if (errEl) errEl.hidden = true;
    const cap = Number.isFinite(row.capW) || Number.isFinite(row.capH) ? { maxW: row.capW, maxH: row.capH } : null;
    const max = effectiveMax(inst.lim, cap);
    const a = fileAspect(file);
    const out = computeLockedDims(inFromCm(editedInput.value), axis, a.w, a.h, {
      minW: inst.lim.minW,
      maxW: max.maxW,
      minH: inst.lim.minH,
      maxH: max.maxH
    });
    if (!out) {
      syncRowDom(node, file, row);
      return;
    }
    if (out.err && errEl) flagErr(errEl, out.err, pendingInputs.has(editedInput));
    row.w = out.w;
    row.h = out.h;
    const variant = matchCustomVariant(cfg3.variants || [], out.w, out.h);
    row.variantId = variant ? variant.id : null;
    syncRowDom(node, file, row);
    commit();
  }
  function onChange(e) {
    if (e.target.matches("[data-bsv4-color]")) {
      const { node: node2, file: file2, row: row2 } = findRow(e.target);
      if (!row2) return;
      const inst2 = instanceFor(node2) || instances[0];
      const rowOpt2 = inst2.settings && inst2.settings.rowOption;
      row2.color = e.target.value || null;
      if (row2.mode === "popular" && row2.popularKey) {
        const v = (cfg3.variants || []).find(
          (x) => x.isPopular && x.option1 === row2.popularKey && (!rowOpt2 || x.option2 === row2.color)
        );
        row2.variantId = v ? v.id : null;
      }
      syncRowDom(node2, file2, row2);
      commit();
      return;
    }
    if (!e.target.matches("[data-bsv4-popular]")) return;
    const { node, file, row } = findRow(e.target);
    if (!row) return;
    const inst = instanceFor(node) || instances[0];
    const value = e.target.value;
    if (!value) return;
    const box = parsePopularDims(value);
    if (!box) return;
    row.mode = "popular";
    row.popularKey = value;
    const cap = popularCapFor(inst.settings && inst.settings.popular, box.w, box.h);
    if (cap) {
      row.capW = cap.maxW;
      row.capH = cap.maxH;
    } else {
      delete row.capW;
      delete row.capH;
    }
    const a = fileAspect(file);
    const fit = containFit(a.w, a.h, box.w, box.h);
    const max = effectiveMax(inst.lim, cap);
    const clamped = clampToMax(fit.w, fit.h, max.maxW, max.maxH) || fit;
    row.w = clamped.w;
    row.h = clamped.h;
    const rowOpt = inst.settings && inst.settings.rowOption;
    const hasBands = (cfg3.variants || []).some((v) => v.isCustom);
    const pricingVariant = hasBands ? matchCustomVariant(cfg3.variants || [], row.w, row.h) : rowOpt ? row.color ? (cfg3.variants || []).find((v) => v.isPopular && v.option1 === value && v.option2 === row.color) : null : (cfg3.variants || []).find((v) => v.isPopular && variantMatchesPrintMethod(v) && v.option1 === value);
    row.variantId = pricingVariant ? pricingVariant.id : null;
    syncRowDom(node, file, row);
    commit();
  }
  function closeColorMenus(except) {
    let anyOpen = false;
    sectionRoot.querySelectorAll("[data-bsv4-color-menu]:not([hidden])").forEach((m) => {
      if (m !== except) m.hidden = true;
      else anyOpen = true;
    });
    if (sectionRoot) sectionRoot.dataset.bsv4CddOpen = anyOpen ? "1" : "";
  }
  function placeColorMenu(menu, btn) {
    const br = btn.getBoundingClientRect();
    menu.style.left = `${Math.round(br.left)}px`;
    menu.style.top = `${Math.round(br.bottom + 4)}px`;
    menu.style.minWidth = `${Math.round(br.width)}px`;
  }
  function swatchChip(key) {
    const chip = document.createElement("span");
    chip.className = "bsv4-cdd__chip";
    const src = sectionRoot.querySelector(
      `[data-bsv4-swatch-for="${typeof CSS !== "undefined" && CSS.escape ? CSS.escape(key) : key}"]`
    );
    if (src) {
      const img = src.querySelector("img");
      if (img && img.getAttribute("src")) {
        chip.style.backgroundImage = `url("${img.getAttribute("src")}")`;
        chip.style.backgroundSize = "cover";
      } else {
        let bg = "";
        src.querySelectorAll("*").forEach((n) => {
          if (bg) return;
          const b = getComputedStyle(n).backgroundColor;
          if (b && b !== "rgba(0, 0, 0, 0)" && b !== "transparent") bg = b;
        });
        chip.style.background = bg || "#fff";
      }
    }
    return chip;
  }
  function onColorMenuClick(e) {
    const openBtn = e.target.closest("[data-bsv4-color-open]");
    if (openBtn) {
      const wrap = openBtn.parentElement;
      const menu = wrap.querySelector("[data-bsv4-color-menu]");
      if (!menu) return true;
      if (menu.hidden && !menu.dataset.bsv4SwFilled) {
        menu.dataset.bsv4SwFilled = "1";
        menu.querySelectorAll("[data-bsv4-color-pick]").forEach((opt) => {
          opt.querySelector(".bsv4-cdd__sw").appendChild(swatchChip(opt.getAttribute("data-bsv4-color-pick")));
        });
      }
      closeColorMenus(menu);
      const opening = menu.hidden;
      if (opening) {
        placeColorMenu(menu, openBtn);
      }
      menu.hidden = !opening;
      sectionRoot.dataset.bsv4CddOpen = opening ? "1" : "";
      return true;
    }
    const pick = e.target.closest("[data-bsv4-color-pick]");
    if (pick) {
      const wrap = pick.closest(".bsv4-row__field--color");
      const sel = wrap.querySelector("[data-bsv4-color]");
      sel.value = pick.getAttribute("data-bsv4-color-pick");
      sel.dispatchEvent(new Event("change", { bubbles: true }));
      closeColorMenus();
      return true;
    }
    closeColorMenus();
    return false;
  }
  function onClick(e) {
    if (onColorMenuClick(e)) return;
    const tab = e.target.closest("[data-bsv4-size-tab]");
    if (tab) {
      setSizeTab(tab.dataset.bsv4SizeTab);
      return;
    }
    if (e.target.closest("[data-bsv4-goto-popular]")) {
      setSizeTab("popular");
      return;
    }
    const add = e.target.closest("[data-bsv4-add-size]");
    if (add) {
      const state2 = getState();
      const file2 = state2.files.find((f) => f.id === state2.activeFileId) || state2.files[0];
      if (file2) {
        const d = fileDims(file2);
        addRow(file2, { w: d.w, h: d.h }, void 0, instanceFor(add));
      }
      return;
    }
    const { node, file, row } = findRow(e.target);
    if (!row) return;
    if (node.classList.contains("is-disabled")) {
      const field = e.target.closest(".bsv4-row__field");
      setSizeTab(row.mode || "custom");
      const el = field && field.querySelector("input, select");
      if (el && !el.disabled) {
        el.focus();
        if (el.tagName === "INPUT") el.select();
      }
      return;
    }
    if (e.target.closest(".bsv4-dpi-up")) {
      const sr = sectionRoot.querySelector(
        `[data-bsv4-bgr-for="${file.id}"] [data-bsv4-toggle-sr]`
      );
      if (sr && !sr.checked) {
        sr.click();
      } else if (!file.superRes) {
        file.superRes = true;
        update({ files: [...getState().files] });
      }
      return;
    }
    const step = e.target.closest("[data-bsv4-step]");
    if (step) {
      const [axis, dir] = step.dataset.bsv4Step.split(":");
      const delta = dir === "+" ? 1 : -1;
      if (axis === "q") {
        const inst = instanceFor(node) || instances[0];
        row.qty = Math.max(inst.lim.minQty, row.qty + delta);
        const qEl = node.querySelector("[data-bsv4-qty]");
        if (qEl) qEl.value = row.qty;
        commit();
      } else {
        const input = node.querySelector(axis === "w" ? "[data-bsv4-w]" : "[data-bsv4-h]");
        if (!input) return;
        input.value = (parseFloat(input.value || 0) + delta * 0.5).toFixed(1);
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    } else if (e.target.closest("[data-bsv4-row-remove]")) {
      file.rows = file.rows.filter((r) => r.id !== row.id);
      node.remove();
      const state2 = getState();
      if (!file.rows.length && state2.files.length === 1 && state2.files[0].id === file.id) {
        update({ files: [], status: "empty", mode: "single" });
        announce("rows-changed", { totalQty: 0 });
        return;
      }
      commit();
    }
  }
  function flagErr(el, msg, sticky) {
    el.textContent = msg;
    el.hidden = false;
    el.classList.remove("is-fading");
    clearTimeout(el.__bsv4ErrT);
    if (sticky) return;
    el.__bsv4ErrT = setTimeout(() => {
      el.classList.add("is-fading");
      el.__bsv4ErrT = setTimeout(() => {
        el.hidden = true;
        el.classList.remove("is-fading");
      }, 400);
    }, 1800);
  }
  function debounce(key, ms, fn) {
    clearTimeout(debounces.get(key));
    debounces.set(key, setTimeout(fn, ms));
  }
  function cancelDebounce(key) {
    clearTimeout(debounces.get(key));
    debounces.delete(key);
  }
  function commit() {
    update({ files: [...getState().files] });
    announce("rows-changed", { totalQty: pageQty() });
  }
  function refitRowsToAspect(file, prevPx, bg) {
    const inst = instances[0];
    if (!inst) return;
    const a = fileAspect(file);
    const scaleW = prevPx && prevPx.w > 0 && file.pxW > 0 ? file.pxW / prevPx.w : null;
    const ext = bg && bg.adopt ? fileDims(file) : null;
    const ceil = bg && bg.ceilPx && bg.ceilPx.w > 0 && bg.ceilPx.h > 0 ? { w: bg.ceilPx.w / 300, h: bg.ceilPx.h / 300 } : null;
    for (const row of file.rows || []) {
      if (row.mode === "popular" && row.popularKey) {
        const box = parsePopularDims(row.popularKey);
        if (!box) continue;
        const cap = Number.isFinite(row.capW) || Number.isFinite(row.capH) ? { maxW: row.capW, maxH: row.capH } : null;
        const max = effectiveMax(inst.lim, cap);
        const fit = containFit(a.w, a.h, box.w, box.h);
        const clamped = clampToMax(fit.w, fit.h, max.maxW, max.maxH) || fit;
        row.w = clamped.w;
        row.h = clamped.h;
      } else {
        let out;
        if (ext) {
          out = computeLockedDims(ext.w, "w", a.w, a.h, inst.lim);
        } else {
          out = computeLockedDims(scaleW ? Math.round(row.w * scaleW * 25.4) / 25.4 : row.w, "w", a.w, a.h, inst.lim);
          if (out && scaleW && Number.isFinite(out.h)) {
            let hSnap = Math.round(out.h * 25.4) / 25.4;
            if (inst.lim.maxH) hSnap = Math.min(hSnap, inst.lim.maxH);
            if (inst.lim.minH) hSnap = Math.max(hSnap, inst.lim.minH);
            out.h = hSnap;
          }
        }
        if (!out) continue;
        if (ceil && out.w > 0 && out.h > 0) {
          const s = Math.min(1, ceil.w / out.w, ceil.h / out.h);
          if (s < 1) {
            out.w = +(out.w * s).toFixed(2);
            out.h = +(out.h * s).toFixed(2);
          }
        }
        row.w = out.w;
        row.h = out.h;
        const v = matchCustomVariant(cfg3.variants || [], row.w, row.h);
        if (v) row.variantId = v.id;
      }
    }
  }
  function pageQty() {
    return getState().files.reduce(
      (sum, f) => sum + f.rows.reduce((s, r) => s + (r.qty || 0), 0),
      0
    );
  }

  // src/pricing.js
  var cfg4;
  var els2;
  var tiers;
  var parsedTiers;
  var model;
  var run = 0;
  var lastWrite = null;
  var teardown = null;
  var viewAllOpen = false;
  var yieldToMain = () => typeof scheduler !== "undefined" && scheduler.yield ? scheduler.yield() : new Promise((r) => setTimeout(r, 0));
  var resolveUnit2 = resolveUnit;
  function deriveVariantTiers(variants) {
    if (!Array.isArray(variants) || !variants.length) return null;
    const out = [];
    for (const v of variants) {
      const label = String(v.option1 || "").trim();
      if (!/^\d+(?:\s*-\s*\d+|\s*\+)?(?:\s*pcs?)?$/i.test(label)) return null;
      const price = parseFloat(v.price);
      if (!Number.isFinite(price)) return null;
      out.push({ qty: label, price });
    }
    const base = out[0].price;
    return out.map((t) => ({
      qty: t.qty,
      pct: base > 0 ? Math.max(0, Math.round((1 - t.price / base) * 100)) : 0,
      price: t.price
    }));
  }
  function computePricing({ files, cartQty, tiers: tierEntries, variants, model: mdl, activeRowId: activeRowId2 }) {
    const parsed = parseTiers(tierEntries || []);
    const flat = [];
    for (const f of files || []) {
      for (const r of f.rows || []) flat.push({ fileId: f.id, row: r });
    }
    const pageQty2 = flat.reduce((s, e) => s + (e.row.qty || 0), 0);
    if (mdl === "per-design") {
      const rows2 = flat.map(({ fileId, row }) => {
        const t = tierFor(parsed, Math.max(row.qty || 0, 1));
        const unit = t ? round2(tierEntries[t.index - 1].price) : 0;
        return {
          fileId,
          rowId: row.id,
          w: row.w,
          h: row.h,
          qty: row.qty,
          unit,
          total: round2(unit * (row.qty || 0)),
          orig: unit,
          origTotal: round2(unit * (row.qty || 0)),
          off: 0
        };
      });
      const total2 = round2(rows2.reduce((s, r) => s + r.total, 0));
      const last = flat.length ? tierFor(parsed, Math.max(flat[flat.length - 1].row.qty || 0, 1)) : null;
      const rawTier2 = last ? { qty: tierEntries[last.index - 1].qty, pct: tierEntries[last.index - 1].pct || 0 } : null;
      return {
        pricing: { model: "per-design", rows: rows2, total: total2, tier: rawTier2 },
        pageQty: pageQty2,
        totalQty: pageQty2,
        activeTier: last,
        activeBase: null
      };
    }
    if (mdl === "per-line") {
      const rows2 = flat.map(({ fileId, row }) => {
        const t = tierFor(parsed, Math.max(row.qty || 0, 1));
        const off2 = t ? t.off : 0;
        const orig = round2(resolveUnit2(row, variants));
        const unit = round2(discounted(orig, off2));
        return {
          fileId,
          rowId: row.id,
          w: row.w,
          h: row.h,
          qty: row.qty,
          unit,
          total: round2(unit * (row.qty || 0)),
          orig,
          origTotal: round2(orig * (row.qty || 0)),
          off: off2
        };
      });
      const total2 = round2(rows2.reduce((s, r) => s + r.total, 0));
      const activeEntry = flat.length ? activeRowId2 && flat.find((e) => e.row.id === activeRowId2) || flat[flat.length - 1] : null;
      const active2 = activeEntry ? tierFor(parsed, Math.max(activeEntry.row.qty || 0, 1)) : null;
      const rawTier2 = active2 ? { qty: tierEntries[active2.index - 1].qty, pct: tierEntries[active2.index - 1].pct || 0 } : null;
      return {
        pricing: { model: "per-line", rows: rows2, total: total2, tier: rawTier2 },
        pageQty: pageQty2,
        totalQty: pageQty2,
        activeTier: active2,
        activeQty: activeEntry ? activeEntry.row.qty || 0 : 0,
        activeBase: activeEntry ? resolveUnit2(activeEntry.row, variants) : null
      };
    }
    const totalQty = pageQty2 + (cartQty || 0);
    const active = flat.length ? tierFor(parsed, Math.max(totalQty, 1)) : null;
    const off = active ? active.off : 0;
    const rows = flat.map(({ fileId, row }) => {
      const orig = round2(resolveUnit2(row, variants));
      const unit = round2(discounted(orig, off));
      return {
        fileId,
        rowId: row.id,
        w: row.w,
        h: row.h,
        qty: row.qty,
        unit,
        total: round2(unit * (row.qty || 0)),
        orig,
        origTotal: round2(orig * (row.qty || 0)),
        off
      };
    });
    const total = round2(rows.reduce((s, r) => s + r.total, 0));
    const rawTier = active ? { qty: tierEntries[active.index - 1].qty, pct: tierEntries[active.index - 1].pct || 0 } : null;
    return {
      pricing: { model: "cumulative", rows, total, tier: rawTier },
      pageQty: pageQty2,
      totalQty,
      activeTier: active,
      // Tier-table reference row = the line item the customer is EDITING
      // (Owen 2026-07-10, modal parity): the last-focused row wins; fall back
      // to the newest row (the old behavior) when nothing was touched or the
      // focused row was deleted.
      activeBase: flat.length ? resolveUnit2(
        (activeRowId2 && flat.find((e) => e.row.id === activeRowId2) || flat[flat.length - 1]).row,
        variants
      ) : null
    };
  }
  var activeRowId = null;
  function initPricing(root2, config) {
    cfg4 = config || {};
    if (!root2.dataset.bsv4PricingFocusBound) {
      root2.dataset.bsv4PricingFocusBound = "1";
      root2.addEventListener("focusin", (e) => {
        const node = e.target.closest && e.target.closest("[data-bsv4-row-id]");
        if (!node || node.dataset.bsv4RowId === activeRowId) return;
        activeRowId = node.dataset.bsv4RowId;
        project();
      });
    }
    if (!cfg4.pricingTable) return;
    const block = root2 && root2.querySelector('[data-bsv4-block="price_table"]');
    if (!block || block.dataset.bsv4PricingInit === "1") return;
    block.dataset.bsv4PricingInit = "1";
    if (teardown) teardown();
    els2 = { lists: [], viewAlls: [] };
    const pageList = block.querySelector("[data-bsv4-tiers]");
    if (pageList) els2.lists.push(pageList);
    const railHost = root2.querySelector("[data-bsv4-pricing-rail]");
    if (railHost && pageList) {
      let railList = railHost.querySelector("[data-bsv4-tiers]");
      if (!railList) {
        const wrap = document.createElement("div");
        wrap.className = "bsv4-pricing";
        wrap.appendChild(pageList.cloneNode(true));
        railHost.appendChild(wrap);
        railList = wrap.querySelector("[data-bsv4-tiers]");
      }
      railHost.hidden = false;
      els2.lists.push(railList);
    }
    for (const list of els2.lists) {
      const existing = list.querySelector(".bsv4-pricing__va");
      if (existing) {
        els2.viewAlls.push(existing);
        continue;
      }
      const va = document.createElement("button");
      va.type = "button";
      va.className = "bsv4-pricing__va";
      va.hidden = true;
      va.innerHTML = '<span class="bsv4-pricing__va-chev" aria-hidden="true"></span><span class="bsv4-pricing__va-text">View All</span>';
      va.addEventListener("click", () => {
        viewAllOpen = !viewAllOpen;
        project();
      });
      const firstTier = list.querySelector("[data-bsv4-tier]");
      if (firstTier) firstTier.parentElement.insertBefore(va, firstTier);
      else list.appendChild(va);
      els2.viewAlls.push(va);
    }
    model = "cumulative";
    tiers = cfg4.tiers || [];
    if ((cfg4.pricingTable.tierSource || "settings") === "variants") {
      const host2 = block.querySelector(".bsv4-pricing") || block;
      host2.setAttribute("data-bsv4-tier-source", "variants");
      const derived = deriveVariantTiers(cfg4.variants);
      if (derived) {
        model = "per-design";
        tiers = derived;
        renderTierRows(derived);
      }
    }
    if (model === "cumulative" && (cfg4.pricingTable.tierScope || "cumulative") === "per_line") {
      model = "per-line";
    }
    parsedTiers = parseTiers(tiers);
    const ctl = new AbortController();
    const unsub = subscribe((_state, patch) => {
      if (patch && patch.pricing === lastWrite && Object.keys(patch).length === 1) return;
      project();
    });
    document.addEventListener("bsv4:rows-changed", () => project(), { signal: ctl.signal });
    document.addEventListener(
      "bsv4:cart-qty-refreshed",
      (e) => {
        cfg4.cartQty = e.detail.count;
        project();
      },
      { signal: ctl.signal }
    );
    teardown = () => {
      unsub();
      ctl.abort();
    };
    project();
  }
  function renderTierRows(derived) {
    for (const list of els2.lists) renderTierRowsInto(list, derived);
  }
  function renderTierRowsInto(list, derived) {
    const wrap = list && list.querySelector("[data-bsv4-tier-rows]");
    if (!wrap) return;
    wrap.textContent = "";
    derived.forEach((t, i) => {
      const row = document.createElement("div");
      row.className = "bsv4-pricing__row";
      row.setAttribute("data-bsv4-tier", String(i + 1));
      row.setAttribute("role", "row");
      const qty = document.createElement("span");
      qty.className = "bsv4-pricing__qty";
      qty.setAttribute("role", "cell");
      qty.textContent = /pc/i.test(t.qty) ? t.qty : `${t.qty} pcs`;
      const price = document.createElement("span");
      price.className = "bsv4-pricing__price";
      price.setAttribute("role", "cell");
      const amt = document.createElement("b");
      amt.setAttribute("data-bsv4-tier-price", "");
      amt.textContent = "\u2014";
      const ea = document.createElement("span");
      ea.className = "bsv4-pricing__ea";
      ea.hidden = true;
      ea.textContent = "ea";
      price.append(amt, ea);
      const off = document.createElement("span");
      off.className = "bsv4-pricing__off";
      off.setAttribute("role", "cell");
      off.textContent = t.pct ? `${t.pct}% off` : "\xA0";
      const msg = document.createElement("span");
      msg.className = "bsv4-pricing__msg";
      msg.setAttribute("data-bsv4-tier-msg", "");
      msg.setAttribute("role", "cell");
      msg.hidden = true;
      row.append(qty, price, off, msg);
      wrap.appendChild(row);
    });
  }
  function setMsg(el, parts) {
    el.textContent = "";
    for (const p of parts) {
      if (typeof p === "string") {
        el.append(p);
      } else {
        const b = document.createElement("b");
        b.textContent = p.b;
        el.append(b);
      }
    }
  }
  async function project() {
    const runId = ++run;
    const state2 = getState();
    const cartQty = state2.cartQty || cfg4.cartQty || 0;
    const files = state2.files.map((f) => ({
      ...f,
      rows: (f.rows || []).filter((r) => r.w > 0)
    }));
    const result = computePricing({
      files,
      cartQty,
      tiers,
      variants: cfg4.variants,
      model,
      activeRowId
    });
    await yieldToMain();
    if (runId !== run) return;
    if (JSON.stringify(result.pricing) !== JSON.stringify(state2.pricing)) {
      lastWrite = result.pricing;
      update({ pricing: result.pricing });
      announce("price-changed", { total: result.pricing.total, tier: result.pricing.tier });
    }
    const tier = result.activeTier;
    let msgParts = null;
    if (result.pricing.rows.length && tier) {
      if (tier.nextOff === null) {
        msgParts = model === "cumulative" ? [`You've beat the video game and reached the `, { b: "Maximum Cumulative Discount!" }] : [`You've reached the `, { b: "maximum discount" }, `!`];
      } else if (model === "per-design") {
        const lastQty = result.pricing.rows[result.pricing.rows.length - 1].qty || 0;
        msgParts = ["Add ", { b: String(tier.max - lastQty + 1) }, ` more to reach ${tier.nextOff}% off.`];
      } else if (model === "per-line") {
        const need = tier.max - Math.max(result.activeQty || 0, 0) + 1;
        msgParts = ["Add ", { b: String(need) }, ` more of this size to reach ${tier.nextOff}% off.`];
      } else {
        const need = unitsToNextTier(tier, result.pageQty, cartQty);
        msgParts = ["Add ", { b: String(need) }, ` of this design or your next upload to reach ${tier.nextOff}% off savings.`];
      }
    }
    for (let li = 0; li < els2.lists.length; li++) {
      const list = els2.lists[li];
      const viewAll = els2.viewAlls[li];
      const foldBelow = tier ? tier.index : 0;
      const foldable = foldBelow > 1;
      if (viewAll) {
        viewAll.hidden = !foldable;
        const txt = viewAll.querySelector(".bsv4-pricing__va-text");
        if (txt) txt.textContent = viewAllOpen ? "hide" : "View All";
        viewAll.classList.toggle("is-open", viewAllOpen);
      }
      const rows = list.querySelectorAll("[data-bsv4-tier]");
      rows.forEach((row, i) => {
        const t = parsedTiers[i];
        if (!t) return;
        row.hidden = foldable && !viewAllOpen && t.index < foldBelow;
        const active = !!tier && t.index === tier.index;
        row.classList.toggle("is-active", active);
        const cell = row.querySelector("[data-bsv4-tier-price]");
        if (cell) {
          let amt = null;
          if (model === "per-design") {
            amt = `\xA3${round2(tiers[i].price).toFixed(2)}`;
          } else if (result.activeBase != null && result.activeBase > 0) {
            amt = `\xA3${discounted(result.activeBase, t.off).toFixed(2)}`;
          }
          cell.textContent = amt || "\u2014";
          const ea = row.querySelector(".bsv4-pricing__ea");
          if (ea) ea.hidden = !amt;
        }
        const msgEl = row.querySelector("[data-bsv4-tier-msg]");
        if (msgEl) {
          if (active && msgParts) {
            setMsg(msgEl, msgParts);
            msgEl.hidden = false;
          } else {
            msgEl.hidden = true;
          }
        }
      });
    }
  }

  // src/cart.js
  var cfg5;
  var els3;
  var busy = false;
  function initCart(root2, config) {
    cfg5 = config;
    els3 = {
      root: root2,
      btn: root2.querySelector("[data-bsv4-checkout]"),
      err: root2.querySelector("[data-bsv4-cart-err]"),
      // ALL precut checkboxes (Builder Pro plan §D): the page CTA card AND the
      // modal rail card both carry data-bsv4-precut — every instance binds,
      // and the store projection keeps them in lockstep.
      precuts: [...root2.querySelectorAll("[data-bsv4-precut]")],
      // anchored to the WRAPPER class, not a .bsv4-cta ancestor — the notes
      // card moved to the Step-1 preview (Owen 2026-07-16) and the wrapper
      // travels with it
      notes: () => root2.querySelector('.bsv4-cta__notes .nt-design-note textarea[name="properties[Design Notes]"]')
    };
    if (!els3.btn) return;
    els3.btnLabel = els3.btn.textContent;
    const rewards = root2.querySelector("[data-bsv4-rewards]");
    if (rewards) {
      const pct = parseFloat(rewards.dataset.bsv4TierPct) || 2;
      const amtEl = rewards.querySelector("[data-bsv4-reward-amt]");
      subscribe(() => {
        const total = getState().pricing?.total || 0;
        const amt = `\xA3${(total * pct / 100).toFixed(2)}`;
        if (amtEl && amtEl.textContent !== amt) amtEl.textContent = amt;
      });
    }
    if (els3.precuts.length) {
      for (const cb of els3.precuts) {
        cb.addEventListener("change", () => update({ precut: cb.checked }));
      }
      const each = root2.querySelector(".bsv4-cta__precut-each");
      const info = root2.querySelector(".bsv4-cta__precut-info");
      const fee = Number(cfg5.precutFee) || 0;
      if (each && fee) each.textContent = `\xA3${fee.toFixed(2)} each`;
      subscribe((state2, patch) => {
        if (!("precut" in patch)) return;
        for (const cb of els3.precuts) {
          if (cb.checked !== state2.precut) cb.checked = state2.precut;
        }
        if (each && fee) {
          each.hidden = !state2.precut;
          if (info) info.style.display = state2.precut ? "none" : "";
        }
      });
    }
    els3.btn.addEventListener("click", () => addToCart());
    setAddAnotherGate(maybeCommitThenIntake);
    const addAnother = root2.querySelector("[data-bsv4-add-another]");
    const addInput = root2.querySelector("[data-bsv4-add-input]");
    if (addAnother && addInput) {
      const mainInput = root2.querySelector("[data-bsv4-file-input]");
      if (mainInput && mainInput.accept) addInput.accept = mainInput.accept;
      const zoneReady = () => !busy && getState().status !== "uploading";
      addAnother.addEventListener("click", () => {
        if (zoneReady()) addInput.click();
      });
      addAnother.addEventListener("keydown", (e) => {
        if ((e.key === "Enter" || e.key === " ") && zoneReady()) {
          e.preventDefault();
          addInput.click();
        }
      });
      addInput.addEventListener("change", () => {
        const files = Array.from(addInput.files || []);
        addInput.value = "";
        if (files.length) addAnotherFlow(() => intake(files));
      });
      addAnother.addEventListener("dragover", (e) => {
        e.preventDefault();
        addAnother.classList.add("is-dragover");
      });
      addAnother.addEventListener("dragleave", () => addAnother.classList.remove("is-dragover"));
      addAnother.addEventListener("drop", (e) => {
        e.preventDefault();
        addAnother.classList.remove("is-dragover");
        const files = Array.from(e.dataTransfer && e.dataTransfer.files || []);
        if (files.length && zoneReady()) addAnotherFlow(() => intake(files));
      });
    }
  }
  async function addAnotherFlow(run2) {
    const ok = await addToCart({ stayAndReset: true });
    if (!ok) return;
    try {
      await run2();
    } catch (_) {
    }
  }
  function maybeCommitThenIntake({ files, entries }) {
    if (busy) return true;
    const state2 = getState();
    if (state2.status !== "uploaded" || state2.mode === "multi" || !state2.files.length) return false;
    addAnotherFlow(() => entries ? intakeFromUrls(entries) : intake(files));
    return true;
  }
  async function showCartConfirm(committed) {
    try {
      const c2 = await fetch("/cart.js", { cache: "no-store" }).then((r) => r.json());
      const n = c2 && Number(c2.item_count);
      if (Number.isFinite(n) && n > 0) {
        const setAll = (sel, fn) => document.querySelectorAll(sel).forEach(fn);
        const sn = String(n);
        setAll("[data-header-cart-count]", (elm) => {
          elm.textContent = sn;
          elm.setAttribute("data-count", sn);
        });
        if (typeof c2.total_price === "number") {
          const money = `\xA3${(c2.total_price / 100).toFixed(2)}`;
          setAll("[data-header-cart-total]", (elm) => {
            elm.textContent = money;
          });
        }
      }
    } catch (_) {
    }
    const el = document.createElement("div");
    el.className = "bsv4-cartok";
    el.setAttribute("role", "status");
    const c = committed || {};
    const price = typeof c.total === "number" && c.total > 0 ? `\xA3${c.total.toFixed(2)}` : "";
    const K = "bsv4-cartok__";
    el.innerHTML = `<div class="${K}head"><span class="${K}check" aria-hidden="true"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5"/></svg></span><strong>Added to cart</strong><button type="button" class="${K}x" aria-label="Close">&times;</button></div>` + (c.name ? `<div class="${K}body">${c.thumb ? `<img class="${K}thumb" src="${c.thumb}" alt="">` : ""}<span class="${K}meta"><strong class="${K}name"></strong><span class="${K}qty">Qty ${c.qty || 1}</span></span>${price ? `<strong class="${K}price">${price}</strong>` : ""}</div>` : "");
    const nameEl = el.querySelector(".bsv4-cartok__name");
    if (nameEl) nameEl.textContent = c.name || "";
    (els3.root || document.body).appendChild(el);
    requestAnimationFrame(() => el.classList.add("is-in"));
    const dismiss = () => {
      el.classList.remove("is-in");
      setTimeout(() => el.remove(), 300);
    };
    let timer = setTimeout(dismiss, 4e3);
    el.addEventListener("mouseenter", () => clearTimeout(timer));
    el.addEventListener("mouseleave", () => {
      clearTimeout(timer);
      timer = setTimeout(dismiss, 1500);
    });
    el.querySelector(`.${K}x`).addEventListener("click", () => {
      clearTimeout(timer);
      dismiss();
    });
  }
  async function addToCart(opts = {}) {
    if (busy) return false;
    if (getState().status === "uploading") return false;
    hideErr();
    const state2 = getState();
    const noteEl = els3.notes();
    const noteBox = noteEl && noteEl.closest(".nt-design-note");
    const stageNotes = !!els3.root.querySelector("[data-bsv4-notes]");
    const noteOpen = stageNotes || !noteBox || noteBox.getAttribute("data-collapsed") === "false";
    const designNote = noteEl && noteOpen ? noteEl.value.trim() : "";
    const popupSource = readPopupAttribution();
    const rowOpt = cfg5 && Array.isArray(cfg5.sizing) && cfg5.sizing[0] && cfg5.sizing[0].rowOption;
    if (rowOpt) {
      const noColor = state2.files.find(
        (f) => (f.rows || []).some(
          (r) => (r.mode || "custom") === "popular" && r.w > 0 && r.variantId == null
        )
      );
      if (noColor) {
        showErr(`Please pick a ${String(rowOpt).split(" ").pop().toLowerCase()} for each size.`);
        const scope = state2.mode === "multi" && els3.root.querySelector(`[data-bsv4-tile="${noColor.id}"]`) || els3.root;
        const btn = scope.querySelector(".bsv4-cdd__btn");
        if (btn) {
          btn.focus();
          btn.scrollIntoView({ block: "center" });
        }
        return false;
      }
    }
    if (window.__bsv4CartGate && window.__bsv4CartGate(state2) === false) return false;
    const items = [];
    const skipped = [];
    for (const file of state2.files) {
      const note = (file.designNote || designNote || "").trim();
      if (!file.params || !file.params.order) {
        const png = ((cfg5 || {}).imgixParams || {}).png || {};
        file.params = {
          ...png,
          ...file.params || {},
          order: file.params && file.params.order || png.order || "",
          cart: file.params && file.params.cart || png.cart || ""
        };
      }
      for (const row of file.rows) {
        if (!Number.isFinite(row.variantId) || !Number.isFinite(row.qty) || row.qty < 1) {
          skipped.push(row.id);
          continue;
        }
        if (!(row.w > 0 && row.h > 0)) {
          skipped.push(row.id);
          continue;
        }
        items.push(buildItem(file, row, { designNote: note, precut: state2.precut, popupSource }));
      }
    }
    if (items.some(
      (it) => !it.properties["Upload (Vector Files Preferred)"] && !it.properties["_Original Image"]
    )) {
      showErr("We couldn't attach your artwork file. Please re-upload this design and try again.");
      return false;
    }
    if (!items.length) {
      if (!state2.files.length) {
        showErr("Upload a design and pick a size first.");
      } else {
        showErr("Pick a size to continue \u2014 enter a custom size or select a popular size.");
        const scope = state2.mode === "multi" && els3.root.querySelector("[data-bsv4-tile]") || els3.root;
        const sel = scope.querySelector("[data-bsv4-popular]");
        if (sel && !sel.value) sel.focus();
      }
      return false;
    }
    busy = true;
    els3.btn.disabled = true;
    els3.btn.textContent = "Adding\u2026";
    try {
      const res = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, attributes: "" })
      });
      let data = null;
      try {
        data = await res.json();
      } catch (_) {
        throw new Error("cart responded with a non-JSON page");
      }
      if (!res.ok || data && data.status && data.status >= 400) {
        throw new Error(data && (data.description || data.message) || `cart error ${res.status}`);
      }
      fireGa4(popupSource);
      announce("added-to-cart", { items: items.length });
      if (opts.stayAndReset) {
        const committed = {
          name: state2.files[0] && state2.files[0].name || "Your design",
          // _cartImg is on every payload item (Gate-1 captures) — no fallback.
          thumb: items[0] && items[0].properties && items[0].properties["_cartImg"] || "",
          qty: items.reduce((n, it) => n + it.quantity, 0),
          // payload always sets quantity
          total: (state2.pricing?.rows || []).reduce((n, r) => n + (r.total || 0), 0) || null
        };
        await refreshCartQty();
        reset();
        showCartConfirm(committed);
        if (noteEl) noteEl.value = "";
        busy = false;
        els3.btn.disabled = false;
        els3.btn.textContent = els3.btnLabel || "Add to Basket";
      } else {
        window.location.href = "/cart";
      }
      return true;
    } catch (err) {
      showErr("Something went wrong adding to your cart \u2014 please try again.");
      announce("cart-error", { error: String(err && err.message) });
      busy = false;
      els3.btn.disabled = false;
      els3.btn.textContent = els3.btnLabel || "Add to Basket";
      return false;
    }
  }
  async function refreshCartQty() {
    try {
      const res = await fetch(`${location.pathname}?view=itemsInCart__cumulative`).then((r) => r.json());
      const count = res ? Number(res.count) : NaN;
      if (Number.isFinite(count)) {
        update({ cartQty: count });
        announce("cart-qty-refreshed", { count });
      }
    } catch (_) {
    }
  }
  function readPopupAttribution() {
    try {
      return sessionStorage.getItem("dtf_popup_attribution");
    } catch (_) {
      return null;
    }
  }
  function fireGa4(popupAttrib) {
    if (!popupAttrib) return;
    try {
      sessionStorage.removeItem("dtf_popup_attribution");
      const age = Date.now() - parseInt(popupAttrib, 10);
      if (age <= 3e5 && typeof window.gtag === "function") {
        window.gtag("event", "dtf_popup_add_to_cart", {
          popup_version: "v1",
          popup_environment: window.Shopify?.theme?.role === "main" ? "production" : "staging",
          device_type: window.innerWidth <= 767 ? "mobile" : "desktop",
          time_to_conversion_ms: age,
          popup_source: "dtf_popup"
        });
      }
    } catch (_) {
    }
  }
  function errEls() {
    return [els3.err, els3.root.querySelector("[data-bsv4-modal-err]")].filter(Boolean);
  }
  function showErr(msg) {
    for (const el of errEls()) {
      el.textContent = msg;
      el.hidden = false;
    }
  }
  function hideErr() {
    for (const el of errEls()) el.hidden = true;
  }

  // src/modal.js
  var cfg6;
  var els4;
  var announcedClosed = true;
  var docBound2 = false;
  function capState(fileCount, maxFiles) {
    const max = Number.isFinite(maxFiles) && maxFiles > 0 ? maxFiles : Infinity;
    return {
      atCap: fileCount >= max,
      remaining: max === Infinity ? Infinity : Math.max(0, max - fileCount)
    };
  }
  function initModal(root2, config) {
    cfg6 = config;
    const wrap = root2.querySelector('[data-bsv4-block="multi_upload"]');
    if (!wrap || !cfg6.modal) return;
    if (wrap.dataset.bsv4ModalBound === "1") return;
    wrap.dataset.bsv4ModalBound = "1";
    const dialog = wrap.querySelector("[data-bsv4-modal]");
    if (!dialog) return;
    els4 = {
      root: root2,
      wrap,
      dialog,
      tiles: dialog.querySelector("[data-bsv4-tiles]"),
      tileTemplate: dialog.querySelector("[data-bsv4-tile-template]"),
      subtotal: dialog.querySelector("[data-bsv4-modal-subtotal]"),
      savingsRow: dialog.querySelector("[data-bsv4-modal-savings]"),
      savingsAmt: dialog.querySelector("[data-bsv4-modal-savings-amt]"),
      total: dialog.querySelector("[data-bsv4-modal-total]"),
      addMore: dialog.querySelector("[data-bsv4-modal-addfile]"),
      capMsg: dialog.querySelector("[data-bsv4-modal-cap]"),
      checkout: dialog.querySelector("[data-bsv4-modal-checkout]"),
      err: dialog.querySelector("[data-bsv4-modal-err]"),
      // UK print method (Vivid AF vs Standard): the rail's own radio pair
      // MIRRORS the page's print_method block — snippets/bsv4-modal.liquid
      // renders these hooks only on the UK theme; [] / null elsewhere = no-op.
      methodInputs: [...dialog.querySelectorAll("[data-bsv4-modal-method-input]")],
      methodStatus: dialog.querySelector("[data-bsv4-modal-method-status]"),
      // Shared page element this block cooperates with (established hosting
      // exception — the uploader owns it; we only click it):
      fileInput: root2.querySelector("[data-bsv4-file-input]")
    };
    if (cfg6.modal.footerCtaText && els4.checkout) {
      els4.checkout.textContent = cfg6.modal.footerCtaText;
    }
    if (els4.capMsg && Number.isFinite(cfg6.modal.maxFiles)) {
      els4.capMsg.textContent = `Maximum ${cfg6.modal.maxFiles} designs per order`;
    }
    const precutCard = dialog.querySelector("[data-bsv4-modal-precut]");
    if (precutCard && !cfg6.precutEnabled) precutCard.hidden = true;
    els4.precutEach = dialog.querySelector("[data-bsv4-precut-each]");
    els4.precutSum = dialog.querySelector("[data-bsv4-precut-sum]");
    const fee = Number(cfg6.precutFee) || 0;
    if (fee && els4.precutEach) {
      els4.precutEach.textContent = `\xA3${fee.toFixed(2)} each`;
      els4.precutEach.hidden = false;
    }
    dialog.addEventListener("cancel", (e) => e.preventDefault());
    dialog.addEventListener("close", onDialogClosed);
    for (const btn of dialog.querySelectorAll("[data-bsv4-view]")) {
      btn.addEventListener("click", () => {
        const large = btn.dataset.bsv4View === "large";
        dialog.classList.toggle("bsv4-modal--large", large);
        for (const b of dialog.querySelectorAll("[data-bsv4-view]")) {
          b.classList.toggle("is-active", b === btn);
        }
      });
    }
    for (const input of els4.methodInputs) {
      input.addEventListener("change", onModalMethodChange);
    }
    els4.lightbox = dialog.querySelector("[data-bsv4-lightbox]");
    els4.lightboxImg = dialog.querySelector("[data-bsv4-lightbox-img]");
    els4.lightbox?.addEventListener("click", () => {
      els4.lightbox.close();
      els4.lightboxImg.src = "";
    });
    if (els4.addMore) {
      if (!els4.fileInput) els4.addMore.hidden = true;
      const browse = () => {
        if (capState(getState().files.length, cfg6.modal.maxFiles).atCap) return;
        els4.fileInput.click();
      };
      els4.addMore.addEventListener("click", browse);
      els4.addMore.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          browse();
        }
      });
      els4.addMore.addEventListener("dragover", (e) => {
        e.preventDefault();
        els4.addMore.classList.add("is-dragover");
      });
      els4.addMore.addEventListener("dragleave", () => els4.addMore.classList.remove("is-dragover"));
      els4.addMore.addEventListener("drop", (e) => {
        e.preventDefault();
        els4.addMore.classList.remove("is-dragover");
        if (capState(getState().files.length, cfg6.modal.maxFiles).atCap) return;
        if (!els4.fileInput || !e.dataTransfer?.files?.length) return;
        els4.fileInput.files = e.dataTransfer.files;
        els4.fileInput.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
    els4.checkout?.addEventListener("click", () => addToCart());
    dialog.querySelector("[data-bsv4-modal-upcancel]")?.addEventListener("click", () => {
      window.resetLoading?.();
    });
    els4.tiles.addEventListener("click", onTileClick);
    els4.tiles.addEventListener("change", onTileTabChange);
    if (cfg6.modal.perFileNotes) els4.tiles.addEventListener("input", onNoteInput);
    subscribe(sync);
    if (!docBound2) {
      docBound2 = true;
      document.addEventListener("bsv4:upload-started", onUploadStarted);
    }
    sync();
  }
  var localArt = /* @__PURE__ */ new Map();
  function onUploadStarted(e) {
    const d = e.detail || {};
    if (d.id && d.localUrl) localArt.set(d.id, d.localUrl);
    if (!els4 || getState().mode !== "multi") return;
    addPendingTile(d);
    if (!els4.dialog.open) openDialog();
  }
  function addPendingTile({ id, name, localUrl }) {
    if (!id || els4.tiles.querySelector(`[data-bsv4-pending="${id}"]`)) return;
    const tile = document.createElement("section");
    tile.className = "bsv4-tile bsv4-tile--pending";
    tile.setAttribute("data-bsv4-pending", id);
    tile.setAttribute("aria-busy", "true");
    tile.innerHTML = '<aside class="bsv4-tile__side"><figure class="bsv4-tile__hero"><img class="bsv4-tile__thumb" alt="" width="200" height="200"></figure></aside><div class="bsv4-tile__main"><p class="bsv4-tile__pending-name"></p><span class="bsv4-skel"></span><span class="bsv4-skel"></span><span class="bsv4-skel"></span></div>';
    const img = tile.querySelector("img");
    img.src = localUrl || NINJA_GIF;
    if (!localUrl) img.classList.add("bsv4-tile__thumb--pending");
    tile.querySelector("p").textContent = name || "Uploading\u2026";
    els4.tiles.appendChild(tile);
  }
  function clearPendingTiles() {
    for (const t of [...els4.tiles.querySelectorAll("[data-bsv4-pending]")]) t.remove();
  }
  function sync() {
    const files = getState().files;
    const mode = getState().mode;
    syncModalMethod();
    if (getState().status !== "uploading") clearPendingTiles();
    const pending = els4.tiles.querySelector("[data-bsv4-pending]") !== null;
    if (files.length && mode === "multi") {
      for (const tile of [...els4.tiles.querySelectorAll("[data-bsv4-tile]")]) {
        if (!files.some((f) => f.id === tile.dataset.bsv4Tile)) tile.remove();
      }
      for (const file of files) {
        const tileEl = els4.tiles.querySelector(`[data-bsv4-tile="${file.id}"]`);
        if (!tileEl) {
          buildTile(file);
          continue;
        }
        const thumb = tileEl.querySelector("[data-bsv4-tile-thumb]");
        if (thumb) swapImage(thumb, thumbUrl(file), tileEl.querySelector(".bsv4-tile__hero"));
      }
      if (!els4.dialog.open) openDialog();
      queueMicrotask(adoptTileRows);
    } else if (!files.length && !pending) {
      for (const tile of [...els4.tiles.querySelectorAll("[data-bsv4-tile]")]) tile.remove();
      if (els4.dialog.open) closeDialog();
    }
    refreshChrome(files);
  }
  function selectedPrintMethod2() {
    const options = getState().options;
    return options && options.printMethod || null;
  }
  function syncModalMethod() {
    if (!els4.methodInputs.length) return;
    const selected = selectedPrintMethod2() || cfg6.printMethod && cfg6.printMethod.defaultValue;
    for (const input of els4.methodInputs) {
      input.checked = input.value === selected;
    }
  }
  function onModalMethodChange(event) {
    const input = event.target;
    if (!input.matches("[data-bsv4-modal-method-input]") || !input.checked) return;
    const pageInput = [...els4.root.querySelectorAll("[data-bsv4-print-method-input]")].find(
      (candidate) => candidate.value === input.value
    );
    if (!pageInput) return;
    if (!pageInput.checked || selectedPrintMethod2() !== input.value) {
      pageInput.checked = true;
      pageInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (els4.methodStatus) {
      els4.methodStatus.textContent = `${input.value} selected. Prices updated.`;
    }
  }
  function buildTile(file) {
    const tile = els4.tileTemplate.content.firstElementChild.cloneNode(true);
    tile.dataset.bsv4Tile = file.id;
    for (const radio of tile.querySelectorAll("[data-bsv4-tiletab]")) {
      radio.name = `bsv4-tt-${file.id}`;
    }
    const thumb = tile.querySelector("[data-bsv4-tile-thumb]");
    if (thumb) {
      thumb.alt = file.name || "";
      const target = thumbUrl(file);
      const local = localArt.get(file.id);
      const orig = originalUrl(file);
      const instant = local || orig;
      if (instant && instant !== target) {
        const fallback = local && orig !== target ? orig : "";
        thumb.onerror = () => {
          const src = thumb.getAttribute("src");
          if (src !== instant && src !== fallback) {
            thumb.onerror = null;
            return;
          }
          if (src === local && fallback) {
            thumb.src = fallback;
            return;
          }
          thumb.removeAttribute("src");
          thumb.style.visibility = "hidden";
          thumb.onerror = null;
        };
        thumb.src = instant;
      }
      swapImage(thumb, target, tile.querySelector(".bsv4-tile__hero"));
    }
    const name = tile.querySelector("[data-bsv4-tile-name]");
    if (name) name.textContent = file.name || "";
    const note = tile.querySelector("[data-bsv4-tile-note]");
    if (note) {
      if (cfg6.modal.perFileNotes) {
        if (file.designNote) {
          note.value = file.designNote;
          const open2 = tile.querySelector("[data-bsv4-tile-note-open]");
          if (open2) open2.checked = true;
        }
      } else {
        (note.closest(".bsv4-tile__note") || note).remove();
      }
    }
    const pend = els4.tiles.querySelector(`[data-bsv4-pending="${file.id}"]`);
    if (pend) pend.replaceWith(tile);
    else els4.tiles.appendChild(tile);
    const host2 = rowsHost(tile);
    for (const row of file.rows) {
      if (!host2) continue;
      const node = row.id ? els4.root.querySelector(`[data-bsv4-row-id="${row.id}"]`) : null;
      if (node) host2.appendChild(node);
      else {
        try {
          renderRow(file, row, host2);
        } catch (err) {
          console.warn("bsv4 modal: shared row component unavailable", err);
        }
      }
    }
    applyTileDim(tile);
    announce("tile-created", { id: file.id, fileId: file.id, tileEl: tile });
  }
  function rowsHost(tile) {
    return tile.querySelector("[data-bsv4-tile-rows]");
  }
  function tileTab(tile) {
    const pop = tile.querySelector('[data-bsv4-tiletab][value="popular"]');
    return pop && pop.checked ? "popular" : "custom";
  }
  function applyTileDim(tile) {
    const file = getState().files.find((f) => f.id === tile.dataset.bsv4Tile);
    if (!file) return;
    const tab = tileTab(tile);
    for (const n of tile.querySelectorAll("[data-bsv4-row-id]")) {
      const row = (file.rows || []).find((r) => r.id === n.dataset.bsv4RowId);
      const off = (row && row.mode || "custom") !== tab;
      n.classList.toggle("is-disabled", off);
      n.querySelectorAll("input, select, button").forEach((el) => {
        el.disabled = off;
      });
    }
  }
  function applyAllTileDims() {
    for (const tile of els4.tiles.querySelectorAll("[data-bsv4-tile]")) applyTileDim(tile);
  }
  function activateTileTab(tile, file, mode) {
    const radio = tile.querySelector(`[data-bsv4-tiletab][value="${mode}"]`);
    if (radio && !radio.checked) radio.checked = true;
    if (!(file.rows || []).some((r) => (r.mode || "custom") === mode)) {
      const w = parseFloat(file.widthIn ?? file.inW);
      const h = parseFloat(file.heightIn ?? file.inH);
      try {
        addRow(file, { w, h }, rowsHost(tile), void 0, mode);
      } catch (err) {
        console.warn("bsv4 modal: shared row component unavailable", err);
      }
    }
    applyTileDim(tile);
  }
  function adoptTileRows() {
    if (getState().mode !== "multi") return;
    for (const file of getState().files) {
      const tile = els4.tiles.querySelector(`[data-bsv4-tile="${file.id}"]`);
      if (!tile) continue;
      const host2 = rowsHost(tile);
      if (!host2) continue;
      for (const row of file.rows || []) {
        if (!row.id) continue;
        const node = els4.root.querySelector(`[data-bsv4-row-id="${row.id}"]`);
        if (node && !host2.contains(node)) host2.appendChild(node);
      }
    }
    applyAllTileDims();
  }
  function onTileClick(e) {
    const tile = e.target.closest("[data-bsv4-tile]");
    if (!tile) return;
    const fileId = tile.dataset.bsv4Tile;
    const file = getState().files.find((f) => f.id === fileId);
    if (!file) return;
    const dimmed = e.target.closest("[data-bsv4-row-id]");
    if (dimmed && dimmed.classList.contains("is-disabled")) {
      e.stopPropagation();
      const row = (file.rows || []).find((r) => r.id === dimmed.dataset.bsv4RowId);
      activateTileTab(tile, file, row && row.mode || "custom");
      const field = e.target.closest(".bsv4-row__field");
      const el = field && field.querySelector("input, select");
      if (el && !el.disabled) {
        el.focus();
        if (el.tagName === "INPUT") el.select();
      }
      return;
    }
    if (e.target.closest("[data-bsv4-tile-add]")) {
      const mode = tileTab(tile);
      const w = parseFloat(file.widthIn ?? file.inW);
      const h = parseFloat(file.heightIn ?? file.inH);
      try {
        addRow(file, { w, h }, rowsHost(tile), void 0, mode);
      } catch (err) {
        console.warn("bsv4 modal: shared row component unavailable", err);
      }
    } else if (e.target.closest("[data-bsv4-tile-goto-popular]")) {
      activateTileTab(tile, file, "popular");
    } else if (e.target.closest("[data-bsv4-tile-zoom]")) {
      if (els4.lightbox && els4.lightboxImg) {
        els4.lightboxImg.alt = file.name || "";
        swapImage(els4.lightboxImg, thumbUrl(file), els4.lightbox);
        els4.lightbox.showModal();
      }
    } else if (e.target.closest("[data-bsv4-tile-remove]")) {
      const doRemove = () => {
        const files = getState().files.filter((f) => f.id !== fileId);
        if (!files.length) {
          reset();
        } else {
          update({ files });
        }
        announce("file-removed", { id: fileId });
        announce("rows-changed", {});
      };
      const target = getState().files.find((f) => f.id === fileId);
      if ((target && target.rows || []).length > 1) confirmRemove(doRemove);
      else doRemove();
    }
  }
  function onTileTabChange(e) {
    if (e.target.matches("[data-bsv4-tile-note-open]")) {
      const tile2 = e.target.closest("[data-bsv4-tile]");
      const file2 = tile2 && getState().files.find((f) => f.id === tile2.dataset.bsv4Tile);
      if (!file2) return;
      const note = tile2.querySelector("[data-bsv4-tile-note]");
      file2.designNote = e.target.checked && note ? note.value.trim() : "";
      return;
    }
    if (!e.target.matches("[data-bsv4-tiletab]") || !e.target.checked) return;
    const tile = e.target.closest("[data-bsv4-tile]");
    const file = tile && getState().files.find((f) => f.id === tile.dataset.bsv4Tile);
    if (!file) return;
    activateTileTab(tile, file, e.target.value === "popular" ? "popular" : "custom");
  }
  function onNoteInput(e) {
    if (!e.target.matches("[data-bsv4-tile-note]")) return;
    const tile = e.target.closest("[data-bsv4-tile]");
    const file = tile && getState().files.find((f) => f.id === tile.dataset.bsv4Tile);
    if (file) file.designNote = e.target.value.trim();
  }
  function refreshChrome(files) {
    const cap = capState(files.length, cfg6.modal.maxFiles);
    if (els4.addMore && !els4.addMore.hidden) {
      els4.addMore.classList.toggle("is-capped", cap.atCap);
      els4.addMore.setAttribute("aria-disabled", cap.atCap ? "true" : "false");
    }
    if (els4.capMsg) els4.capMsg.hidden = !cap.atCap;
    const precutFee = Number(cfg6.precutFee) || 0;
    let precutQty = 0;
    for (const f of files) for (const r of f.rows || []) precutQty += Number(r.qty) || 0;
    if (els4.precutSum) {
      els4.precutSum.textContent = `+\xA3${(precutFee * precutQty).toFixed(2)}`;
      els4.precutSum.hidden = !(precutFee && precutQty);
    }
    const pricing = getState().pricing || {};
    const rows = Array.isArray(pricing.rows) ? pricing.rows : [];
    const hasRows = rows.length > 0;
    const total = pricing.total || 0;
    const origSum = rows.reduce((s, r) => s + (Number.isFinite(r.origTotal) ? r.origTotal : r.total || 0), 0);
    const savings = Math.max(0, origSum - total);
    const precutCost = getState().precut ? precutFee * precutQty : 0;
    if (els4.subtotal) els4.subtotal.textContent = hasRows ? `\xA3${origSum.toFixed(2)}` : "\u2014";
    if (els4.total) els4.total.textContent = hasRows ? `\xA3${(total + precutCost).toFixed(2)}` : "\u2014";
    if (els4.savingsRow) {
      const show = hasRows && savings > 4e-3;
      els4.savingsRow.hidden = !show;
      if (show && els4.savingsAmt) els4.savingsAmt.textContent = `\u2212\xA3${savings.toFixed(2)}`;
    }
  }
  function thumbUrl(file) {
    try {
      return previewUrl(file);
    } catch (_) {
      return file.imgixUrl || "";
    }
  }
  function openDialog() {
    if (!els4.dialog.open) {
      els4.dialog.show();
      announcedClosed = false;
      announce("modal-opened", { files: getState().files.length });
      els4.dialog.scrollIntoView({ block: "start" });
    }
  }
  function closeDialog() {
    if (els4.dialog.open) els4.dialog.close();
    onDialogClosed();
  }
  function onDialogClosed() {
    if (!announcedClosed) {
      announcedClosed = true;
      announce("dialog-closed", { files: getState().files.length });
    }
  }

  // src/print-method.js
  setPrintMethodSource(() => {
    const options = getState().options;
    return options && options.printMethod || null;
  });
  function initPrintMethod(root2, config) {
    const methodConfig = config && config.printMethod;
    const block = root2 && root2.querySelector('[data-bsv4-block="print_method"]');
    if (!methodConfig || !block || block.dataset.bsv4PrintMethodInit === "1") return;
    block.dataset.bsv4PrintMethodInit = "1";
    const selector = block.querySelector("[data-bsv4-print-method]");
    const inputs = [...block.querySelectorAll("[data-bsv4-print-method-input]")];
    if (!selector || !inputs.length) return;
    const initial = inputs.find((input) => input.checked) || inputs.find((input) => input.value === methodConfig.defaultValue) || inputs[0];
    const options = block.querySelector("[data-bsv4-print-method-options]");
    const desktopPreview = block.querySelector("[data-bsv4-print-method-desktop-preview]");
    const mobilePreview = block.querySelector("[data-bsv4-print-method-mobile-preview]");
    const mobileSummary = block.querySelector("[data-bsv4-print-method-summary]");
    const continueButton = block.querySelector("[data-bsv4-print-method-continue]");
    const comparisonButtons = [...block.querySelectorAll("[data-bsv4-print-method-comparison]")];
    const mobileQuery = window.matchMedia("(max-width: 560px)");
    const methodKind = (input) => input && input.dataset.bsv4PrintMethodKind === "vivid" ? "vivid" : "standard";
    const methodSummary = (input) => {
      const option = input && input.closest("[data-bsv4-print-method-option]");
      const label = option && option.querySelector(".bsv4-print-method__topline strong");
      return `${label ? label.textContent.trim() : input.value} \xB7 ${methodKind(input) === "vivid" ? "9" : "5"} inks`;
    };
    const updateSelectionUi = (input) => {
      const kind = methodKind(input);
      selector.dataset.bsv4SelectedMethod = kind;
      selector.dataset.bsv4PreviewMethod = kind;
      if (mobileSummary) mobileSummary.textContent = methodSummary(input);
      for (const button of comparisonButtons) {
        button.setAttribute("aria-pressed", String(button.dataset.bsv4PrintMethodComparison === kind));
      }
    };
    const openDesktopPreview = (kind) => {
      if (!desktopPreview || mobileQuery.matches) return;
      selector.dataset.bsv4PreviewMethod = kind;
      desktopPreview.hidden = false;
    };
    const closeDesktopPreview = () => {
      if (desktopPreview) desktopPreview.hidden = true;
    };
    initial.checked = true;
    updateSelectionUi(initial);
    update({ options: { ...getState().options, printMethod: initial.value } });
    for (const option of block.querySelectorAll("[data-bsv4-print-method-option]")) {
      const kind = option.dataset.bsv4PrintMethodOption === "vivid" ? "vivid" : "standard";
      option.addEventListener("pointerenter", () => openDesktopPreview(kind));
      option.addEventListener("focusin", () => openDesktopPreview(kind));
    }
    if (options) {
      options.addEventListener("pointerleave", closeDesktopPreview);
      options.addEventListener("focusout", () => {
        requestAnimationFrame(() => {
          if (!options.contains(document.activeElement)) closeDesktopPreview();
        });
      });
    }
    if (continueButton) {
      continueButton.addEventListener("click", () => {
        const sizeBlock = root2.querySelector('[data-bsv4-block="size_inputs"]');
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        sizeBlock?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
      });
    }
    for (const button of comparisonButtons) {
      button.addEventListener("click", () => {
        const kind = button.dataset.bsv4PrintMethodComparison === "vivid" ? "vivid" : "standard";
        const input = inputs.find((candidate) => methodKind(candidate) === kind);
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
    const remapRows = () => {
      const state2 = getState();
      for (const file of state2.files) {
        for (const row of file.rows || []) {
          if (row.mode === "popular" && !row.popularKey) {
            row.variantId = null;
            delete row.price;
            continue;
          }
          const popularVariant = row.popularKey && (config.variants || []).find(
            (variant) => variant.isPopular && variant.option1 === row.popularKey && variantMatchesPrintMethod(variant)
          );
          const pricingVariant = row.mode === "popular" && !config.smartSizing ? popularVariant : matchCustomVariant(config.variants || [], row.w, row.h) || popularVariant;
          row.variantId = pricingVariant ? pricingVariant.id : null;
          delete row.price;
        }
      }
      update({ files: [...state2.files] });
      announce("rows-changed", {});
    };
    block.addEventListener("change", (event) => {
      const input = event.target.closest && event.target.closest("[data-bsv4-print-method-input]");
      if (!input || !input.checked) return;
      update({ options: { ...getState().options, printMethod: input.value } });
      updateSelectionUi(input);
      if (methodKind(input) === "vivid" && mobileQuery.matches && mobilePreview) mobilePreview.open = true;
      remapRows();
      const status = block.querySelector("[data-bsv4-print-method-status]");
      if (status) status.textContent = `${input.value} selected. Prices updated.`;
      announce("print-method-changed", { value: input.value });
    });
  }

  // src/designs.js
  var wired = false;
  function initDesigns(root2, config) {
    if (!root2.querySelector('[data-bsv4-panel="previous"] :is(.md-container, [data-alv2-open])')) return;
    if (wired) return;
    wired = true;
    (function wireColsPicker() {
      const panel = root2.querySelector('[data-bsv4-panel="previous"]');
      if (!panel || panel.querySelector(".dtfut__cols") === null) return;
      const syncActiveCol = (cols) => {
        for (const b of panel.querySelectorAll(".dtfut__cols-btn")) {
          b.classList.toggle("is-active", b.getAttribute("data-dtfut-cols") === cols);
        }
      };
      const isMobile = window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
      const initialCols = isMobile ? "2" : "4";
      panel.setAttribute("data-cols", initialCols);
      syncActiveCol(initialCols);
      const mobileCols = panel.querySelector(".dtfut__cols--mobile");
      const filtersRow = panel.querySelector(".md-filters__row");
      if (mobileCols && filtersRow && !filtersRow.contains(mobileCols)) {
        filtersRow.appendChild(mobileCols);
      }
      panel.addEventListener("click", (e) => {
        const btn = e.target.closest(".dtfut__cols-btn");
        if (!btn) return;
        const cols = btn.getAttribute("data-dtfut-cols");
        if (!cols) return;
        panel.setAttribute("data-cols", cols);
        syncActiveCol(cols);
      });
    })();
    (function wireNoResults() {
      const panel = root2.querySelector('[data-bsv4-panel="previous"]');
      const search = panel && panel.querySelector(".md-search-input");
      const grid = panel && panel.querySelector(".md-grid");
      if (!panel || !search || !grid) return;
      const refresh = () => {
        const hasQuery = search.value.trim().length > 0;
        const hasCard = grid.querySelector(".md-card") !== null;
        panel.setAttribute("data-dtfut-empty-search", hasQuery && !hasCard ? "true" : "false");
      };
      search.addEventListener("input", refresh);
      new MutationObserver(refresh).observe(grid, { childList: true });
      refresh();
    })();
    (function wireZeroDesignsHide() {
      const apply = (count) => {
        const hide = count === 0;
        const pill = root2.querySelector('[data-bsv4-source="previous"]');
        if (pill) pill.hidden = hide;
        for (const link of root2.querySelectorAll('[data-bsv4-addlink="previous"]')) {
          link.hidden = hide;
        }
        const panel = root2.querySelector('[data-bsv4-panel="previous"]');
        if (hide && panel && !panel.hidden) setSource("upload");
      };
      window.addEventListener("nt-md-count", (e) => {
        if (e.detail && typeof e.detail.count === "number") apply(e.detail.count);
      });
      if (document.body.getAttribute("data-md-has-designs") === "0") apply(0);
    })();
    document.addEventListener("bsv4:source-changed", (e) => {
      if (!e.detail || e.detail.source !== "previous") return;
      const md = document.querySelector('[data-bsv4-panel="previous"] .md-container');
      if (md && typeof md.__mdRenderNow === "function") {
        md.__mdRenderNow();
        announce("designs-opened", {});
      }
    });
    document.addEventListener("md-picker:use-designs", (e) => {
      if (window.__bsv4StudioPick || window.__studioPickActive) return;
      const items = e.detail && e.detail.items || [];
      const entries = items.map((it) => {
        const raw = String(it.file || "");
        const url = raw.split("?")[0];
        return url ? {
          url,
          name: it.fileName || url.split("/").pop(),
          orderName: it.orderName || void 0,
          // AI multi-select dispatches isAIImage:true through this same
          // event (ai-chat-sandbox.js:1253-1260) — carry the attribution
          // flags so cart-payload stamps them (audit 2026-07-09).
          isAIImage: it.isAIImage || void 0,
          isAIEdit: it.isAIEdit || void 0,
          isReadyToPress: it.isReadyToPress || void 0,
          isRtpAiEdit: it.isRtpAiEdit || void 0,
          // Purchased designs' saved URLs carry the toggle params the
          // order was placed with (parseOrderString appends them) —
          // read them BEFORE the query strip so a bg-removed reorder
          // lands bg-removed again (Owen 2026-07-22; intakeFromUrls
          // used to hard-set false and the background came back).
          bgRemove: /bg-remove=true/i.test(raw) || void 0,
          superRes: /upscale=true/i.test(raw) || void 0
        } : null;
      }).filter(Boolean);
      if (!entries.length) return;
      if (maybeCommitThenIntake({ entries })) return;
      setSource("upload");
      intakeFromUrls(entries);
    });
  }

  // src/addmore-popups.js
  var wired2 = false;
  var openRef = null;
  function openPicker(mode) {
    if (openRef) openRef(mode);
  }
  function initAddmorePopups(root2) {
    if (wired2) return;
    const dlg = root2.querySelector("[data-bsv4-pickdlg]");
    if (!dlg || typeof dlg.showModal !== "function") {
      for (const link of root2.querySelectorAll("[data-bsv4-addlink]")) link.hidden = true;
      return;
    }
    wired2 = true;
    if (dlg.parentElement !== root2) root2.appendChild(dlg);
    const host2 = dlg.querySelector("[data-bsv4-pickdlg-host]");
    let adopted = null;
    (function ensureModalLinks() {
      const zone = root2.querySelector("[data-bsv4-modal-addfile]");
      if (!zone || zone.querySelector("[data-bsv4-addlink]")) return;
      const p = document.createElement("p");
      p.className = "bsv4-cta__addmore-links";
      for (const [mode, label] of [
        ["previous", "Choose a previously uploaded design"],
        ["ai", "Create with AI"]
      ]) {
        const b = document.createElement("button");
        b.type = "button";
        b.setAttribute("data-bsv4-addlink", mode);
        b.textContent = label;
        if (mode === "ai") {
          const or = document.createElement("span");
          or.textContent = " or ";
          p.appendChild(or);
        }
        p.appendChild(b);
      }
      const sub = zone.querySelector(".bsv4-cta__addmore-sub");
      if (sub && sub.parentNode === zone) sub.after(p);
      else zone.appendChild(p);
    })();
    for (const [mode, sel] of [
      ["previous", '[data-bsv4-panel="previous"]'],
      ["ai", '[data-bsv4-panel="ai"]']
    ]) {
      if (!root2.querySelector(sel)) {
        for (const link of root2.querySelectorAll(`[data-bsv4-addlink="${mode}"]`)) {
          link.hidden = true;
        }
      }
    }
    function open2(mode) {
      const node = root2.querySelector(`[data-bsv4-panel="${mode === "ai" ? "ai" : "previous"}"]`);
      if (!node || adopted) return;
      adopted = { node, parent: node.parentNode, next: node.nextSibling };
      host2.appendChild(node);
      node.hidden = false;
      dlg.setAttribute("aria-label", mode === "ai" ? "Create a design with AI" : "Your previous designs");
      dlg.showModal();
      if (mode === "previous") {
        const md = node.querySelector(".md-container");
        if (md && typeof md.__mdRenderNow === "function") md.__mdRenderNow();
        announce("designs-opened", { via: "add-another" });
      } else {
        if (typeof window.__bsv4AiActivate === "function") window.__bsv4AiActivate();
        else injectAiAssets(node);
        const chat = node.querySelector(".ai-chat");
        if (chat) chat.dispatchEvent(new CustomEvent("ai-chat:reset"));
        document.dispatchEvent(
          new CustomEvent("dtfut:tab-change", { detail: { name: "ai" }, bubbles: true })
        );
      }
    }
    function restore() {
      if (!adopted) return;
      const { node, parent, next } = adopted;
      node.hidden = true;
      if (parent) parent.insertBefore(node, next);
      adopted = null;
      const zoom = dlg.querySelector("#ai-pop-zoom-modal");
      if (zoom) document.body.appendChild(zoom);
      window.__studioPickActive = false;
      window.__bsv4StudioPick = false;
    }
    function injectAiAssets(panel) {
      const chat = panel.querySelector(".ai-chat");
      const reveal = () => {
        if (chat) {
          chat.classList.add("ai-chat--ready");
          chat.style.opacity = "";
        }
        panel.style.opacity = "";
      };
      let pending = 0;
      const one = () => {
        if (--pending <= 0) reveal();
      };
      for (const attr of ["data-bsv4-ai-css", "data-bsv4-ai-css2", "data-bsv4-ai-js"]) {
        const url = panel.getAttribute(attr);
        if (!url) continue;
        if (attr === "data-bsv4-ai-js") {
          if (!document.querySelector(`script[src="${url}"]`)) {
            const s = document.createElement("script");
            s.src = url;
            document.head.appendChild(s);
          }
          continue;
        }
        let l = document.querySelector(`link[href="${url}"]`);
        if (!l) {
          l = document.createElement("link");
          l.rel = "stylesheet";
          l.href = url;
          document.head.appendChild(l);
        }
        if (!l.sheet) {
          pending++;
          l.addEventListener("load", one, { once: true });
          l.addEventListener("error", one, { once: true });
        }
      }
      if (pending === 0) reveal();
      else setTimeout(reveal, 3e3);
    }
    document.addEventListener(
      "click",
      (e) => {
        const link = e.target.closest && e.target.closest("[data-bsv4-addlink]");
        if (link && root2.contains(link)) {
          e.preventDefault();
          e.stopPropagation();
          open2(link.dataset.bsv4Addlink);
          return;
        }
        if (dlg.open && e.target === dlg) dlg.close();
        if (dlg.open) {
          const zoom = document.getElementById("ai-pop-zoom-modal");
          if (zoom && !dlg.contains(zoom)) dlg.appendChild(zoom);
        }
      },
      true
    );
    dlg.addEventListener("close", restore);
    openRef = open2;
    const closeBtn = dlg.querySelector("[data-bsv4-pickdlg-close]");
    if (closeBtn) closeBtn.addEventListener("click", () => dlg.close());
    document.addEventListener("md-picker:use-designs", () => {
      if (dlg.open) dlg.close();
    });
    document.addEventListener("bsv4:files-received", () => {
      if (dlg.open) dlg.close();
    });
    document.addEventListener("bsv4:added-to-cart", () => {
      if (dlg.open) dlg.close();
    });
  }

  // src/handoffs.js
  var STASH_KEY = "dtf:pending-ai-uploads:v1";
  function initHandoffs() {
    rehydrateStash();
    consumeParams();
  }
  function rehydrateStash() {
    try {
      const raw = sessionStorage.getItem(STASH_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const items = parsed && Array.isArray(parsed.items) ? parsed.items : null;
      if (!items || !items.length) return;
      sessionStorage.removeItem(STASH_KEY);
      const u = new URL(window.location.href);
      if (u.searchParams.get("file") || u.searchParams.get("my_designs_selection")) return;
      if (items.length === 1) {
        const url = items[0] && (items[0].file || items[0].url);
        if (!url) return;
        u.searchParams.set("file", url);
      } else {
        const payload = items.map((it, i) => ({
          id: `ai-stash-${Date.now()}-${i}`,
          name: it && it.fileName || `ai-image-${i + 1}.png`,
          fileURL: it && (it.file || it.url)
        })).filter((e) => !!e.fileURL);
        if (!payload.length) return;
        u.searchParams.set("my_designs_selection", JSON.stringify(payload));
      }
      window.location.replace(u.toString());
    } catch (_) {
    }
  }
  function getParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }
  function dropParams(...names) {
    const u = new URL(window.location.href);
    for (const n of names) u.searchParams.delete(n);
    history.replaceState(null, "", u.toString());
  }
  async function consumeParams() {
    const ordertype = getParam("ordertype");
    if (ordertype) {
      setAttribution({ designType: 1, orderName: getParam("order_name") || "" });
    }
    const src = getParam("src");
    if (src === "dtf_popup") sessionStorage.setItem("dtf_popup_attribution", String(Date.now()));
    else if (src) sessionStorage.removeItem("dtf_popup_attribution");
    if (src) dropParams("src");
    const selRaw = getParam("my_designs_selection");
    if (selRaw) {
      dropParams("my_designs_selection");
      let sel = [];
      try {
        sel = JSON.parse(decodeURIComponent(selRaw));
      } catch (_) {
        sel = [];
      }
      if (Array.isArray(sel) && sel.length) {
        await intakeFromUrls(
          sel.map((it) => ({ url: it.fileURL, name: it.name, orderName: it.orderName }))
        );
        return;
      }
    }
    let fileURL = getParam("file");
    if (!fileURL) return;
    fileURL = fileURL.replace("#", "%23");
    const fileQuery = fileURL.split("?").slice(1).join("?");
    fileURL = fileURL.split("?")[0];
    const flags = {};
    if (/(^|&)bg-remove=true(&|$)/i.test(fileQuery)) flags.bgRemove = true;
    if (/(^|&)upscale=true(&|$)/i.test(fileQuery)) flags.superRes = true;
    if (getParam("rtp") === "Y") flags.isReadyToPress = "Yes";
    if (getParam("pwe") === "Y") flags.isRtpAiEdit = "Yes";
    if (getParam("_design_studio") === "Yes") {
      flags.isDesignStudio = "Yes";
      window.__bsv4StudioHandoff = true;
    }
    dropParams("file", "rtp", "pwe", "_design_studio", "order_name", "ordertype", "orderFileWidth", "orderFileHeight");
    if (fileURL.includes("amazonaws.com") || fileURL.includes("imgix.net")) {
      await intakeFromUrls([{ url: fileURL, ...flags }]);
    } else {
      try {
        const blob = await fetch(fileURL).then((r) => r.blob());
        const name = fileURL.split("/").pop() || "design.png";
        await intake([new File([blob], name, { type: blob.type || "image/png" })]);
      } catch (err) {
        console.warn("[bsv4] external file handoff failed:", err && err.message);
      }
    }
  }

  // src/integrations.js
  function initIntegrations(root2) {
    wireCountdown(root2);
    wireBackdropClose(root2);
    wireBfcacheResync(root2);
    wireReviewCommas(root2);
    document.addEventListener("bsv4:file-uploaded", () => window.refreshGRTUpsellBlocks?.());
  }
  function wireReviewCommas(root2) {
    const cell = root2.querySelector(".bsv4__meta-rating");
    if (!cell) return;
    const format = () => {
      const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
      let node;
      while (node = walker.nextNode()) {
        const m = node.nodeValue.match(/\b(\d{4,})\b/);
        if (m) {
          const withCommas = Number(m[1]).toLocaleString("en-US");
          if (withCommas !== m[1]) node.nodeValue = node.nodeValue.replace(m[1], withCommas);
        }
      }
    };
    format();
    const mo = new MutationObserver(() => format());
    mo.observe(cell, { childList: true, characterData: true, subtree: true });
  }
  function wireBackdropClose(root2) {
    for (const dialog of root2.querySelectorAll("dialog")) {
      if (dialog.closest('[data-bsv4-block="multi_upload"]')) continue;
      dialog.addEventListener("click", (e) => {
        if (e.target !== dialog) return;
        dialog.close();
        document.dispatchEvent(new CustomEvent("bsv4:dialog-closed"));
      });
    }
  }
  function wireBfcacheResync(root2) {
    window.addEventListener("pageshow", async (e) => {
      if (!e.persisted) return;
      try {
        const res = await fetch(`${location.pathname}?view=itemsInCart__cumulative`).then((r) => r.json());
        const count = res ? Number(res.count) : NaN;
        if (Number.isFinite(count)) {
          root2.__bsv4cfg && (root2.__bsv4cfg.cartQty = count);
          update({ cartQty: count });
          document.dispatchEvent(new CustomEvent("bsv4:cart-qty-refreshed", { detail: { count } }));
        }
      } catch (_) {
      }
    });
  }
  function wireCountdown(root2) {
    const perk = root2.querySelector("[data-bsv4-countdown]");
    const textEl = perk && perk.querySelector("[data-bsv4-countdown-text]");
    if (!perk || !textEl || !textEl.textContent.includes("[")) return;
    const template = textEl.textContent;
    const cardRow = root2.querySelector("[data-bsv4-cdcard]");
    const cardTitle = cardRow && cardRow.querySelector("[data-bsv4-cd-title]");
    const cardSub = cardRow && cardRow.querySelector("[data-bsv4-cd-sub]");
    perk.hidden = true;
    const wire = (attempt = 0) => {
      const h = document.querySelector(".nt-cd-clock") || document.querySelector(".headerCountdown") || document;
      const dateEl = [...h.querySelectorAll("date")].find((el) => el.textContent.trim());
      const timeEl = h.querySelector(".grt-time-box time") || h.querySelector("time");
      if (!dateEl && !timeEl) {
        if (attempt < 5) setTimeout(() => wire(attempt + 1), 1e3 * (attempt + 1));
        return;
      }
      const apply = () => {
        const rawDate = dateEl ? dateEl.textContent.trim() : "";
        const date = rawDate.includes(",") ? rawDate.split(",").slice(1).join(",").trim() : rawDate;
        const rawT = timeEl ? timeEl.textContent : "";
        const hm = rawT.match(/(\d+)\s*h\s*(\d+)\s*m(?:\s*(\d+)\s*s)?/i);
        const digits = rawT.replace(/[A-Za-z]/g, "").replace(/\s+/g, "");
        const parts = hm ? [hm[1], hm[2], hm[3]].filter(Boolean) : digits.split(":").filter(Boolean);
        const time = parts.length >= 2 ? `${parseInt(parts[0], 10)}h ${parseInt(parts[1], 10)}m` : "";
        perk.hidden = !date && !time;
        if (!perk.hidden) {
          textEl.innerHTML = template.replace("[DATE]", `<strong class="bsv4-perks__hl">${date}</strong>`).replace("[TIME]", `<strong class="bsv4-perks__hl">${time}</strong>`);
        }
        if (cardRow) {
          const timeFull = parts.length >= 3 ? `${parseInt(parts[0], 10)}h ${parseInt(parts[1], 10)}m ${parseInt(parts[2], 10)}s` : time;
          if (date || time) {
            if (cardTitle) {
              const seg = (cardRow.dataset.tplTitle || "Order in [TIME]").split("[TIME]");
              const t = document.createElement("span");
              t.className = "bsv4-perks__cd-time";
              t.textContent = timeFull;
              cardTitle.replaceChildren(seg[0] || "", t, seg.slice(1).join("[TIME]"));
            }
            if (cardSub) cardSub.textContent = (cardRow.dataset.tplSub || "For Fastest Delivery [DATE]").replace("[DATE]", date);
          }
        }
      };
      apply();
      if (timeEl) {
        new MutationObserver(apply).observe(timeEl, { characterData: true, childList: true, subtree: true });
      }
    };
    wire();
    document.addEventListener("nt-geo:changed", () => setTimeout(wire, 0));
  }

  // src/ga-bridge.js
  var UPLOAD_TAB_MAP = {
    upload: "upload",
    previous: "previous_designs",
    ai: "create_ai",
    studio: "design_studio"
  };
  var OPEN_SETTLE_MS = 250;
  var COMMIT_MS = 600;
  var carouselFired = false;
  var openFired = false;
  var proceeded = false;
  var openTimer = null;
  var lastCommitted = {};
  var commitTimers = {};
  function environment() {
    return window.Shopify && Shopify.theme && Shopify.theme.role === "main" ? "production" : "staging";
  }
  function fire(name, params) {
    if (typeof window.gtag === "undefined") return;
    params = params || {};
    params.environment = environment();
    params.device_type = window.innerWidth <= 767 ? "mobile" : "desktop";
    window.gtag("event", name, params);
  }
  function uploadIndex(el) {
    const tile = el.closest && el.closest("[data-bsv4-tile]");
    if (!tile || !tile.parentElement) return void 0;
    return [...tile.parentElement.children].indexOf(tile);
  }
  function inModal(el) {
    return !!(el.closest && el.closest("[data-bsv4-modal]"));
  }
  var gaApi = { fire, uploadIndex, inModal };
  function initGaBridge(root2) {
    if (window.__bsv4GaBridge) return;
    window.__bsv4GaBridge = true;
    document.addEventListener(
      "click",
      (e) => {
        const t = e.target;
        if (!t || !t.closest || !root2.contains(t)) return;
        if (t.matches('input[type="file"]')) return;
        if (inModal(t)) {
          if (t.closest("[data-bsv4-modal-checkout]")) {
            proceeded = true;
            fire("multiupload_proceed_checkout", {});
          } else if (t.closest("[data-bsv4-tile-remove]")) {
            fire("multiupload_delete_upload", { upload_index: uploadIndex(t) });
          } else if (t.closest("[data-bsv4-edit-open]")) {
            fire("multiupload_edit_ai_click", { upload_index: uploadIndex(t) });
          } else if (t.closest(".bsv4-tile__add")) {
            fire("multiupload_add_size_click", { upload_index: uploadIndex(t) });
          }
          return;
        }
        if (t.closest("[data-bsv4-video-open]")) {
          fire("dtf_pdp_video_play", {});
          return;
        }
        const source = t.closest("[data-bsv4-source]");
        if (source) {
          const key = source.getAttribute("data-bsv4-source");
          fire("dtf_pdp_upload_tab_click", { tab: UPLOAD_TAB_MAP[key] || key });
          return;
        }
        if (!carouselFired && t.closest("[data-bsv4-gal-prev], [data-bsv4-gal-next], [data-bsv4-thumb]")) {
          carouselFired = true;
          fire("dtf_pdp_carousel_interact", {});
          return;
        }
        const addlink = t.closest("[data-bsv4-addlink]");
        if (addlink) {
          fire(
            addlink.getAttribute("data-bsv4-addlink") === "ai" ? "dtf_pdp_add_design_ai_click" : "dtf_pdp_add_design_previous_click",
            {}
          );
          return;
        }
        if (t.closest("[data-bsv4-add-another]")) {
          fire("dtf_pdp_add_design_upload_click", {});
          return;
        }
        if (t.closest(".nt-design-note__header")) {
          fire("dtf_pdp_note_toggle", {});
          return;
        }
        if (t.closest("[data-bsv4-checkout]")) {
          fire("dtf_pdp_proceed_checkout", {});
          return;
        }
        const sizeTab = t.closest("[data-bsv4-size-tab]");
        if (sizeTab) {
          fire("dtf_pdp_size_tab_switch", {
            tab: sizeTab.getAttribute("data-bsv4-size-tab")
          });
        }
      },
      true
    );
    document.addEventListener(
      "change",
      (e) => {
        const t = e.target;
        if (!t || !t.closest || !root2.contains(t)) return;
        if (!inModal(t)) {
          if (t.matches("[data-bsv4-precut]")) fire("dtf_pdp_precut_toggle", {});
          return;
        }
        if (t.matches("[data-bsv4-tiletab]")) {
          if (t.checked)
            fire("multiupload_size_tab_switch", {
              tab: t.value,
              upload_index: uploadIndex(t)
            });
        } else if (t.matches("[data-bsv4-popular]")) {
          if (t.value)
            fire("multiupload_popular_size_select", {
              size: t.value.trim(),
              upload_index: uploadIndex(t)
            });
        } else if (t.matches("[data-bsv4-toggle-bgr]")) {
          fire("multiupload_remove_bg_toggle", {
            state: t.checked ? "on" : "off",
            upload_index: uploadIndex(t)
          });
        } else if (t.matches("[data-bsv4-toggle-sr]")) {
          fire("multiupload_upscale_toggle", {
            state: t.checked ? "on" : "off",
            upload_index: uploadIndex(t)
          });
        } else if (t.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) {
          commitField(t);
        }
      },
      true
    );
    document.addEventListener(
      "input",
      (e) => {
        const t = e.target;
        if (!t || !t.closest || !root2.contains(t) || !inModal(t)) return;
        if (!t.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) return;
        const idx = uploadIndex(t);
        const key = (t.matches("[data-bsv4-qty]") ? "qty:" : "sz:") + idx + ":" + (t.matches("[data-bsv4-w]") ? "w" : "h");
        clearTimeout(commitTimers[key]);
        commitTimers[key] = setTimeout(() => commitField(t), COMMIT_MS);
      },
      true
    );
    function commitField(t) {
      const idx = uploadIndex(t);
      if (idx === void 0) return;
      if (t.matches("[data-bsv4-qty]")) {
        const qty = parseInt(t.value, 10);
        if (isNaN(qty) || lastCommitted["qty:" + idx] === qty) return;
        lastCommitted["qty:" + idx] = qty;
        fire("multiupload_qty_change", { qty, upload_index: idx });
      } else {
        const dim = t.matches("[data-bsv4-w]") ? "width" : "height";
        const num = parseFloat(t.value);
        const key = "sz:" + idx + ":" + dim;
        if (isNaN(num) || lastCommitted[key] === num) return;
        lastCommitted[key] = num;
        fire("multiupload_custom_size_edit", {
          dimension: dim,
          size_value: num,
          upload_index: idx
        });
      }
    }
    const dialog = root2.querySelector("[data-bsv4-modal]");
    if (dialog) {
      const onOpenChange = () => {
        if (dialog.open) {
          if (openTimer || openFired) return;
          openTimer = setTimeout(() => {
            openTimer = null;
            if (!dialog.open || openFired) return;
            openFired = true;
            proceeded = false;
            fire("multiupload_modal_open", {});
          }, OPEN_SETTLE_MS);
        } else {
          if (openTimer) {
            clearTimeout(openTimer);
            openTimer = null;
          }
          if (openFired && !proceeded) fire("multiupload_abandon", {});
          openFired = false;
          proceeded = false;
        }
      };
      new MutationObserver(onOpenChange).observe(dialog, {
        attributes: true,
        attributeFilter: ["open"]
      });
    }
  }

  // src/studio.js
  var root;
  var host;
  var open = false;
  function initStudio(rootEl) {
    root = rootEl;
    host = root.querySelector("[data-bsv4-studio-host]");
    if (!host) return;
    if (!window.__ensureV0Css) {
      window.__ensureV0Css = (cb) => {
        ensureStyles();
        if (typeof cb === "function") cb();
      };
    }
    document.addEventListener("bsv4:source-changed", (e) => {
      const src = e.detail && e.detail.source;
      if (src === "studio") openStudio();
      else if (open) closeStudio();
    });
  }
  function ensureStyles() {
    if (document.querySelector('link[rel="stylesheet"][href*="v0-styles"]')) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = host && host.dataset && host.dataset.bsv4V0css || "";
    if (!link.href) {
      const anyAsset = document.querySelector('link[href*="/assets/"], script[src*="/assets/"]');
      if (anyAsset) {
        const src = anyAsset.href || anyAsset.src;
        link.href = src.replace(/\/assets\/[^/?]+.*/, "/assets/v0-styles.css");
      }
    }
    if (link.href) document.head.appendChild(link);
  }
  function openStudio() {
    if (window.__ensureV0Js && !window.__v0JsLoaded) {
      window.__ensureV0Js(() => openStudio());
      return;
    }
    const editorOverlay = document.querySelector(".customTabelPopup__overlay-editor");
    const introOverlay = document.querySelector(".customTabelPopup__overlay-modal");
    if (!editorOverlay) return;
    ensureStyles();
    editorOverlay.__inlineParent = host;
    if (editorOverlay.parentNode !== host) host.appendChild(editorOverlay);
    editorOverlay.classList.add("studio-inline");
    editorOverlay.classList.remove("studio-embedded");
    editorOverlay.style.display = "block";
    if (introOverlay) introOverlay.style.display = "none";
    const swatch = document.querySelector(".transparent-swatch.canvas-color-swatch");
    if (swatch) {
      try {
        swatch.click();
      } catch (_) {
      }
    }
    const prevBtn = document.getElementById("prev-designs-btn");
    if (prevBtn && !prevBtn.__bsv4Wired) {
      prevBtn.__bsv4Wired = true;
      prevBtn.__spdInit = true;
      prevBtn.style.display = "";
      prevBtn.addEventListener("click", () => {
        window.__studioPickActive = true;
        window.__bsv4StudioPick = true;
        if (document.querySelector("[data-alv2-modal]") && window.NTArtworkLibrary && window.NTArtworkLibrary.open) {
          window.NTArtworkLibrary.open();
          return;
        }
        openPicker("previous");
      });
      if (!window.__bsv4StudioAlv2Cleanup) {
        window.__bsv4StudioAlv2Cleanup = true;
        document.addEventListener("alv2:modal-closed", () => {
          setTimeout(() => {
            window.__bsv4StudioPick = false;
          }, 0);
        });
      }
    }
    host.hidden = false;
    root.querySelector(".bsv4-uploader__zone")?.setAttribute("data-bsv4-studio-hidden", "");
    open = true;
    if (window.matchMedia && window.matchMedia("(max-width: 749px)").matches) {
      window.__dtfMobileZoomTarget = 38;
      setZoomWhenReady(38);
    }
  }
  function closeStudio() {
    const editorOverlay = document.querySelector(".customTabelPopup__overlay-editor");
    if (editorOverlay) editorOverlay.style.display = "none";
    host.hidden = true;
    root.querySelector(".bsv4-uploader__zone")?.removeAttribute("data-bsv4-studio-hidden");
    open = false;
  }
  function setZoomWhenReady(targetPct, tries = 0) {
    if (typeof window.setStudioZoom !== "function") {
      if (tries > 150) return;
      setTimeout(() => setZoomWhenReady(targetPct, tries + 1), 100);
      return;
    }
    try {
      window.setStudioZoom(targetPct);
    } catch (_) {
    }
    let attempts = 0;
    const iv = setInterval(() => {
      attempts++;
      const display = document.getElementById("zoom-display");
      const current = display && parseInt((display.textContent || "").replace("%", ""), 10);
      if (current && current !== targetPct) {
        try {
          window.setStudioZoom(targetPct);
        } catch (_) {
        }
      }
      if (attempts > 15) clearInterval(iv);
    }, 100);
  }

  // src/gallery.js
  function initGallery(root2) {
    const gal = root2.querySelector("[data-bsv4-gallery]");
    if (!gal) return;
    const slides = [...gal.querySelectorAll("[data-bsv4-slide]")];
    const thumbs = [...gal.querySelectorAll("[data-bsv4-thumb]")];
    let index = 0;
    const show = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle("is-active", n === index));
      thumbs.forEach((t, n) => t.classList.toggle("is-active", n === index));
      thumbs[index]?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      const v = gal.querySelector("[data-bsv4-slide] video");
      if (v) slides[index].contains(v) ? v.play().catch(() => {
      }) : v.pause();
      [1, slides.length - 1].forEach((d) => {
        const s = slides[(index + d) % slides.length];
        if (s && s.loading === "lazy") s.loading = "eager";
      });
    };
    gal.addEventListener("click", (e) => {
      const thumb = e.target.closest("[data-bsv4-thumb]");
      if (thumb) show(parseInt(thumb.dataset.bsv4Thumb, 10));
      else if (e.target.closest("[data-bsv4-gal-prev]")) show(index - 1);
      else if (e.target.closest("[data-bsv4-gal-next]")) show(index + 1);
    });
    const stage = gal.querySelector(".bsv4-gallery__stage") || gal;
    let swipeX = 0, swipeY = 0;
    stage.addEventListener("touchstart", (e) => {
      swipeX = e.touches[0].clientX;
      swipeY = e.touches[0].clientY;
    }, { passive: true });
    stage.addEventListener("touchend", (e) => {
      const dx = e.changedTouches[0].clientX - swipeX;
      const dy = e.changedTouches[0].clientY - swipeY;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) show(index + (dx < 0 ? 1 : -1));
    }, { passive: true });
    const card = root2.querySelector("[data-bsv4-video-open]");
    const dialog = root2.querySelector("[data-bsv4-video-dialog]");
    if (card && dialog) {
      card.addEventListener("click", () => {
        dialog.showModal();
        dialog.querySelector("video")?.play().catch(() => {
        });
      });
      const close = () => {
        dialog.querySelector("video")?.pause();
        dialog.close();
      };
      dialog.querySelector("[data-bsv4-video-close]").addEventListener("click", close);
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) close();
      });
    }
  }

  // src/size-guide.js
  function containFit2(w, h, capW, capH) {
    w = parseFloat(w);
    h = parseFloat(h);
    capW = parseFloat(capW);
    capH = parseFloat(capH);
    if (!(w > 0) || !(h > 0) || !(capW > 0) || !(capH > 0)) return null;
    const scale = Math.min(capW / w, capH / h);
    return { w: round22(w * scale), h: round22(h * scale) };
  }
  function fitLabel(fit) {
    return fit ? `${cmFromIn(fit.w)}x${cmFromIn(fit.h)}cm` : "";
  }
  function round22(n) {
    return Number(n.toFixed(2));
  }
  function designDims(file) {
    const rows = file.rows || [];
    const row = rows[rows.length - 1];
    const w = row && row.w != null ? row.w : file.widthIn != null ? file.widthIn : file.inW;
    const h = row && row.h != null ? row.h : file.heightIn != null ? file.heightIn : file.inH;
    return { w, h };
  }
  function initSizeGuide(root2, cfg7) {
    const block = root2.querySelector('[data-bsv4-block="size_guide"]');
    if (!block || !cfg7 || !cfg7.sizeGuide) return;
    if (block.dataset.bsv4SizeGuideInit === "1") return;
    block.dataset.bsv4SizeGuideInit = "1";
    const garments = cfg7.sizeGuide.garments || [];
    const tiles = Array.from(block.querySelectorAll("[data-bsv4-sg-tile]")).map((el, i) => {
      const g = garments[i] || {};
      return {
        el,
        capW: parseFloat(el.dataset.bsv4SgCapW != null ? el.dataset.bsv4SgCapW : g.capW),
        capH: parseFloat(el.dataset.bsv4SgCapH != null ? el.dataset.bsv4SgCapH : g.capH),
        label: el.querySelector("[data-bsv4-sg-label]"),
        design: el.querySelector("[data-bsv4-sg-design]")
      };
    });
    if (!tiles.length) return;
    for (const t of tiles) {
      if (!t.design) continue;
      t.design.addEventListener("load", () => {
        t.design.hidden = false;
      });
      t.design.addEventListener("error", () => {
        t.design.hidden = true;
      });
    }
    const custom = block.querySelector("[data-bsv4-sg-custom]");
    const customWrap = custom && custom.closest(".bsv4-size-guide__swatch--custom");
    function applyColor(color, isCustom) {
      for (const t of tiles) t.el.style.backgroundColor = color;
      if (customWrap) customWrap.classList.toggle("is-active", isCustom);
      if (isCustom) {
        for (const r of block.querySelectorAll("[data-bsv4-sg-swatch]")) r.checked = false;
      }
    }
    block.addEventListener("change", (e) => {
      const sw = e.target.closest && e.target.closest("[data-bsv4-sg-swatch]");
      if (sw && sw.checked) applyColor(sw.value, false);
    });
    if (custom) custom.addEventListener("input", () => applyColor(custom.value, true));
    subscribe((state2) => {
      const file = activeFile(state2);
      if (!file || !file.imgixUrl) {
        for (const t of tiles) {
          if (t.design) {
            t.design.hidden = true;
            t.design.removeAttribute("src");
          }
        }
        return;
      }
      const dims = designDims(file);
      const url = previewUrl(file);
      for (const t of tiles) {
        const fit = containFit2(dims.w, dims.h, t.capW, t.capH);
        if (!fit) continue;
        if (t.label) t.label.textContent = fitLabel(fit);
        if (t.design) {
          if (t.design.getAttribute("src") !== url) {
            t.design.hidden = true;
            t.design.src = url;
          }
        }
      }
    });
  }

  // src/main.js
  var perksMq = window.matchMedia("(max-width: 720px)");
  var perksOutMq = window.matchMedia("(min-width: 0px)");
  var perksRoot = null;
  function placePerks(root2) {
    perksRoot = root2;
    const perks = root2.querySelector(".bsv4-perks:not(.bsv4-perks--static)");
    const zone = root2.querySelector(".bsv4-uploader__zone");
    if (!perks || !zone) return;
    const card = zone.closest('[data-bsv4-block="upload_box"]');
    if (perksOutMq.matches && card && root2.dataset.bsv4Ab === "b") {
      if (perks.previousElementSibling !== card) card.after(perks);
      perks.classList.add("bsv4-perks--inup", "bsv4-perks--out");
    } else {
      if (perks.previousElementSibling !== zone) zone.after(perks);
      perks.classList.add("bsv4-perks--inup");
      perks.classList.remove("bsv4-perks--out");
    }
  }
  try {
    perksOutMq.addEventListener("change", () => {
      if (perksRoot) placePerks(perksRoot);
    });
  } catch (x) {
  }
  function collapseEmptyMount() {
    for (const c of document.querySelectorAll(".shopify-section.mount-app .container--vertical-space")) {
      const blk = c.querySelector(".shopify-app-block");
      const empty = !blk || !Array.from(blk.children).some((k) => !/^(LINK|SCRIPT|STYLE)$/.test(k.tagName));
      if (perksMq.matches && empty) {
        c.style.setProperty("margin", "0", "important");
        c.style.setProperty("padding", "0", "important");
      } else {
        c.style.removeProperty("margin");
        c.style.removeProperty("padding");
      }
    }
  }
  try {
    perksMq.addEventListener("change", collapseEmptyMount);
  } catch (x) {
  }
  var desktopToolsMq = window.matchMedia("(min-width: 1000px)");
  function syncDesktopTools(root2) {
    const prev = root2.querySelector(".bsv4-preview");
    if (!prev || prev.dataset.bsv4DesktopTools !== "classic") return;
    const apply = (flip) => {
      prev.classList.toggle("bsv4-preview--stagetools", !desktopToolsMq.matches);
      if (!flip) return;
      if (typeof window.__bsv4ToolsModeFlip === "function") window.__bsv4ToolsModeFlip();
      if (typeof window.__bsv4PlacePins === "function") window.__bsv4PlacePins();
    };
    apply(false);
    try {
      desktopToolsMq.addEventListener("change", () => apply(true));
    } catch (x) {
    }
  }
  var stickBands = null;
  function syncStickTop(root2) {
    if (!stickBands) {
      stickBands = Array.from(document.querySelectorAll(".shopify-section, header")).filter((el) => {
        if (root2.contains(el)) return false;
        const p = getComputedStyle(el).position;
        return p === "sticky" || p === "fixed";
      });
      if (!stickBands.length) return;
    }
    const rects = stickBands.map((el) => el.getBoundingClientRect()).filter((r) => r.height > 0 && r.bottom < window.innerHeight * 0.5 && r.width > window.innerWidth * 0.5).sort((a, b) => a.top - b.top);
    let bottom = 0;
    for (const r of rects) {
      if (r.top <= bottom + 1 && r.bottom > bottom) bottom = r.bottom;
    }
    if (bottom > 0) root2.style.setProperty("--bsv4-stick-top", `${Math.round(bottom) + 24}px`);
  }
  function initStickTop(root2) {
    syncStickTop(root2);
    let trail = 0;
    const onMove = () => {
      syncStickTop(root2);
      clearTimeout(trail);
      trail = setTimeout(() => syncStickTop(root2), 450);
    };
    window.addEventListener("scroll", onMove, { passive: true });
    window.addEventListener("resize", onMove);
    window.addEventListener("load", onMove);
  }
  window.__bsv4 = { getState, update, subscribe, announce };
  window.__bsv4Bridge = {
    getState,
    update,
    subscribe,
    announce,
    uploadBlob,
    intakeFromUrls,
    previewUrl,
    resolveDimensions,
    saveFileToUser,
    // My Designs save — the Add Text chunk applies uploads of its own
    nbEdit,
    refitRowsToAspect,
    IMGIX_HOST,
    S3_HOST,
    gaApi
  };
  window.__bsv4Rev = false ? "dev-unbuilt" : "r20261019-222605.77c196e3+";
  if (!window.__bsv4AnchorWired) {
    window.__bsv4AnchorWired = true;
    document.addEventListener("click", (e) => {
      const a = e.target.closest && e.target.closest("[data-bsv4-scrollto]");
      if (!a) return;
      e.preventDefault();
      const want = (a.getAttribute("data-bsv4-scrollto") || "").toLowerCase();
      if (!want) return;
      for (const h of document.querySelectorAll("h1, h2, h3, h4")) {
        if ((h.textContent || "").toLowerCase().includes(want)) {
          const t = h.closest(".shopify-section") || h;
          const targetY = Math.max(0, t.getBoundingClientRect().top + window.pageYOffset - 90);
          const startY = window.pageYOffset;
          const dist = targetY - startY;
          const dur = Math.min(900, Math.max(350, Math.abs(dist) * 0.22));
          const t0 = performance.now();
          let doneAt = 0;
          const step = (now) => {
            const p = Math.min(1, (now - t0) / dur);
            window.scrollTo(0, startY + dist * (1 - Math.pow(1 - p, 3)));
            if (p < 1) requestAnimationFrame(step);
            else doneAt = now;
          };
          requestAnimationFrame(step);
          setTimeout(() => {
            if (!doneAt) window.scrollTo(0, targetY);
          }, dur + 250);
          return;
        }
      }
    });
  }
  function boot(root2) {
    if (!root2 || root2.dataset.bsv4Booted === "1") return;
    root2.dataset.bsv4Booted = "1";
    const cfg7 = readConfig();
    if (cfg7.family) update({ family: cfg7.family });
    if (cfg7.cartQty) update({ cartQty: Number(cfg7.cartQty) || 0 });
    subscribe((state2) => {
      root2.dataset.state = state2.status;
      root2.dataset.mode = state2.mode;
    });
    const safe = (name, fn) => {
      try {
        fn();
      } catch (err) {
        console.error(`bsv4: ${name} init failed`, err);
      }
    };
    safe("uploader", () => initUploader(root2, cfg7));
    safe("desktop-tools", () => syncDesktopTools(root2));
    safe("perks", () => placePerks(root2));
    safe("mount", collapseEmptyMount);
    safe("sticktop", () => initStickTop(root2));
    safe("preview", () => initPreview(root2, cfg7));
    safe("modal", () => initModal(root2, cfg7));
    safe("print-method", () => initPrintMethod(root2, cfg7));
    safe("sizing", () => initSizing(root2, cfg7));
    safe("pricing", () => initPricing(root2, cfg7));
    safe("cart", () => initCart(root2, cfg7));
    safe("designs", () => initDesigns(root2, cfg7));
    safe("addmore-popups", () => initAddmorePopups(root2));
    const bgrSrc = root2.dataset.bsv4Bgrjs;
    if (window.__bsv4BgrBoot) {
      safe("bgr", () => window.__bsv4BgrBoot());
    } else if (bgrSrc && !window.__bsv4BgrWired) {
      window.__bsv4BgrWired = true;
      const fetchBgrChunk = () => {
        const s = document.createElement("script");
        s.setAttribute("data-bsv4-bgrchunk", "");
        s.onerror = () => {
          s.remove();
          wireBgrChunk();
        };
        s.src = bgrSrc;
        document.body.appendChild(s);
      };
      const wireBgrChunk = () => {
        const un = subscribe((state2) => {
          if (state2.status !== "uploading" && !state2.files.length) return;
          un();
          fetchBgrChunk();
        });
      };
      wireBgrChunk();
    }
    window.__bsv4Bridge.cfg = cfg7;
    const editSrc = root2.dataset.bsv4Editjs;
    if (window.__bsv4EditBoot) {
      safe("edit", () => window.__bsv4EditBoot());
    } else if (cfg7.preview && cfg7.preview.aiEdit && editSrc && !window.__bsv4EditWired) {
      window.__bsv4EditWired = true;
      const fetchChunk = () => {
        const s = document.createElement("script");
        s.setAttribute("data-bsv4-editchunk", "");
        s.onerror = () => {
          s.remove();
          wireChunk();
        };
        s.src = editSrc;
        document.body.appendChild(s);
      };
      const wireChunk = () => {
        const un = subscribe((state2) => {
          if (!state2.files.length) return;
          un();
          fetchChunk();
        });
      };
      wireChunk();
    }
    const treatSrc = root2.dataset.bsv4Treatjs;
    if (window.__bsv4TreatBoot) {
      safe("treat", () => window.__bsv4TreatBoot());
    } else if (treatSrc && !window.__bsv4TreatWired) {
      window.__bsv4TreatWired = true;
      const fetchTreatChunk = () => {
        const s = document.createElement("script");
        s.setAttribute("data-bsv4-treatchunk", "");
        s.onerror = () => {
          s.remove();
          wireTreatChunk();
        };
        s.src = treatSrc;
        document.body.appendChild(s);
      };
      const wireTreatChunk = () => {
        const un = subscribe((state2) => {
          if (state2.status !== "uploading" && !state2.files.length) return;
          un();
          fetchTreatChunk();
        });
      };
      wireTreatChunk();
    }
    if (!window.__bsv4CalcWired) {
      window.__bsv4CalcWired = true;
      document.addEventListener(
        "click",
        (e) => {
          const trg = e.target.closest && e.target.closest("a.pricingTable, ._price-calc-link, .openPriceCalculator");
          if (!trg) return;
          const r = document.querySelector("[data-bsv4-root]");
          const calcSrc = r && r.dataset.bsv4Calcjs;
          if (!r || !calcSrc) return;
          e.preventDefault();
          e.stopPropagation();
          if (window.__bsv4CalcBoot) return void window.__bsv4CalcBoot(true);
          if (window.__bsv4CalcFetching) return;
          window.__bsv4CalcFetching = true;
          const s = document.createElement("script");
          s.onerror = () => {
            s.remove();
            window.__bsv4CalcFetching = false;
          };
          s.src = calcSrc;
          document.body.appendChild(s);
        },
        true
      );
    }
    const qrTrg = root2.querySelector("[data-bsv4-qr-open]");
    if (qrTrg)
      qrTrg.addEventListener("click", () => {
        if (window.__bsv4QrBoot) return void window.__bsv4QrBoot(true);
        if (window.__bsv4QrFetching) return;
        const qrSrc = root2.dataset.bsv4Qrjs;
        if (!qrSrc) return;
        window.__bsv4QrFetching = true;
        const s = document.createElement("script");
        s.onerror = () => {
          s.remove();
          window.__bsv4QrFetching = false;
        };
        s.src = qrSrc;
        document.body.appendChild(s);
      });
    safe("studio", () => initStudio(root2, cfg7));
    safe("gallery", () => initGallery(root2, cfg7));
    safe("size-guide", () => initSizeGuide(root2, cfg7));
    safe("integrations", () => initIntegrations(root2, cfg7));
    safe("ga-bridge", () => initGaBridge(root2));
    safe("handoffs", () => initHandoffs());
    announce("boot", { version: cfg7.version || "v5", family: cfg7.family || "dtf" });
  }
  boot(document.querySelector("[data-bsv4-root]"));
  document.addEventListener("shopify:section:load", (e) => {
    const root2 = e.target && e.target.querySelector && e.target.querySelector("[data-bsv4-root]");
    if (root2) boot(root2);
  });
})();
